import React, { useEffect, useState, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import { Download, Calendar, Users, Euro, PlusCircle, Tag, LogIn, RotateCcw, LayoutGrid, FileText, Check, Car, Image, Save, Trash2, Mail, Link as LinkIcon, AlertTriangle, Activity, FileArchive, TestTube2, MessageCircle, MapPin, Plus, X, Calculator } from "lucide-react";
import { Nav, Footer, Hinomaru } from "@/components/Nav";
import { QrCheckInScanner } from "@/components/QrCheckInScanner";
// V56.4 · Photographer page rimossa: la stessa Admin.jsx renderizza il tab Fotografo
// in base al role restituito dal backend (nessun doppione, permessi filtrati).
import { TeamView } from "@/components/TeamView";
import { PricingTable } from "@/components/PricingTable";
import { adminStats, adminBookings, adminDays, adminCreateDay, adminPartners, adminCreatePartner, adminExportCsv, adminAgenda, adminRefund, adminMarkInvoice, adminInvoicesCsv, adminGetCars, adminUpdateCar, adminSendEmail, adminResendCompletion, adminResetEmailStatus, adminResetAllFailedEmails, adminEmailHealth, adminEmailTest, adminOcrPatente, adminGetEvent, adminSetEventSettings, adminSetCapacity, adminDeleteBooking, adminCleanupCancelled, adminCancelBooking, adminPlanning, adminPlanningUpdate, adminPlanningCsv, adminPlanningXlsx, adminPlanningPdfUrl, adminPlanningPdfBlob, adminPlanningUploadCsv, adminPlanningV2Get, adminPlanningV2Config, adminPlanningV2Regenerate, adminPlanningV2SlotUpdate, adminPlanningV2SlotSwap, adminPlanningV2SlotDelete, adminAnalytics, adminDossierPdfUrl, adminBookingDocUrl, adminSingleDocPdfUrl, adminBookingPaymentInfo, adminGetMarketing, adminUpdateMarketing, adminSendMediaLink, adminRequestLicenseReupload, adminRequestDocReupload, adminUploadPatente, adminUploadCfPhoto, adminSignManleva, adminUpdateFiscal, adminPrintMissingUrl, adminPrintMissingAllUrl, adminMissingDocsSummary, adminOfficinaNotesGet, adminOfficinaNotesSave, adminCreateTestBooking, adminCreateManualBooking, adminRegisterPayment, adminPaymentsExportUrl, adminWhatsappOptinCsv, adminDdayLive, adminMarkArrived, adminUnmarkArrived, adminCheckinPrelim, adminCheckinGate, adminCheckinReset, authRole, adminMediaDeliver, adminWhatsAppLink, adminGetMediaProducts, adminUpdateMediaProduct, adminResetMediaProducts, adminListVouchers, adminResendVoucherEmails, adminListDiscounts, adminCreateDiscount, adminDiscountPreviewUrl, adminSendDiscountEmail, adminDeactivateDiscount, adminChangeProduct, adminShiftFascia, adminListCustomers, adminBulkSendDiscount, adminMediaMatrix, photographerMediaMatrix, photographerConfig, photographerAddMedia, mediaSlotSetStatus, mediaSlotSendInvite, mediaSlotCopyLink, mediaSlotDeliver, mediaSlotRedeliver, mediaFollowUpBulk, mediaReassignPaid, mediaSlotManualDeliver, photographerSetBigTransferLink, photographerSetBigTransferLinks, photographerSendBigTransfer, getMediaDeliverySettings, setMediaDeliverySettings, mediaBulkSendLinks, mediaBulkInvite, mediaCreateManualClient, photographerDeleteMedia,
    cbCassaCash, cbCassaCashCreate, cbCassaCashDelete, cbEventi, pushGetPublicKey, pushSubscribe, pushUnsubscribe, pushListSubscriptions, pushSendTest, api, API } from "@/lib/api";
import { LogOut, Camera } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ContabilitaDashboard } from "@/pages/Contabilita";
import { PlanningBoundary } from "@/components/PlanningBoundary";
import { EventScopeBar, useEventScope } from "@/components/EventScope";
import { getEventId } from "@/lib/eventScope";
import { PhotographyWorkspace } from "@/components/gallery/GalleryManager";
import { InvoiceWorkspace } from "@/components/invoices/InvoiceWorkspace";
import { NewsletterAdmin } from "@/components/NewsletterAdmin";
import { Dialog, DialogContent, DialogTitle, DialogTrigger, DialogHeader, DialogFooter } from "@/components/ui/dialog";

// ═══════════════════════════════════════════════════════════════════════════
// APP_VERSION — Bumpa ad ogni deploy per verifica visiva rapida in admin
// Formato: "V<numero> · <breve descrizione>"
// REGOLA VERSIONE: modifica piccola → sposta il DECIMALE (V60 → V60.1 → V60.2);
// nuova sezione o macro-funzione → sposta la VERSIONE INTERA (V60 → V61).
// ═══════════════════════════════════════════════════════════════════════════
const APP_VERSION = "V68 · Newsletter eventi (pubblica + area riservata)";

export default function Admin() {
    const { eventId, eventLabel } = useEventScope();
    const [activeTab, setActiveTab] = useState("event");
    const [token, setToken] = useState(localStorage.getItem("velora_admin_token") || "");
    const [input, setInput] = useState("");
    const [role, setRole] = useState(null);
    const [checkingRole, setCheckingRole] = useState(Boolean(token));
    const [verifiedToken, setVerifiedToken] = useState(null);
    const [stats, setStats] = useState(null);
    const [manualBookingOpen, setManualBookingOpen] = useState(false);
    const [mediaLedgerOpen, setMediaLedgerOpen] = useState(false);
    const [cashModalOpen, setCashModalOpen] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [showMissingList, setShowMissingList] = useState(false);
    const [days, setDays] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);

    // Detect role when token changes
    useEffect(() => {
        setRole(null); setVerifiedToken(null);
        if (!token) { setCheckingRole(false); return; }
        let active = true;
        const controller = new AbortController();
        setCheckingRole(true);
        authRole(token, controller.signal)
            .then(r => {
                if (!active) return;
                if (!["admin", "photographer"].includes(r.role)) {
                    localStorage.removeItem("velora_admin_token");
                    setToken(""); setRole(null);
                    toast.error("Token non valido");
                } else {
                    setRole(r.role);
                    setVerifiedToken(token);
                }
            })
            .catch(e => { if (active && e?.code !== "ERR_CANCELED") { toast.error("Errore verifica token"); setToken(""); setRole(null); } })
            .finally(() => { if (active) setCheckingRole(false); });
        return () => { active = false; controller.abort(); };
    }, [token]);

    const logout = () => {
        localStorage.removeItem("velora_admin_token");
        setToken(""); setRole(null); setVerifiedToken(null); setCheckingRole(false);
    };

    const load = async () => {
        if (!token || role !== "admin") return;
        const requestedEvent = getEventId();
        setLoading(true);
        try {
            const [s, b, d, p] = await Promise.all([adminStats(token), adminBookings(token), adminDays(token), adminPartners(token)]);
            if (requestedEvent !== getEventId()) return;
            setStats(s); setBookings(b); setDays(d); setPartners(p);
        } catch (e) {
            if (e?.response?.status === 401) { logout(); toast.error("Token non valido"); }
            else toast.error("Errore caricamento");
        } finally { setLoading(false); }
    };

    useEffect(() => { setStats(null); setBookings([]); setDays([]); setCashModalOpen(false); setMediaLedgerOpen(false); if (role === "admin") load(); }, [role, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (token && (checkingRole || verifiedToken !== token || !role)) {
        return <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]"><Nav /><p role="status" data-testid="admin-auth-loading" className="max-w-md mx-auto px-5 py-24 text-sm text-[#9AA2AD]">Verifica accesso…</p></div>;
    }
    if (!token) {
        return (
            <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
                <Nav />
                <div className="max-w-md mx-auto px-5 py-24">
                    <h1 className="font-display text-5xl uppercase mb-6">Area Riservata</h1>
                    <p className="text-sm text-[#9AA2AD] mb-4">Inserisci il token amministratore o fotografo.</p>
                    <input data-testid="admin-token-input" value={input} onChange={e => setInput(e.target.value)} type="password" placeholder="Token" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 mb-3" />
                    <button data-testid="admin-login-btn" disabled={checkingRole} onClick={() => { localStorage.setItem("velora_admin_token", input); setToken(input); }} className="w-full bg-[#E2231A] hover:bg-[#C11B15] px-5 py-3 font-display uppercase tracking-wider flex items-center justify-center gap-2 transition-colors duration-150 disabled:opacity-60"><LogIn size={18} /> {checkingRole ? "Verifica…" : "Entra"}</button>
                </div>
                <Footer />
            </div>
        );
    }

    // V56.4 · Photographer role: stessa Admin.jsx, ma vede SOLO il tab Fotografo
    // (nessun doppione di componenti — la pagina è unica, cambia solo il permesso).
    if (role === "photographer") {
        return (
            <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
                <Nav />
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
                    <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                        <div>
                            <h1 className="font-display text-5xl uppercase flex items-center gap-3">
                                <Camera className="text-[#E2231A]" strokeWidth={1.5} /> Dashboard Fotografo
                            </h1>
                            <p className="text-xs text-[#9AA2AD] uppercase tracking-widest mt-2">
                                Carica foto/video associandoli al singolo cliente · zero accesso a dati sensibili.
                            </p>
                        </div>
                        <button
                            data-testid="photographer-logout"
                            onClick={logout}
                            className="border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                        ><LogOut size={14} /> Esci</button>
                    </div>
                    <EventScopeBar token={token} prefix="photographer" />
                    <PhotographyWorkspace token={token}><PhotographyMatrixView key={eventId} token={token} role="photographer" /></PhotographyWorkspace>
                </div>
                <Footer />
            </div>
        );
    }

    // Admin fallthrough (role === 'admin')

    return (
        <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
            <Nav />
            <QrCheckInScanner token={token} open={scannerOpen} onClose={() => { setScannerOpen(false); load(); }} />
            {showMissingList && <MissingDocsListModal token={token} onClose={() => setShowMissingList(false)} />}
            <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
                <EventScopeBar token={token} />
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                    <div>
                        <h1 className="font-display text-5xl uppercase">Area Riservata</h1>
                        <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]/60 mt-1" data-testid="app-version">{APP_VERSION}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <PushNotificationButton token={token} />
                        <button
                            data-testid="btn-open-qr-scanner"
                            onClick={() => setScannerOpen(true)}
                            className="bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"
                            title="Scansiona QR per check-in D-Day"
                        >📷 Check-in QR</button>
                        <CreateTestBookingButton token={token} onDone={load} />
                        <button
                            data-testid="btn-whatsapp-csv"
                            onClick={() => adminWhatsappOptinCsv(token).catch(() => toast.error("Errore export"))}
                            title="Esporta CSV numeri WhatsApp con consenso marketing"
                            className="border border-[#25D366]/60 hover:bg-[#25D366]/10 text-[#25D366] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"
                        ><MessageCircle size={14} /> WhatsApp CSV</button>
                        <button data-testid="csv-export" onClick={() => adminExportCsv(token).catch(() => toast.error("Errore export"))} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Download size={14} /> CSV</button>
                        <a data-testid="csv-payments-export" href={adminPaymentsExportUrl(token)} target="_blank" rel="noreferrer" title="Esporta CSV pagamenti (Stripe + Cash + Bonifico)" className="border border-[#f59e0b]/60 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Download size={14} /> CSV Pagamenti</a>
                        <button data-testid="btn-reconcile-media" onClick={async () => {
                            if (!window.confirm("Riconcilia i pagamenti media (foto extra) mancanti da Stripe verso il database?\nUtile se vedi meno €25 nelle stats.")) return;
                            try {
                                const r = await api.post("/admin/reconcile-media-txns", null, { headers: { "x-admin-token": token } });
                                alert(`Riconciliazione completata.\n${r.data.count} transazione/i recuperate.\n\nRicarica per aggiornare le stats.`);
                                load();
                            } catch (e) {
                                alert("Errore riconciliazione: " + (e?.response?.data?.detail || e.message));
                            }
                        }} title="Recupera pagamenti media Stripe non registrati" className="border border-[#16a34a]/60 hover:bg-[#16a34a]/10 text-[#16a34a] px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">↻ Riconcilia Media</button>
                        <button data-testid="btn-manual-booking" onClick={() => setManualBookingOpen(true)} className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Plus size={14} /> Prenotazione Manuale</button>
                        <button data-testid="btn-bulk-request-docs" onClick={async () => {
                            // 1) DRY-RUN preview: quanti clienti hanno doc mancanti?
                            let preview;
                            try {
                                const r = await api.post("/admin/bookings/request-missing-docs-bulk", { dry_run: true }, { headers: { "x-admin-token": token } });
                                preview = r.data;
                            } catch (e) {
                                alert("Errore anteprima: " + (e?.response?.data?.detail || e.message));
                                return;
                            }
                            if (!preview.count) { alert("✅ Nessun cliente con documenti mancanti!"); return; }
                            const lines = preview.processed.slice(0, 20).map(p => `• ${p.customer_name || "—"} (${p.voucher || "—"}): ${p.missing_labels.join(", ")}`);
                            const more = preview.count > 20 ? `\n…e altri ${preview.count - 20}` : "";
                            if (!window.confirm(`📨 Inviare email a ${preview.count} cliente/i con documenti mancanti?\n\n${lines.join("\n")}${more}\n\nOgnuno riceverà UNA email personalizzata con la lista dei propri doc mancanti + link al tunnel.`)) return;
                            try {
                                const r = await api.post("/admin/bookings/request-missing-docs-bulk", { dry_run: false }, { headers: { "x-admin-token": token } });
                                const okCount = r.data.processed.filter(p => p.email_sent).length;
                                alert(`✅ Richieste inviate.\n${okCount}/${r.data.count} email spedite con successo.`);
                                load();
                            } catch (e) {
                                alert("Errore invio massivo: " + (e?.response?.data?.detail || e.message));
                            }
                        }} title="Scansiona tutti i booking e invia email a chi ha documenti mancanti" className="border border-[#E2231A]/60 hover:bg-[#E2231A]/10 text-[#E2231A] px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">📨 Richiedi Doc Mancanti (Tutti)</button>
                        <a data-testid="btn-print-missing-all" href={adminPrintMissingAllUrl(token)} target="_blank" rel="noreferrer" title="Genera un unico PDF con i moduli mancanti di tutti i clienti (manleva, GDPR, marketing, ecc.) — pronto per stampa e firma manuale in pista" className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><FileText size={14} /> Stampa Moduli Mancanti (Bulk)</a>
                        <button data-testid="btn-show-missing-list" onClick={() => setShowMissingList(true)} title="Vedi elenco dettagliato: quali moduli mancano a ogni cliente" className="border border-[#f59e0b]/60 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">📋 Elenco Doc Mancanti</button>
                    </div>
                </div>

                {stats && (
                    <>
                    <div className="grid md:grid-cols-7 gap-3 mb-2">
                        <Stat tid="stat-revenue-turni" icon={<Euro strokeWidth={1.5} />} label="🏁 Cassa Turni" value={`€${((stats.revenue_turni_cents ?? stats.revenue_cents ?? 0) / 100).toFixed(2)}`} />
                        <button onClick={() => setMediaLedgerOpen(true)} data-testid="stat-revenue-foto"
                            className="text-left border border-[#16a34a]/40 bg-[#16a34a]/5 hover:bg-[#16a34a]/10 p-4 transition-colors">
                            <div className="text-[9px] uppercase tracking-widest text-[#16a34a] flex items-center gap-1">📸 Cassa Foto (fotografo) <span className="text-[#9AA2AD]/60">▸ dettaglio</span></div>
                            <div className="text-2xl font-bold text-[#16a34a] mt-1">€{((stats.revenue_media_cents ?? 0) / 100).toFixed(2)}</div>
                            <div className="text-[9px] text-[#16a34a]/70 mt-1">{(stats.media_ledger || []).length} movimenti · click per vedere</div>
                        </button>
                        <button onClick={() => setCashModalOpen(true)} data-testid="stat-cassa-cash"
                            className="text-left border border-[#f59e0b]/40 bg-[#f59e0b]/5 hover:bg-[#f59e0b]/10 p-4 transition-colors">
                            <div className="text-[9px] uppercase tracking-widest text-[#f59e0b] flex items-center gap-1">💵 Cassa Cash <span className="text-[#9AA2AD]/60">▸ dettaglio</span></div>
                            <div className="text-2xl font-bold text-[#f59e0b] mt-1">€{((stats.cassa_cash_cents ?? 0) / 100).toFixed(2)}</div>
                            <div className="text-[9px] text-[#f59e0b]/70 mt-1">{(stats.cassa_cash_ledger || []).length} paganti · click per gestire</div>
                        </button>
                        <Stat tid="stat-bookings" icon={<Users strokeWidth={1.5} />} label="Prenotazioni" value={stats.bookings_total} />
                        <Stat tid="stat-completed" icon={<Users strokeWidth={1.5} />} label="Completate" value={stats.bookings_completed} />
                        <Stat tid="stat-turn-slots" icon={<Calendar strokeWidth={1.5} />} label="Slot turni prenotati" value={`${stats.occupied_turn_slots ?? 0} / ${stats.ideal_slots ?? 0}`} highlight />
                        <Stat tid="stat-slots" icon={<Calendar strokeWidth={1.5} />} label="Slot liberi" value={`${stats.slots_free ?? 0} / ${stats.ideal_slots ?? 0}`} />
                    </div>
                    <div className="text-[10px] text-[#9AA2AD] mb-8 px-1">
                        🔒 <strong className="text-[#F4F2ED]">Casse separate:</strong> la Cassa Foto <span className="text-[#16a34a]">(al fotografo)</span> non tocca mai la Cassa Turni <span className="text-[#E2231A]">(business)</span>. Click su "Cassa Foto" per vedere il dettaglio dei movimenti.
                    </div>
                    </>
                )}

                {mediaLedgerOpen && (
                    <MediaLedgerModal
                        rows={stats?.media_ledger || []}
                        total={stats?.revenue_media_cents || 0}
                        onClose={() => setMediaLedgerOpen(false)}
                    />
                )}

                {cashModalOpen && (
                    <CassaCashModal
                        token={token}
                        onClose={() => { setCashModalOpen(false); load(); }}
                    />
                )}

                <Tabs key={eventId} value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="bg-[#1C2026] border border-[#9AA2AD]/20 rounded-none p-0 flex flex-nowrap justify-start w-full velora-scroll-x velora-scroll-hint">
                        <TabsTrigger data-testid="tab-event" value="event" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">Evento</TabsTrigger>
                        <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">Prenotazioni</TabsTrigger>
                        <TabsTrigger data-testid="tab-partners" value="partners" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">🎌 Sconti</TabsTrigger>
                        <TabsTrigger data-testid="tab-fatture" value="fatture" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">Fatture</TabsTrigger>
                        <TabsTrigger data-testid="tab-planning" value="planning" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">Planning</TabsTrigger>
                        <TabsTrigger data-testid="tab-dday" value="dday" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">D-Day Live</TabsTrigger>
                        <TabsTrigger data-testid="tab-cars" value="cars" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">Prodotti & Prezzi</TabsTrigger>
                        <TabsTrigger data-testid="tab-media" value="media" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">Media</TabsTrigger>
                        <TabsTrigger data-testid="tab-vouchers" value="vouchers" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">Voucher 🎁</TabsTrigger>
                        <TabsTrigger data-testid="tab-marketing" value="marketing" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">Marketing 📢</TabsTrigger>
                        <TabsTrigger data-testid="tab-newsletter" value="newsletter" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">✉️ Newsletter</TabsTrigger>
                        <TabsTrigger data-testid="tab-photography" value="photography" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">📸 Fotografo</TabsTrigger>
                        <TabsTrigger data-testid="tab-team" value="team" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] data-[state=active]:text-[#F4F2ED] uppercase tracking-widest text-xs px-5 py-3">Team 👥</TabsTrigger>
                        <TabsTrigger data-testid="tab-officina" value="officina" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">🔧 Officina/Ricambi</TabsTrigger>
                        <TabsTrigger data-testid="tab-stats" value="stats" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">Statistiche</TabsTrigger>
                        <TabsTrigger data-testid="tab-accounting" value="accounting" className="rounded-none flex-shrink-0 data-[state=active]:bg-[#E2231A] uppercase tracking-widest text-xs px-5 py-3">💶 Contabilità</TabsTrigger>
                    </TabsList>

                    <TabsContent value="event" className="mt-6">
                        {eventId === "tutti" ? <p data-testid="event-select-required" className="text-sm text-[#9AA2AD] py-6">Tutti gli eventi / Generale · seleziona un evento per le sue impostazioni.</p> : <EventView token={token} />}
                    </TabsContent>

                    <TabsContent value="newsletter" className="mt-6">
                        <NewsletterAdmin token={token} />
                    </TabsContent>

                    <TabsContent value="bookings" className="mt-6">
                        <EmailHealthPanel token={token} />
                        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                            <div className="text-xs uppercase tracking-widest text-[#9AA2AD]">
                                Totale: <b className="text-[#F4F2ED]">{bookings.length}</b> · Annullate: <b className="text-[#F4F2ED]">{bookings.filter(b => b.status === "annullata").length}</b>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <button data-testid="btn-manual-booking-prenotazioni" onClick={() => setManualBookingOpen(true)}
                                    className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Plus size={14} /> Prenotazione Manuale</button>
                                <CleanupCancelledButton token={token} count={bookings.filter(b => b.status === "annullata").length} onDone={load} />
                            </div>
                        </div>
                        <DualScrollX testid="bookings-scroll" className="-mx-5 md:mx-0" maxHeight="72vh">
                            <table className="w-full text-sm min-w-[1100px]">
                                <thead className="sticky top-0 z-10">
                                    <tr className="bg-[#1C2026] text-[#9AA2AD] uppercase tracking-widest text-[10px]">
                                        <th className="text-left p-3">Turno</th>
                                        <th className="text-left p-3">Voucher</th>
                                        <th className="text-left p-3">Cliente</th>
                                        <th className="text-left p-3">WhatsApp</th>
                                        <th className="text-left p-3">Formula</th>
                                        <th className="text-left p-3">Turno €</th>
                                        <th className="text-left p-3 bg-[#16a34a]/10">Foto €</th>
                                        <th className="text-left p-3">Stato</th>
                                        <th className="text-left p-3">Timeline</th>
                                        <th className="text-left p-3">Accettazione</th>
                                        <th className="text-left p-3">Media</th>
                                        <th className="text-left p-3">Email</th>
                                        <th className="text-left p-3">Azione</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings.map(b => (
                                        <BookingRow key={b.id} b={b} token={token} load={load} />
                                    ))}
                                    {bookings.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-[#9AA2AD]">Nessuna prenotazione ancora.</td></tr>}
                                </tbody>
                            </table>
                        </DualScrollX>
                    </TabsContent>

                    <TabsContent value="days" className="mt-6 hidden">
                        {/* Legacy tab hidden post single-event pivot */}
                    </TabsContent>

                    <TabsContent value="partners" className="mt-6">
                        <DiscountsView token={token} />
                    </TabsContent>

                    <TabsContent value="fatture" className="mt-6">
                        <InvoiceWorkspace key={eventId} token={token} />
                    </TabsContent>

                    <TabsContent value="cars" className="mt-6">
                        <CarsView token={token} />
                    </TabsContent>

                    <TabsContent value="media" className="mt-6">
                        <MediaProductsView token={token} />
                    </TabsContent>

                    <TabsContent value="vouchers" className="mt-6">
                        <VouchersView token={token} />
                    </TabsContent>

                    <TabsContent value="marketing" className="mt-6">
                        <MarketingView token={token} />
                    </TabsContent>

                    <TabsContent value="photography" className="mt-6">
                        <PhotographyWorkspace token={token}><PhotographyMatrixView token={token} role="admin" /></PhotographyWorkspace>
                    </TabsContent>

                    <TabsContent value="team" className="mt-6">
                        <TeamView token={token} />
                    </TabsContent>

                    <TabsContent value="planning" className="mt-6">
                        <div className="flex justify-end mb-3 gap-2 flex-wrap">
                            <button data-testid="btn-manual-booking-planning" onClick={() => setManualBookingOpen(true)}
                                className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Plus size={14} /> Prenotazione Manuale (entra in Planning)</button>
                        </div>
                        <PlanningBoundary><PlanningView token={token} onGlobalReload={load} /></PlanningBoundary>
                    </TabsContent>

                    <TabsContent value="dday" className="mt-6">
                        <DDayLiveView token={token} />
                    </TabsContent>

                    <TabsContent value="stats" className="mt-6">
                        <StatsView token={token} />
                    </TabsContent>

                    <TabsContent value="officina" className="mt-6">
                        <OfficinaView token={token} />
                    </TabsContent>

                    <TabsContent value="accounting" className="mt-6">
                        <ContabilitaDashboard token={token} embedded />
                    </TabsContent>
                </Tabs>
            </div>
            <Footer />
            {manualBookingOpen && <ManualBookingModal token={token} onClose={() => setManualBookingOpen(false)} onSaved={() => { setManualBookingOpen(false); load(); }} />}
        </div>
    );
}

const Stat = ({ icon, label, value, tid, highlight }) => (
    <div data-testid={tid} className={`border p-5 ${highlight ? "border-[#E2231A]/50 bg-[#E2231A]/5" : "border-[#9AA2AD]/20 bg-[#1C2026]"}`}>
        <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] ${highlight ? "text-[#E2231A]" : "text-[#9AA2AD]"}`}>{icon} {label}</div>
        <div className={`font-display text-4xl mt-2 ${highlight ? "text-[#E2231A]" : ""}`}>{value}</div>
    </div>
);

// V54.2 · CustomerNameCell — mostra nome + email; se nome è placeholder ("Cliente"/vuoto)
// e la patente è caricata, offre un pulsante "🔤 Leggi da patente" (OCR Gemini).
const _isPlaceholderName = (n) => {
    if (!n) return true;
    const s = String(n).trim().toLowerCase();
    if (["", "cliente", "customer", "test", "n/a", "-", "—"].includes(s)) return true;
    if (s.startsWith("test cliente")) return true;
    return false;
};

const CustomerNameCell = ({ b, token, load }) => {
    const [busy, setBusy] = React.useState(false);
    const placeholder = _isPlaceholderName(b.customer_name);
    const hasPatente = !!(b.has_license || b.patente_fronte_url || b.patente_url);
    const runOcr = async (force = false) => {
        setBusy(true);
        try {
            const r = await adminOcrPatente(token, b.id, force);
            if (r.full_name) {
                toast.success(`Patente letta · ${r.full_name}${r.customer_name_updated ? " (nome aggiornato)" : ""}`);
                if (load) load();
            } else {
                toast.warning("OCR non è riuscito a leggere il nome — usa 'Colma lacune' per inserirlo a mano");
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message;
            let hint = "";
            if (typeof detail === "string" && detail.toLowerCase().includes("pdf")) {
                hint = " · La patente è in PDF: sostituiscila con una foto JPG/PNG da 'Colma lacune'.";
            } else if (typeof detail === "string" && detail.toLowerCase().includes("non caricata")) {
                hint = " · Il cliente non ha ancora caricato la patente fronte.";
            } else if (typeof detail === "string" && (detail.includes("400") || detail.toLowerCase().includes("blocked") || detail.toLowerCase().includes("image"))) {
                hint = " · Foto poco leggibile — chiedi al cliente uno scatto migliore.";
            }
            toast.error("OCR fallito: " + detail + hint);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div data-testid={`booking-customer-${b.id}`}>
            <div className={placeholder ? "text-[#f59e0b] italic" : "text-[#F4F2ED]"}>
                {b.customer_name || "—"}
                {placeholder && hasPatente && (
                    <button
                        onClick={() => runOcr(false)}
                        disabled={busy}
                        title="Estrai nome dalla patente (OCR AI)"
                        data-testid={`btn-ocr-patente-${b.id}`}
                        className="ml-2 text-[9px] uppercase tracking-widest border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-1.5 py-0.5 disabled:opacity-50"
                    >
                        {busy ? "…" : "🔤 Leggi patente"}
                    </button>
                )}
                {placeholder && !hasPatente && (
                    <span className="ml-2 text-[9px] uppercase tracking-widest text-[#9AA2AD]" title="Il cliente non ha ancora caricato la patente">
                        ⚠ patente mancante
                    </span>
                )}
                {!placeholder && hasPatente && (
                    <button
                        onClick={() => runOcr(true)}
                        disabled={busy}
                        title="Rileggi nome dalla patente (sovrascrivi)"
                        data-testid={`btn-ocr-patente-force-${b.id}`}
                        className="ml-2 text-[9px] text-[#9AA2AD]/60 hover:text-[#4B7BEA] disabled:opacity-50"
                    >
                        {busy ? "…" : "🔤"}
                    </button>
                )}
            </div>
            {b.booking_intestatario_name && b.booking_intestatario_name.trim() && (
                <div className="text-[9px] uppercase tracking-widest text-[#ca8a04] mt-0.5" title="Chi ha effettuato l'ordine e pagato (diverso dal pilota)">
                    💳 paga: {b.booking_intestatario_name}
                </div>
            )}
            <span className="text-[#9AA2AD] text-xs">{b.customer_email}</span>
        </div>
    );
};

// V54.1 · DualScrollX — wrapper con DUE scrollbar orizzontali (in cima E in fondo)
// sincronizzate, così l'utente vede sempre uno slider orizzontale senza dover
// scrollare fino al fondo della tabella. Usa max-h per limitare l'altezza e
// mostrare anche uno scroll verticale interno; l'header resta sticky.
const DualScrollX = ({ children, maxHeight = "75vh", className = "", testid }) => {
    const topRef = React.useRef(null);
    const bottomRef = React.useRef(null);
    const innerRef = React.useRef(null);
    const [contentWidth, setContentWidth] = React.useState(0);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const syncing = React.useRef(false);

    React.useEffect(() => {
        const measure = () => {
            if (innerRef.current) {
                const sw = innerRef.current.scrollWidth;
                setContentWidth(sw);
            }
            if (bottomRef.current) {
                setHasOverflow(bottomRef.current.scrollWidth > bottomRef.current.clientWidth + 1);
            }
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (innerRef.current) ro.observe(innerRef.current);
        if (bottomRef.current) ro.observe(bottomRef.current);
        window.addEventListener("resize", measure);
        return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
    }, [children]);

    const onScroll = (src) => {
        if (syncing.current) return;
        syncing.current = true;
        const targets = [topRef.current, bottomRef.current].filter(Boolean);
        for (const t of targets) {
            if (t && t !== src) t.scrollLeft = src.scrollLeft;
        }
        syncing.current = false;
    };

    return (
        <div className={`border border-[#9AA2AD]/20 ${className}`} data-testid={testid}>
            {/* Scrollbar orizzontale SEMPRE visibile in cima (solo se c'è overflow) */}
            {hasOverflow && (
                <div
                    ref={topRef}
                    onScroll={(e) => onScroll(e.currentTarget)}
                    className="overflow-x-auto overflow-y-hidden h-3 bg-[#15181C] border-b border-[#9AA2AD]/15"
                    data-testid={testid ? `${testid}-top-scroll` : undefined}
                >
                    <div style={{ width: contentWidth || "100%", height: 1 }} />
                </div>
            )}
            {/* Contenuto vero: scroll orizzontale + verticale */}
            <div
                ref={bottomRef}
                onScroll={(e) => onScroll(e.currentTarget)}
                className="overflow-auto velora-scroll-x"
                style={{ maxHeight }}
            >
                <div ref={innerRef} className="inline-block min-w-full align-top">
                    {children}
                </div>
            </div>
        </div>
    );
};

const Badge = ({ status }) => {
    const colors = {
        pagata: "border-[#ca8a04] text-[#ca8a04]",
        completata: "border-[#16a34a] text-[#16a34a]",
        no_show: "border-[#9AA2AD] text-[#9AA2AD]",
        annullata: "border-[#9AA2AD] text-[#9AA2AD]",
        programmata: "border-[#9AA2AD] text-[#9AA2AD]",
        confirmed: "border-[#16a34a] text-[#16a34a]",
        non_confermata: "border-[#E2231A] text-[#E2231A]",
    };
    return <span className={`px-2 py-0.5 border text-[10px] uppercase tracking-widest ${colors[status] || "border-[#9AA2AD] text-[#9AA2AD]"}`}>{status}</span>;
};

function CreateDayDialog({ token, onCreated }) {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ date: "", circuit: "Autodromo di Modena", cars: ["EVO", "WRX"], capacity_per_car: 20, minimum_participants: 8, slot_minutes: 15, start_time: "10:00", end_time: "18:00", instructors: [] });
    const submit = async () => {
        try {
            await adminCreateDay(token, { ...f, date: new Date(f.date).toISOString() });
            toast.success("Giornata creata");
            setOpen(false);
            onCreated();
        } catch (e) { toast.error("Errore creazione"); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="btn-new-day" className="border border-[#E2231A] hover:bg-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><PlusCircle size={14} /> Nuova giornata</button></DialogTrigger>
            <DialogContent className="bg-[#1C2026] border-[#9AA2AD]/20 rounded-none text-[#F4F2ED]">
                <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Nuova giornata</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <input data-testid="new-day-date" type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                    <input value={f.circuit} onChange={e => setF({ ...f, circuit: e.target.value })} placeholder="Circuito" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                    <div className="grid grid-cols-2 gap-3">
                        <input type="number" min={4} max={40} value={f.capacity_per_car} onChange={e => setF({ ...f, capacity_per_car: parseInt(e.target.value || "0") })} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" placeholder="Slot per auto" />
                        <input type="number" min={0} value={f.minimum_participants} onChange={e => setF({ ...f, minimum_participants: parseInt(e.target.value || "0") })} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" placeholder="Minimo" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <input type="time" value={f.start_time} onChange={e => setF({ ...f, start_time: e.target.value })} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                        <input type="time" value={f.end_time} onChange={e => setF({ ...f, end_time: e.target.value })} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                    </div>
                </div>
                <DialogFooter><button data-testid="btn-create-day" onClick={submit} className="bg-[#E2231A] hover:bg-[#C11B15] px-5 py-3 uppercase tracking-widest text-sm font-display">Crea</button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CreatePartnerDialog({ token, onCreated }) {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ name: "", codice_sconto: "", percentuale_sconto: 10, percentuale_provvigione: 10 });
    const submit = async () => {
        try { await adminCreatePartner(token, f); toast.success("Partner creato"); setOpen(false); onCreated(); }
        catch (e) { toast.error("Errore"); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="btn-new-partner" className="border border-[#E2231A] hover:bg-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Tag size={14} /> Nuovo partner</button></DialogTrigger>
            <DialogContent className="bg-[#1C2026] border-[#9AA2AD]/20 rounded-none text-[#F4F2ED]">
                <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Nuovo partner</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Nome" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                    <input value={f.codice_sconto} onChange={e => setF({ ...f, codice_sconto: e.target.value.toUpperCase() })} placeholder="Codice (es. CLUB10)" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 uppercase tracking-widest" />
                    <div className="grid grid-cols-2 gap-3">
                        <input type="number" value={f.percentuale_sconto} onChange={e => setF({ ...f, percentuale_sconto: parseInt(e.target.value || "0") })} placeholder="Sconto %" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                        <input type="number" value={f.percentuale_provvigione} onChange={e => setF({ ...f, percentuale_provvigione: parseInt(e.target.value || "0") })} placeholder="Provvigione %" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                    </div>
                </div>
                <DialogFooter><button onClick={submit} className="bg-[#E2231A] hover:bg-[#C11B15] px-5 py-3 uppercase tracking-widest text-sm font-display">Crea</button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RefundButton({ token, bookingId, onDone }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const refund = async () => {
        setLoading(true);
        try {
            const r = await adminRefund(token, bookingId);
            toast.success(r.already_refunded ? "Già rimborsato" : `Rimborsato €${((r.amount || 0) / 100).toFixed(2)}`);
            setOpen(false);
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore rimborso"); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid={`btn-refund-${bookingId}`} className="text-xs uppercase tracking-widest text-[#E2231A] hover:text-[#F4F2ED] flex items-center gap-1"><RotateCcw size={12} /> Rimborsa</button></DialogTrigger>
            <DialogContent className="bg-[#1C2026] border-[#9AA2AD]/20 rounded-none text-[#F4F2ED]">
                <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Confermi rimborso?</DialogTitle></DialogHeader>
                <p className="text-sm text-[#9AA2AD]">Verrà rimborsato l'intero importo via Stripe. Slot liberati, voucher annullato. L'azione è irreversibile.</p>
                <DialogFooter>
                    <button onClick={() => setOpen(false)} className="border border-[#9AA2AD]/30 px-4 py-2 text-xs uppercase tracking-widest">Annulla</button>
                    <button data-testid="btn-confirm-refund" onClick={refund} disabled={loading} className="bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 px-5 py-2 uppercase tracking-widest text-sm font-display">{loading ? "…" : "Rimborsa ora"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AgendaView({ token, days, onChange }) {
    const [dayId, setDayId] = useState(null);
    const [data, setData] = useState(null);
    useEffect(() => {
        if (days.length && !dayId) setDayId(days[0].id);
    }, [days, dayId]);
    useEffect(() => {
        if (!dayId) return;
        adminAgenda(token, dayId).then(setData).catch(() => toast.error("Errore agenda"));
    }, [dayId, token]);

    if (!days.length) return <div className="text-[#9AA2AD] p-6">Nessuna giornata configurata. Vai al tab "Giornate" per crearne una.</div>;
    if (!data) return <div className="text-[#9AA2AD] p-6">Caricamento…</div>;

    const carLabel = { EVO: "Auto 1 · EVO", WRX: "Auto 2 · WRX", PASSENGER: "Passeggero" };
    const stats = data.stats || {};

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <LayoutGrid size={18} className="text-[#E2231A]" strokeWidth={1.5} />
                <select data-testid="agenda-day-select" value={dayId || ""} onChange={e => setDayId(e.target.value)} className="bg-[#1C2026] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-2 text-sm uppercase tracking-widest">
                    {days.map(d => <option key={d.id} value={d.id}>{new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })} · {d.circuit}</option>)}
                </select>
                <div className="ml-auto flex gap-3 text-xs uppercase tracking-widest text-[#9AA2AD]">
                    <span data-testid="agenda-booked" className="text-[#E2231A]">Occupati {stats.booked}/{stats.total_slots}</span>
                    <span>Incasso €{((stats.revenue_cents || 0) / 100).toFixed(2)}</span>
                    <span>Accompagnatori del team Velora: {(data.day.instructors || []).join(", ") || "—"}</span>
                </div>
            </div>

            <div className="border border-[#9AA2AD]/20 velora-scroll-x">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[#1C2026] text-[#9AA2AD] uppercase tracking-widest text-[10px]">
                            <th className="text-left p-3 w-20">Ora</th>
                            {data.day.cars.map(c => <th key={c} className="text-left p-3">{carLabel[c] || c}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.grid.map(row => (
                            <tr key={row.time} className="border-t border-[#9AA2AD]/10">
                                <td className="p-3 font-display text-lg">{new Date(row.time).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</td>
                                {data.day.cars.map(c => {
                                    const cell = row.cells[c];
                                    if (!cell) return <td key={c} className="p-3 text-[#9AA2AD]/40">—</td>;
                                    if (cell.status === "free") return <td key={c} className="p-3 text-[#9AA2AD] text-xs uppercase tracking-widest">libero</td>;
                                    if (cell.status === "held") return <td key={c} className="p-3 text-[#ca8a04] text-xs uppercase tracking-widest">tenuto</td>;
                                    return (
                                        <td key={c} data-testid={`cell-${cell.slot_id}`} className="p-3">
                                            <div className="font-display text-sm">{cell.customer_name}</div>
                                            <div className="text-[10px] text-[#9AA2AD] uppercase tracking-widest">{cell.voucher_code} · €{(cell.amount / 100).toFixed(0)}</div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge status={cell.booking_status} />
                                                {cell.manleva_signed && <span className="text-[10px] text-[#E2231A] uppercase tracking-widest">✓ firmato</span>}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}



function FattureView({ token, bookings, onChange }) {
    const [onlyPending, setOnlyPending] = useState(true);

    const paid = bookings.filter(b => b.status === "pagata" || b.status === "completata");
    const list = onlyPending ? paid.filter(b => !(b.invoice && b.invoice.emessa)) : paid;

    const totalPaid = paid.reduce((s, b) => s + (b.amount || 0), 0);
    const totalPending = paid.filter(b => !(b.invoice && b.invoice.emessa)).reduce((s, b) => s + (b.amount || 0), 0);
    const totalIssued = totalPaid - totalPending;

    return (
        <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
                <Stat tid="stat-total-paid" icon={<Euro strokeWidth={1.5} />} label="Incassato totale" value={`€${(totalPaid / 100).toFixed(2)}`} />
                <Stat tid="stat-total-pending" icon={<FileText strokeWidth={1.5} />} label="Da fatturare" value={`€${(totalPending / 100).toFixed(2)}`} />
                <Stat tid="stat-total-issued" icon={<Check strokeWidth={1.5} />} label="Fatturato" value={`€${(totalIssued / 100).toFixed(2)}`} />
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button data-testid="filter-pending" onClick={() => setOnlyPending(v => !v)} className={`px-4 py-2 border uppercase tracking-widest text-xs transition-colors duration-150 ${onlyPending ? "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]" : "border-[#9AA2AD]/30 text-[#9AA2AD]"}`}>
                    {onlyPending ? "Solo da emettere" : "Tutte"}
                </button>
                <div className="ml-auto flex gap-2">
                    <button data-testid="csv-fatture-pending" onClick={() => adminInvoicesCsv(token, true).catch(() => toast.error("Errore export"))} className="border border-[#E2231A] hover:bg-[#E2231A] hover:text-[#F4F2ED] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Download size={14} /> CSV da emettere</button>
                    <button data-testid="csv-fatture-all" onClick={() => adminInvoicesCsv(token, false).catch(() => toast.error("Errore export"))} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Download size={14} /> CSV tutte</button>
                </div>
            </div>

            <div className="border border-[#9AA2AD]/20 velora-scroll-x">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[#1C2026] text-[#9AA2AD] uppercase tracking-widest text-[10px]">
                            <th className="text-left p-3">Voucher</th>
                            <th className="text-left p-3">Data</th>
                            <th className="text-left p-3">Cliente</th>
                            <th className="text-left p-3">CF / P.IVA</th>
                            <th className="text-left p-3">Fatt. a</th>
                            <th className="text-left p-3">SDI / PEC</th>
                            <th className="text-left p-3">€</th>
                            <th className="text-left p-3">Stato</th>
                            <th className="text-left p-3">Azione</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map(b => {
                            const fis = b.fiscal || {};
                            const inv = b.invoice || {};
                            const emessa = !!inv.emessa;
                            return (
                                <tr key={b.id} className="border-t border-[#9AA2AD]/10 hover:bg-[#1C2026]" data-testid={`fattura-row-${b.id}`}>
                                    <td className="p-3 font-display">{b.voucher_code}</td>
                                    <td className="p-3 text-xs">{b.created_at ? new Date(b.created_at).toLocaleDateString("it-IT") : "—"}</td>
                                    <td className="p-3">{b.customer_name}<br /><span className="text-[#9AA2AD] text-xs">{b.customer_email}</span></td>
                                    <td className="p-3 text-xs uppercase">
                                        <div>{fis.codice_fiscale || <span className="text-[#9AA2AD]">—</span>}</div>
                                        {inv.partita_iva && <div className="text-[#9AA2AD]">P.IVA: {inv.partita_iva}</div>}
                                    </td>
                                    <td className="p-3 text-xs">{inv.ragione_sociale || (inv.richiede_fattura === false ? <span className="text-[#9AA2AD]">Ricevuta</span> : <span className="text-[#9AA2AD]">{b.customer_name}</span>)}</td>
                                    <td className="p-3 text-xs uppercase">{inv.codice_sdi || inv.pec || <span className="text-[#9AA2AD]">—</span>}</td>
                                    <td className="p-3 font-display">€{(b.amount / 100).toFixed(2)}</td>
                                    <td className="p-3">
                                        {emessa ? (
                                            <span className="px-2 py-0.5 border text-[10px] uppercase tracking-widest border-[#16a34a] text-[#16a34a]">Emessa{inv.numero ? ` · ${inv.numero}` : ""}</span>
                                        ) : (
                                            <span className="px-2 py-0.5 border text-[10px] uppercase tracking-widest border-[#E2231A] text-[#E2231A]">Da emettere</span>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        {emessa ? (
                                            <button data-testid={`btn-invoice-undo-${b.id}`} onClick={async () => {
                                                try { await adminMarkInvoice(token, b.id, { emessa: false }); toast.success("Contrassegnata da emettere"); onChange(); }
                                                catch { toast.error("Errore"); }
                                            }} className="text-xs uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A]">Annulla</button>
                                        ) : (
                                            <MarkInvoiceDialog token={token} bookingId={b.id} onDone={onChange} />
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {list.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-[#9AA2AD]">Nessuna fattura {onlyPending ? "da emettere" : ""}.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function MarkInvoiceDialog({ token, bookingId, onDone }) {
    const [open, setOpen] = useState(false);
    const [numero, setNumero] = useState("");
    const [data, setData] = useState(new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(false);
    const submit = async () => {
        setLoading(true);
        try {
            await adminMarkInvoice(token, bookingId, { emessa: true, numero, data_emissione: data });
            toast.success("Fattura segnata come emessa");
            setOpen(false);
            onDone();
        } catch { toast.error("Errore"); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button data-testid={`btn-mark-invoice-${bookingId}`} className="text-xs uppercase tracking-widest text-[#E2231A] hover:text-[#F4F2ED] flex items-center gap-1"><Check size={12} /> Segna emessa</button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C2026] border-[#9AA2AD]/20 rounded-none text-[#F4F2ED]">
                <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Fattura emessa</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <input data-testid="input-invoice-number" value={numero} onChange={e => setNumero(e.target.value)} placeholder="Numero fattura (es. 2026/001)" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                    <input data-testid="input-invoice-date" type="date" value={data} onChange={e => setData(e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3" />
                </div>
                <DialogFooter>
                    <button onClick={() => setOpen(false)} className="border border-[#9AA2AD]/30 px-4 py-2 text-xs uppercase tracking-widest">Annulla</button>
                    <button data-testid="btn-confirm-invoice" onClick={submit} disabled={loading} className="bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 px-5 py-2 uppercase tracking-widest text-sm font-display">{loading ? "…" : "Conferma"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function VouchersView({ token }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ tier: "combo", purchaser_name: "", purchaser_email: "", recipient_name: "", recipient_email: "", message: "", admin_note: "", validity_days: 365, send_email: false });

    const load = async () => {
        try {
            const d = await adminListVouchers(token);
            setItems(d.items || []);
        } catch { toast.error("Errore caricamento voucher"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const stats = useMemo(() => {
        const s = { total: items.length, active: 0, redeemed: 0, cancelled: 0, expired: 0, revenue_cents: 0 };
        const now = new Date().toISOString();
        for (const v of items) {
            s.revenue_cents += v.amount_paid_cents || 0;
            if (v.status === "redeemed") s.redeemed++;
            else if (v.status === "cancelled") s.cancelled++;
            else if (v.expires_at && v.expires_at < now) s.expired++;
            else s.active++;
        }
        return s;
    }, [items]);

    const createVoucher = async () => {
        if (!form.tier) return;
        setCreating(true);
        try {
            const r = await api.post("/admin/vouchers/create-manual", form, { headers: { "x-admin-token": token } });
            toast.success(`Creato ${r.data.code}`);
            setShowCreate(false);
            setForm({ tier: "combo", purchaser_name: "", purchaser_email: "", recipient_name: "", recipient_email: "", message: "", admin_note: "", validity_days: 365, send_email: false });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore creazione");
        } finally { setCreating(false); }
    };

    const cancelVoucher = async (v) => {
        const reason = window.prompt(`Annullare voucher ${v.code}?\n\nMotivo (obbligatorio):`);
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/admin/vouchers/${v.id}/cancel`, { reason }, { headers: { "x-admin-token": token } });
            toast.success("Voucher annullato");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };

    const reactivateVoucher = async (v) => {
        if (!window.confirm(`Riattivare voucher ${v.code}?`)) return;
        try {
            await api.post(`/admin/vouchers/${v.id}/reactivate`, {}, { headers: { "x-admin-token": token } });
            toast.success("Riattivato");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };

    const resendEmails = async (v) => {
        try {
            const r = await api.post(`/admin/vouchers/${v.id}/resend-emails`, {}, { headers: { "x-admin-token": token } });
            toast.success(`Email inviate a: ${r.data.sent_to.join(", ") || "nessuno (mancano email)"}`);
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };

    if (loading) return <div className="text-center py-12 text-[#9AA2AD]">Caricamento…</div>;

    return (
        <div className="space-y-4" data-testid="vouchers-view">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatBox label="Totali" value={stats.total} testid="vc-total" />
                <StatBox label="Attivi" value={stats.active} color="#16a34a" testid="vc-active" />
                <StatBox label="Riscattati" value={stats.redeemed} color="#4B7BEA" testid="vc-redeemed" />
                <StatBox label="Annullati" value={stats.cancelled} color="#E2231A" testid="vc-cancelled" />
                <StatBox label="Incasso" value={`€${(stats.revenue_cents/100).toFixed(0)}`} color="#f59e0b" testid="vc-revenue" />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl uppercase">Voucher Cofanetti Regalo</h2>
                    <p className="text-xs text-[#9AA2AD] mt-1">Vendite via /voucher e /offerta-lancio · Riscattabili su /riscatta · Creabili manualmente per omaggi/promo</p>
                </div>
                <button
                    onClick={() => setShowCreate(v => !v)}
                    className="bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] px-4 py-2 text-[11px] uppercase tracking-widest"
                    data-testid="btn-create-manual-voucher"
                >+ Crea voucher manuale</button>
            </div>

            {/* Form creazione manuale */}
            {showCreate && (
                <div className="border border-[#E2231A]/40 bg-[#E2231A]/5 p-5 space-y-3" data-testid="create-voucher-form">
                    <h3 className="font-display uppercase text-sm tracking-widest">Voucher manuale (omaggio / promo / stampato)</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Taglio</span>
                            <select value={form.tier} onChange={e => setForm(f => ({...f, tier: e.target.value}))} className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm">
                                <option value="singola">Singola in Pista · €79</option>
                                <option value="combo">Combo Duello · €129</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Validità (giorni)</span>
                            <input type="number" min="30" max="1095" value={form.validity_days} onChange={e => setForm(f => ({...f, validity_days: parseInt(e.target.value)||365}))} className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Nome "acquirente" (chi regala)</span>
                            <input type="text" value={form.purchaser_name} onChange={e => setForm(f => ({...f, purchaser_name: e.target.value}))} placeholder="es. VELORA JDM (per omaggi)" className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Email acquirente (opzionale)</span>
                            <input type="email" value={form.purchaser_email} onChange={e => setForm(f => ({...f, purchaser_email: e.target.value}))} className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Nome destinatario</span>
                            <input type="text" value={form.recipient_name} onChange={e => setForm(f => ({...f, recipient_name: e.target.value}))} className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Email destinatario</span>
                            <input type="email" value={form.recipient_email} onChange={e => setForm(f => ({...f, recipient_email: e.target.value}))} className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm" />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Messaggio personalizzato (appare nel PDF)</span>
                        <textarea rows={2} value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder='es. "Vinci il concorso VELORA JDM!"' className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm resize-none" />
                    </label>
                    <label className="block">
                        <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Nota admin (interna, non visibile al cliente)</span>
                        <input type="text" value={form.admin_note} onChange={e => setForm(f => ({...f, admin_note: e.target.value}))} placeholder="es. Contest Instagram Marzo 2026" className="w-full bg-[#15181C] border border-[#9AA2AD]/20 px-3 py-2 mt-1 text-sm" />
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.send_email} onChange={e => setForm(f => ({...f, send_email: e.target.checked}))} />
                        <span>Invia subito email di consegna (se compilate)</span>
                    </label>
                    <div className="flex gap-2">
                        <button onClick={createVoucher} disabled={creating} className="bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="btn-confirm-create-voucher">{creating ? "Creazione…" : "Crea voucher"}</button>
                        <button onClick={() => setShowCreate(false)} className="border border-[#9AA2AD]/30 hover:border-[#F4F2ED] text-[#9AA2AD] hover:text-[#F4F2ED] px-4 py-2 text-[11px] uppercase tracking-widest">Annulla</button>
                    </div>
                </div>
            )}

            {/* Lista voucher */}
            <div className="border border-[#9AA2AD]/20 velora-scroll-x">
                <table className="w-full text-sm">
                    <thead className="bg-[#15181C] uppercase tracking-widest text-[10px] text-[#9AA2AD]">
                        <tr>
                            <th className="p-3 text-left">Codice</th>
                            <th className="p-3 text-left">Taglio</th>
                            <th className="p-3 text-left">Valore</th>
                            <th className="p-3 text-left">Da → A</th>
                            <th className="p-3 text-left">Stato</th>
                            <th className="p-3 text-left">Creato / Scadenza</th>
                            <th className="p-3 text-left">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#9AA2AD]">Nessun voucher ancora. Attendi la prima vendita o crea un omaggio.</td></tr>}
                        {items.map(v => (
                            <tr key={v.id} className="border-t border-[#9AA2AD]/10 hover:bg-[#1C2026]" data-testid={`voucher-row-${v.code}`}>
                                <td className="p-3 font-mono text-xs">{v.code}{v.created_by_admin && <div className="text-[9px] text-[#f59e0b] uppercase mt-0.5">manuale</div>}</td>
                                <td className="p-3 uppercase text-xs tracking-widest">{v.tier}</td>
                                <td className="p-3 font-display text-lg">€{(v.amount_cents/100).toFixed(0)}</td>
                                <td className="p-3 text-xs">
                                    <div>{v.purchaser_name || "—"}</div>
                                    <div className="text-[#9AA2AD] text-[10px]">→ {v.recipient_name || "(personale)"}</div>
                                </td>
                                <td className="p-3"><VoucherStatusBadge v={v} /></td>
                                <td className="p-3 text-[10px] text-[#9AA2AD]">
                                    {(v.created_at || "").slice(0, 10)}<br/>
                                    <span className="text-[9px]">scade {(v.expires_at || "").slice(0, 10)}</span>
                                </td>
                                <td className="p-3">
                                    <div className="flex flex-col gap-1 min-w-[140px]">
                                        <a href={`/api/voucher/pdf/${v.code}`} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] px-2 py-1 text-center" data-testid={`btn-voucher-pdf-${v.code}`}>📄 PDF Regalo</a>
                                        <button onClick={() => resendEmails(v)} className="text-[10px] uppercase tracking-widest border border-[#9AA2AD]/30 hover:border-[#4B7BEA] hover:text-[#4B7BEA] px-2 py-1" data-testid={`btn-voucher-resend-${v.code}`}>✉ Reinvia email</button>
                                        {v.status === "active" && <button onClick={() => cancelVoucher(v)} className="text-[10px] uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1" data-testid={`btn-voucher-cancel-${v.code}`}>✗ Annulla</button>}
                                        {v.status === "cancelled" && <button onClick={() => reactivateVoucher(v)} className="text-[10px] uppercase tracking-widest border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] px-2 py-1" data-testid={`btn-voucher-reactivate-${v.code}`}>↺ Riattiva</button>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function VoucherStatusBadge({ v }) {
    const now = new Date().toISOString();
    const isExpired = v.expires_at && v.expires_at < now && v.status === "active";
    const map = {
        active: { color: "#16a34a", label: "Attivo" },
        redeemed: { color: "#4B7BEA", label: "Riscattato" },
        cancelled: { color: "#E2231A", label: "Annullato" },
    };
    const cur = isExpired ? { color: "#f59e0b", label: "Scaduto" } : (map[v.status] || { color: "#9AA2AD", label: v.status });
    return <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1" style={{ background: `${cur.color}22`, color: cur.color, border: `1px solid ${cur.color}55` }}>{cur.label}</span>;
}

function StatBox({ label, value, color = "#F4F2ED", testid }) {
    const { eventLabel } = useEventScope();
    label = `${label} · ${eventLabel}`;
    return (
        <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-3" data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">{label}</div>
            <div className="font-display text-2xl mt-1" style={{ color }}>{value}</div>
        </div>
    );
}

// V52 · DiscountsView — Yen-Velorese Sconti manager
function DiscountsView({ token }) {
    const [items, setItems] = useState([]);
    const [customers, setCustomers] = useState([]);   // V52.3 · rubrica clienti
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [f, setF] = useState({ kind: "percent", value: 20, code: "", email: "", recipient_name: "", expires_at: "", max_uses: 1 });
    const [previewId, setPreviewId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rubricaField, setRubricaField] = useState(null); // "email" | "name" | null
    const [bulkModal, setBulkModal] = useState(null); // { discount, ... } | null

    const load = async () => {
        setLoading(true);
        try {
            const [ds, cs] = await Promise.all([adminListDiscounts(token), adminListCustomers(token).catch(() => ({ customers: [] }))]);
            setItems(ds);
            setCustomers(cs.customers || []);
        }
        catch (e) { toast.error("Errore caricamento sconti"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const pickCustomer = (c) => {
        setF(prev => ({ ...prev, email: c.email || prev.email, recipient_name: c.name || prev.recipient_name }));
        setRubricaField(null);
    };

    // Filtro rubrica in base al campo attivo
    const rubricaFiltered = useMemo(() => {
        if (!rubricaField) return [];
        const q = (rubricaField === "email" ? f.email : f.recipient_name).trim().toLowerCase();
        const base = customers.filter(c => (c.email || c.name));
        if (!q) return base.slice(0, 20);
        return base.filter(c =>
            (c.email || "").toLowerCase().includes(q) ||
            (c.name || "").toLowerCase().includes(q)
        ).slice(0, 20);
    }, [rubricaField, f.email, f.recipient_name, customers]);

    const create = async () => {
        if (!f.value || f.value <= 0) { toast.error("Valore sconto obbligatorio"); return; }
        if (f.kind === "percent" && f.value > 100) { toast.error("Percentuale max 100"); return; }
        setBusy(true);
        try {
            const payload = {
                kind: f.kind,
                value: parseFloat(f.value),
                code: f.code.trim().toUpperCase().replace(/[^A-Z0-9\-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || null,
                email: f.email.trim().toLowerCase() || null,
                recipient_name: f.recipient_name.trim() || null,
                expires_at: f.expires_at || null,
                max_uses: parseInt(f.max_uses) || null,
            };
            const r = await adminCreateDiscount(token, payload);
            toast.success(`Sconto creato: ${r.discount.code}`);
            setShowForm(false);
            setF({ kind: "percent", value: 20, code: "", email: "", recipient_name: "", expires_at: "", max_uses: 1 });
            await load();
            setPreviewId(r.discount.id);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione"); }
        finally { setBusy(false); }
    };

    const sendEmail = async (d) => {
        const to = prompt(`Invia banconota Yen-Velorese a quale email?`, d.email || "");
        if (!to || !to.includes("@")) return;
        setBusy(true);
        try {
            const r = await adminSendDiscountEmail(token, d.id, to);
            if (r.ok) toast.success(`Email inviata a ${to}`);
            else toast.error(r.detail?.hint || r.detail?.reason || "Errore invio");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    const deactivate = async (d) => {
        if (!window.confirm(`Disattivare lo sconto ${d.code}?\nNon sarà più utilizzabile su Stripe.`)) return;
        setBusy(true);
        try {
            await adminDeactivateDiscount(token, d.id);
            toast.success("Sconto disattivato");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="discounts-view">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="uppercase tracking-widest text-sm text-[#F4F2ED]">🎌 Sconti Yen-Velorese</h2>
                    <p className="text-[10px] text-[#9AA2AD]">Codici sconto sincronizzati automaticamente con Stripe Promotion Codes · il cliente li inserisce nel checkout</p>
                </div>
                <button onClick={() => setShowForm(v => !v)} data-testid="btn-new-discount"
                    className="bg-[#E2231A] text-white uppercase tracking-widest text-xs px-4 py-2 hover:bg-[#c41d15]">
                    {showForm ? "✕ Annulla" : "＋ Nuovo Sconto"}
                </button>
            </div>

            {showForm && (
                <div className="border border-[#E2231A]/40 bg-[#1C2026]/50 p-4 space-y-3" data-testid="discount-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Tipo sconto</div>
                            <select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} data-testid="disc-kind"
                                className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2">
                                <option value="percent">% Percentuale (es. -20%)</option>
                                <option value="amount">€ Cifra fissa (es. -€25)</option>
                            </select>
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Valore {f.kind === "percent" ? "(%)" : "(€)"}</div>
                            <input type="number" step="0.01" value={f.value} onChange={e => setF({ ...f, value: e.target.value })} data-testid="disc-value"
                                className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2 font-mono" />
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Codice (vuoto = auto)</div>
                            <input type="text" value={f.code} onChange={e => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9\-]+/g, "-").replace(/-+/g, "-") })} data-testid="disc-code"
                                placeholder="es. AMICO20" className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2 font-mono uppercase" />
                            <div className="text-[8px] text-[#9AA2AD]/70 mt-1">Solo lettere, numeri e trattini · gli spazi diventano "-"</div>
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Max utilizzi (vuoto = illimitato)</div>
                            <input type="number" min="1" value={f.max_uses} onChange={e => setF({ ...f, max_uses: e.target.value })} data-testid="disc-max-uses"
                                className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2" />
                        </label>
                        <label className="block relative">
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Nome destinatario (opzionale)</div>
                                <button type="button" onClick={() => setRubricaField(rubricaField === "name" ? null : "name")}
                                    data-testid="disc-rubrica-name"
                                    className="text-[9px] uppercase tracking-widest text-[#4B7BEA] hover:text-[#E2231A]">
                                    📇 Rubrica
                                </button>
                            </div>
                            <input type="text" value={f.recipient_name} onChange={e => setF({ ...f, recipient_name: e.target.value })} data-testid="disc-recipient-name"
                                onFocus={() => setRubricaField("name")}
                                placeholder="es. Mario Rossi" className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2" />
                            {rubricaField === "name" && rubricaFiltered.length > 0 && (
                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0F1114] border border-[#E2231A]/40 max-h-56 overflow-y-auto shadow-2xl" data-testid="rubrica-name-list">
                                    {rubricaFiltered.map(c => (
                                        <button type="button" key={c.email} onClick={() => pickCustomer(c)}
                                            data-testid={`rubrica-item-${c.email}`}
                                            className="block w-full text-left px-3 py-2 hover:bg-[#E2231A]/10 border-b border-[#9AA2AD]/10 last:border-0">
                                            <div className="text-xs text-[#F4F2ED] font-semibold">{c.name || <span className="text-[#9AA2AD] italic">Senza nome</span>}</div>
                                            <div className="text-[10px] text-[#9AA2AD] font-mono">{c.email} · {c.count || 0} ordini</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </label>
                        <label className="block relative">
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Email destinatario (per invio)</div>
                                <button type="button" onClick={() => setRubricaField(rubricaField === "email" ? null : "email")}
                                    data-testid="disc-rubrica-email"
                                    className="text-[9px] uppercase tracking-widest text-[#4B7BEA] hover:text-[#E2231A]">
                                    📇 Rubrica
                                </button>
                            </div>
                            <input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="disc-email"
                                onFocus={() => setRubricaField("email")}
                                placeholder="mario@example.com" className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2" />
                            {rubricaField === "email" && rubricaFiltered.length > 0 && (
                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0F1114] border border-[#E2231A]/40 max-h-56 overflow-y-auto shadow-2xl" data-testid="rubrica-email-list">
                                    {rubricaFiltered.map(c => (
                                        <button type="button" key={c.email} onClick={() => pickCustomer(c)}
                                            data-testid={`rubrica-item-email-${c.email}`}
                                            className="block w-full text-left px-3 py-2 hover:bg-[#E2231A]/10 border-b border-[#9AA2AD]/10 last:border-0">
                                            <div className="text-xs text-[#F4F2ED] font-mono">{c.email}</div>
                                            <div className="text-[10px] text-[#9AA2AD]">{c.name || "—"} · {c.count || 0} ordini</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </label>
                        <label className="block md:col-span-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Scadenza (opzionale)</div>
                            <input type="datetime-local" value={f.expires_at} onChange={e => setF({ ...f, expires_at: e.target.value })} data-testid="disc-expires"
                                className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-2 py-2" />
                        </label>
                    </div>
                    <button onClick={create} disabled={busy} data-testid="btn-create-discount"
                        className="bg-[#E2231A] text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#c41d15] disabled:opacity-40">
                        {busy ? "Creazione…" : "Genera codice + banconota"}
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-[#9AA2AD] p-6 text-center">Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="text-[#9AA2AD] p-6 text-center border border-dashed border-[#9AA2AD]/20">
                    Nessuno sconto creato — premi "＋ Nuovo Sconto"
                </div>
            ) : (
                <DualScrollX testid="discounts-scroll" maxHeight="70vh">
                    <table className="w-full text-xs min-w-[900px]">
                        <thead className="sticky top-0 z-10 bg-[#1C2026] text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                            <tr>
                                <th className="text-left p-2">Codice</th>
                                <th className="text-left p-2">Sconto</th>
                                <th className="text-left p-2">Destinatario</th>
                                <th className="text-left p-2">Utilizzi</th>
                                <th className="text-left p-2">Scadenza</th>
                                <th className="text-left p-2">Email</th>
                                <th className="text-left p-2">Stato</th>
                                <th className="text-right p-2">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(d => (
                                <tr key={d.id} className="border-t border-[#9AA2AD]/10" data-testid={`row-discount-${d.code}`}>
                                    <td className="p-2 font-mono font-bold text-[#F4F2ED]">{d.code}</td>
                                    <td className="p-2 text-[#E2231A] font-bold">{d.kind === "percent" ? `-${d.value}%` : `-€${d.value}`}</td>
                                    <td className="p-2">{d.recipient_name || <span className="text-[#9AA2AD]">al portatore</span>}</td>
                                    <td className="p-2">
                                        {d.usage_count > 0 ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#16a34a]/15 border border-[#16a34a]/40 text-[#16a34a] px-2 py-0.5" data-testid={`usage-${d.code}`}>
                                                ✓ Usato {d.usage_count}{d.max_uses ? `/${d.max_uses}` : ""} {d.usage_count === 1 ? "volta" : "volte"}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#9AA2AD]" data-testid={`usage-${d.code}`}>
                                                ○ Mai usato{d.max_uses ? ` (0/${d.max_uses})` : ""}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-2 text-[10px]">{d.expires_at ? new Date(d.expires_at).toLocaleDateString("it-IT") : "—"}</td>
                                    <td className="p-2 text-[10px] text-[#9AA2AD]">{d.email || "—"}{d.email_sent_at && " ✓"}</td>
                                    <td className="p-2">{d.active ? <span className="text-[#16a34a]">● Attivo</span> : <span className="text-[#9AA2AD]">○ Disattivato</span>}</td>
                                    <td className="p-2 text-right">
                                        <div className="flex gap-1 justify-end flex-wrap">
                                            <button onClick={() => setPreviewId(previewId === d.id ? null : d.id)} data-testid={`btn-preview-${d.code}`}
                                                className="text-[10px] border border-[#4B7BEA]/40 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-2 py-1 uppercase tracking-widest">👁 Anteprima</button>
                                            <button onClick={() => sendEmail(d)} disabled={busy || !d.active} data-testid={`btn-send-${d.code}`}
                                                className="text-[10px] border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] px-2 py-1 uppercase tracking-widest disabled:opacity-40">📧 Invia 1</button>
                                            <button onClick={() => setBulkModal({ discount: d })} disabled={busy || !d.active} data-testid={`btn-bulk-send-${d.code}`}
                                                className="text-[10px] border border-[#9333ea]/40 hover:bg-[#9333ea]/10 text-[#9333ea] px-2 py-1 uppercase tracking-widest disabled:opacity-40">🚀 Bulk</button>
                                            {d.active && (
                                                <button onClick={() => deactivate(d)} disabled={busy} data-testid={`btn-deact-${d.code}`}
                                                    className="text-[10px] border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1 uppercase tracking-widest disabled:opacity-40">✕ Disattiva</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DualScrollX>
            )}

            {previewId && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewId(null)}>
                    <div className="max-w-3xl w-full" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end mb-2">
                            <button onClick={() => setPreviewId(null)} className="text-white bg-[#E2231A] px-3 py-1 text-xs uppercase tracking-widest">✕ Chiudi</button>
                        </div>
                        <img src={adminDiscountPreviewUrl(token, previewId)} alt="Banconota Yen-Velorese" className="w-full block" data-testid="discount-preview-img" />
                    </div>
                </div>
            )}

            {bulkModal && (
                <BulkSendModal
                    token={token}
                    discount={bulkModal.discount}
                    customers={customers}
                    onClose={() => setBulkModal(null)}
                    onSent={async () => { setBulkModal(null); await load(); }}
                />
            )}
        </div>
    );
}


// V52.6 · Modal per invio massivo sconto con testo custom + selezione clienti
function BulkSendModal({ token, discount, customers, onClose, onSent }) {
    const label = discount.kind === "percent" ? `-${discount.value}%` : `-€${discount.value}`;
    const [mode, setMode] = useState("past_event"); // past_event | all | selected
    const [selected, setSelected] = useState(new Set()); // set di email
    const [search, setSearch] = useState("");
    const [subject, setSubject] = useState(`🎌 Invito al prossimo evento · Yen-Velorese ${label} su VELORA JDM`);
    const [message, setMessage] = useState(
        `Grazie per aver partecipato al nostro ultimo evento su pista.\n\n` +
        `Per ringraziarti abbiamo preparato uno Yen-Velorese ${label} da usare al prossimo Track Day.\n` +
        `Il codice qui sotto scala automaticamente al checkout Stripe — nessun passaggio manuale.\n\n` +
        `Ci vediamo in pista.\n— Il team VELORA JDM`
    );
    const [busy, setBusy] = useState(false);

    const pastEventCustomers = customers.filter(c => c.past_event);
    const filtered = customers.filter(c => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (c.email || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q);
    });

    const toggle = (email) => {
        const next = new Set(selected);
        if (next.has(email)) next.delete(email);
        else next.add(email);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map(c => c.email)));
    };

    const targetCount = mode === "past_event" ? pastEventCustomers.length
                       : mode === "all" ? customers.length
                       : selected.size;

    const submit = async () => {
        if (targetCount === 0) { toast.error("Seleziona almeno 1 destinatario"); return; }
        if (!window.confirm(`Confermi invio email a ${targetCount} destinatari?\nCodice: ${discount.code}\nSconto: ${label}`)) return;
        setBusy(true);
        try {
            const payload = {
                custom_message: message,
                custom_subject: subject,
                emails: mode === "selected" ? Array.from(selected) : [],
                all_customers: mode === "all",
                only_past_event: mode === "past_event",
            };
            const r = await adminBulkSendDiscount(token, discount.id, payload);
            toast.success(`✅ Inviate ${r.sent_count}/${r.sent_count + r.failed_count} email`);
            if (r.failed_count > 0) toast.error(`${r.failed_count} fallite`);
            await onSent();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio massivo"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="bulk-send-modal">
            <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto bg-[#0F1114] border border-[#9333ea]/40" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-[#9AA2AD]/20 flex items-center justify-between sticky top-0 bg-[#0F1114] z-10">
                    <div>
                        <h3 className="uppercase tracking-widest text-sm text-[#9333ea]">🚀 Invio Massivo · {discount.code}</h3>
                        <p className="text-[10px] text-[#9AA2AD]">Sconto <strong className="text-[#E2231A]">{label}</strong> · a più clienti con testo personalizzato</p>
                    </div>
                    <button onClick={onClose} className="text-white bg-[#E2231A] px-3 py-1 text-xs uppercase tracking-widest" data-testid="bulk-close">✕</button>
                </div>

                <div className="p-4 space-y-4">
                    {/* MODE SELECTOR */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <button onClick={() => setMode("past_event")} data-testid="bulk-mode-past"
                            className={`p-3 border text-left ${mode === "past_event" ? "border-[#9333ea] bg-[#9333ea]/10" : "border-[#9AA2AD]/30 hover:border-[#9AA2AD]/60"}`}>
                            <div className="text-[10px] uppercase tracking-widest text-[#9333ea]">👥 Solo clienti evento precedente</div>
                            <div className="text-xs text-[#F4F2ED] mt-1">Chi ha già partecipato (arrivato/checked-in)</div>
                            <div className="text-[10px] text-[#9AA2AD] mt-1">{pastEventCustomers.length} clienti</div>
                        </button>
                        <button onClick={() => setMode("all")} data-testid="bulk-mode-all"
                            className={`p-3 border text-left ${mode === "all" ? "border-[#9333ea] bg-[#9333ea]/10" : "border-[#9AA2AD]/30 hover:border-[#9AA2AD]/60"}`}>
                            <div className="text-[10px] uppercase tracking-widest text-[#9333ea]">🌐 Tutta la rubrica</div>
                            <div className="text-xs text-[#F4F2ED] mt-1">Ogni email univoca in DB</div>
                            <div className="text-[10px] text-[#9AA2AD] mt-1">{customers.length} clienti</div>
                        </button>
                        <button onClick={() => setMode("selected")} data-testid="bulk-mode-sel"
                            className={`p-3 border text-left ${mode === "selected" ? "border-[#9333ea] bg-[#9333ea]/10" : "border-[#9AA2AD]/30 hover:border-[#9AA2AD]/60"}`}>
                            <div className="text-[10px] uppercase tracking-widest text-[#9333ea]">✏️ Seleziona singolarmente</div>
                            <div className="text-xs text-[#F4F2ED] mt-1">Scegli chi vuoi dalla rubrica</div>
                            <div className="text-[10px] text-[#9AA2AD] mt-1">{selected.size} selezionati</div>
                        </button>
                    </div>

                    {/* SELECTION LIST (only if mode=selected) */}
                    {mode === "selected" && (
                        <div className="border border-[#9AA2AD]/20 bg-[#15181C]" data-testid="bulk-select-list">
                            <div className="flex items-center gap-2 p-2 border-b border-[#9AA2AD]/20">
                                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                                    placeholder="🔍 Cerca per nome o email…"
                                    className="flex-1 bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#9333ea] outline-none text-xs px-2 py-1.5"
                                    data-testid="bulk-search" />
                                <button onClick={toggleAll} data-testid="bulk-toggle-all"
                                    className="text-[10px] uppercase tracking-widest border border-[#9333ea]/40 hover:bg-[#9333ea]/10 text-[#9333ea] px-2 py-1.5">
                                    {selected.size === filtered.length && filtered.length > 0 ? "Deseleziona tutti" : "Seleziona tutti"}
                                </button>
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                                {filtered.length === 0 ? (
                                    <div className="p-4 text-center text-[#9AA2AD] text-xs">Nessun cliente trovato</div>
                                ) : filtered.map(c => (
                                    <label key={c.email} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#9333ea]/5 border-b border-[#9AA2AD]/10 cursor-pointer" data-testid={`bulk-row-${c.email}`}>
                                        <input type="checkbox" checked={selected.has(c.email)} onChange={() => toggle(c.email)}
                                            className="accent-[#9333ea]" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-[#F4F2ED] truncate">{c.name || <span className="text-[#9AA2AD] italic">Senza nome</span>}</div>
                                            <div className="text-[10px] text-[#9AA2AD] font-mono truncate">{c.email}</div>
                                        </div>
                                        <div className="text-[9px] text-[#9AA2AD] uppercase tracking-widest">
                                            {c.past_event && <span className="text-[#16a34a]">● passato</span>}
                                            <span className="ml-2">{c.count || 0} ord</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* EMAIL SUBJECT */}
                    <label className="block">
                        <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Oggetto email</div>
                        <input type="text" value={subject} onChange={e => setSubject(e.target.value)} data-testid="bulk-subject"
                            className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#9333ea] outline-none text-xs px-2 py-2" />
                    </label>

                    {/* CUSTOM MESSAGE */}
                    <label className="block">
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Testo email personalizzato · va sopra la banconota</div>
                            <button type="button" onClick={() => setMessage("")} className="text-[9px] uppercase tracking-widest text-[#4B7BEA] hover:text-[#E2231A]">🗑 Svuota</button>
                        </div>
                        <textarea value={message} onChange={e => setMessage(e.target.value)} rows="8"
                            data-testid="bulk-message"
                            placeholder="Scrivi qui il tuo messaggio… (le nuove righe diventano <br/> nell'email)"
                            className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#9333ea] outline-none text-xs px-3 py-2 font-mono resize-y" />
                        <div className="text-[9px] text-[#9AA2AD]/70 mt-1">Sotto il messaggio viene inserita automaticamente la banconota Yen-Velorese con il codice · gli step "Come usarlo" restano sempre in fondo</div>
                    </label>

                    {/* CTA */}
                    <div className="flex items-center justify-between border-t border-[#9AA2AD]/20 pt-4">
                        <div className="text-xs text-[#9AA2AD]">
                            Invierai <strong className="text-[#F4F2ED]">{targetCount}</strong> email · codice <span className="font-mono text-[#E2231A]">{discount.code}</span>
                        </div>
                        <button onClick={submit} disabled={busy || targetCount === 0}
                            data-testid="bulk-send-confirm"
                            className="bg-[#9333ea] text-white uppercase tracking-widest text-xs px-4 py-2 hover:bg-[#7e22ce] disabled:opacity-40">
                            {busy ? "Invio in corso…" : `🚀 Invia a ${targetCount} clienti`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}



function MediaProductsView({ token }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);

    const load = async () => {
        try {
            const d = await adminGetMediaProducts(token);
            setItems(d.items || []);
        } catch { toast.error("Errore caricamento pacchetti media"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateField = (pid, field, value) => {
        setItems(prev => prev.map(p => p.id === pid ? { ...p, [field]: value } : p));
    };

    const savePid = async (pid) => {
        const p = items.find(x => x.id === pid);
        if (!p) return;
        setSaving(pid);
        try {
            const features = typeof p.features === "string"
                ? p.features.split("\n").map(s => s.trim()).filter(Boolean)
                : p.features;
            await adminUpdateMediaProduct(token, {
                product_id: pid,
                icon: p.icon,
                title: p.title,
                features,
                featured: !!p.featured,
                enabled: p.enabled !== false,
                order: parseInt(p.order || 99, 10),
            });
            toast.success(`${p.title} aggiornato`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore salvataggio");
        } finally { setSaving(null); }
    };

    const resetAll = async () => {
        if (!window.confirm("Ripristinare i default per TUTTI i pacchetti media? Le tue modifiche saranno perse.")) return;
        try {
            await adminResetMediaProducts(token);
            toast.success("Default ripristinati");
            await load();
        } catch { toast.error("Errore ripristino"); }
    };

    if (loading) return <div className="text-center py-12 text-[#9AA2AD]">Caricamento…</div>;

    return (
        <div className="space-y-4" data-testid="media-products-view">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl uppercase">Pacchetti Foto & Video</h2>
                    <p className="text-xs text-[#9AA2AD] mt-1">Modifica titoli, icone e caratteristiche dei pacchetti mostrati in home e nella pagina Boarding Pass. <b className="text-[#F4F2ED]">Il prezzo</b> resta gestito lato server per garantire coerenza con Stripe.</p>
                </div>
                <button
                    onClick={resetAll}
                    className="border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors duration-150"
                    data-testid="btn-reset-media-products"
                >Ripristina default</button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                {items.map(p => (
                    <div
                        key={p.id}
                        className={`border ${p.featured ? "border-[#E2231A] bg-gradient-to-br from-[#E2231A]/10 to-[#1C2026]" : "border-[#9AA2AD]/30 bg-[#1C2026]"} p-5 flex flex-col`}
                        data-testid={`media-product-card-${p.id}`}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD] font-mono">{p.id}</span>
                            <span className="text-[10px] uppercase tracking-widest text-[#F4F2ED] bg-[#E2231A] px-2 py-0.5">€{p.price}</span>
                        </div>
                        <label className="block mb-3">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Icona (emoji)</span>
                            <input
                                type="text"
                                value={p.icon || ""}
                                onChange={(e) => updateField(p.id, "icon", e.target.value)}
                                className="w-full bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none text-2xl px-3 py-2 mt-1"
                                data-testid={`input-media-icon-${p.id}`}
                            />
                        </label>
                        <label className="block mb-3">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Titolo</span>
                            <input
                                type="text"
                                value={p.title || ""}
                                onChange={(e) => updateField(p.id, "title", e.target.value)}
                                className="w-full bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none text-sm px-3 py-2 mt-1"
                                data-testid={`input-media-title-${p.id}`}
                                placeholder='es. "Foto HD" (evita numeri hardcoded)'
                            />
                        </label>
                        <label className="block mb-3">
                            <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Caratteristiche (una per riga)</span>
                            <textarea
                                value={Array.isArray(p.features) ? p.features.join("\n") : (p.features || "")}
                                onChange={(e) => updateField(p.id, "features", e.target.value)}
                                rows={5}
                                className="w-full bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none text-xs px-3 py-2 mt-1 font-mono resize-none"
                                data-testid={`input-media-features-${p.id}`}
                            />
                        </label>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <label className="block">
                                <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Ordine</span>
                                <input
                                    type="number"
                                    min="1"
                                    value={p.order || 99}
                                    onChange={(e) => updateField(p.id, "order", e.target.value)}
                                    className="w-full bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none text-sm px-3 py-2 mt-1"
                                    data-testid={`input-media-order-${p.id}`}
                                />
                            </label>
                            <div className="flex flex-col gap-2 pt-1">
                                <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#F4F2ED] cursor-pointer">
                                    <input type="checkbox" checked={!!p.featured} onChange={(e) => updateField(p.id, "featured", e.target.checked)} data-testid={`chk-media-featured-${p.id}`} />
                                    In evidenza
                                </label>
                                <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#F4F2ED] cursor-pointer">
                                    <input type="checkbox" checked={p.enabled !== false} onChange={(e) => updateField(p.id, "enabled", e.target.checked)} data-testid={`chk-media-enabled-${p.id}`} />
                                    Visibile in home
                                </label>
                            </div>
                        </div>
                        <button
                            onClick={() => savePid(p.id)}
                            disabled={saving === p.id}
                            className="mt-auto bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 text-[#F4F2ED] py-2 text-[11px] uppercase tracking-widest transition-colors duration-150"
                            data-testid={`btn-save-media-${p.id}`}
                        >{saving === p.id ? "Salvataggio…" : "Salva modifiche"}</button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CarsView({ token }) {
    const [cars, setCars] = useState({ EVO: null, WRX: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        adminGetCars().then(d => { setCars(d); setLoading(false); }).catch(() => { setLoading(false); toast.error("Errore caricamento"); });
    }, []);

    const updateField = (carId, field, value) => {
        setCars(prev => ({ ...prev, [carId]: { ...prev[carId], [field]: value } }));
    };

    // V47 · Rendering: aggiungi la tabella prezzi dopo la sezione auto
    // Nota: il return principale è più giù; qui iniettiamo pricingSection sopra il return con state
    // eslint-disable-next-line no-unused-vars
    const _pricingSectionMarker = true;

    const saveCar = async (carId) => {
        setSaving(carId);
        try {
            const c = cars[carId];
            const payload = {
                title: c.title,
                subtitle: c.subtitle,
                description: c.description,
                image_url: c.image_url,
                specs: (typeof c.specs === "string" ? c.specs.split(",").map(s => s.trim()).filter(Boolean) : c.specs),
            };
            await adminUpdateCar(token, carId, payload);
            toast.success(`${carId} aggiornata`);
        } catch (e) {
            toast.error("Errore salvataggio");
        } finally { setSaving(null); }
    };

    const uploadImage = async (carId, file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return toast.error("Immagine troppo grande (max 5MB)");
        // Convert to base64 data URL for immediate persistence
        const reader = new FileReader();
        reader.onload = () => { updateField(carId, "image_url", reader.result); toast.info("Immagine pronta — clicca Salva"); };
        reader.readAsDataURL(file);
    };

    if (loading) return <div className="text-center py-12 text-[#9AA2AD]">Caricamento…</div>;

    return (
        <div className="grid md:grid-cols-2 gap-6">
            {["EVO", "WRX", "PASSENGER"].map(carId => {
                const c = cars[carId] || {};
                const specsStr = Array.isArray(c.specs) ? c.specs.join(", ") : (c.specs || "");
                return (
                    <div key={carId} className="border border-[#9AA2AD]/20 bg-[#1C2026] p-5" data-testid={`car-editor-${carId}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Car className={carId === "EVO" ? "text-[#E2231A]" : "text-[#4B7BEA]"} size={20} />
                                <h3 className="font-display text-2xl uppercase">{carId}</h3>
                            </div>
                            <button data-testid={`save-car-${carId}`} onClick={() => saveCar(carId)} disabled={saving === carId} className="bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"><Save size={14} /> {saving === carId ? "…" : "Salva"}</button>
                        </div>
                        {c.image_url && (
                            <div className="mb-3 h-40 bg-cover bg-center border border-[#9AA2AD]/10" style={{ backgroundImage: `url(${c.image_url})` }} />
                        )}
                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1 mt-3">URL immagine</label>
                        <input data-testid={`car-${carId}-image-url`} value={c.image_url || ""} onChange={e => updateField(carId, "image_url", e.target.value)} placeholder="https://..." className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm mb-1" />
                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2 mt-1">... oppure carica file (max 5MB)</label>
                        <input data-testid={`car-${carId}-image-file`} type="file" accept="image/*" onChange={e => uploadImage(carId, e.target.files?.[0])} className="w-full text-xs text-[#9AA2AD] mb-3" />

                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Titolo</label>
                        <input data-testid={`car-${carId}-title`} value={c.title || ""} onChange={e => updateField(carId, "title", e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm mb-3" />

                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Sottotitolo</label>
                        <input data-testid={`car-${carId}-subtitle`} value={c.subtitle || ""} onChange={e => updateField(carId, "subtitle", e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm mb-3" />

                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Descrizione</label>
                        <textarea data-testid={`car-${carId}-description`} value={c.description || ""} onChange={e => updateField(carId, "description", e.target.value)} rows={5} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm mb-3" />

                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Specifiche (separate da virgola)</label>
                        <input data-testid={`car-${carId}-specs`} value={specsStr} onChange={e => updateField(carId, "specs", e.target.value)} placeholder="300 cv, 4x4 S-AWC, ..." className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm" />
                    </div>
                );
            })}
            <p className="md:col-span-2 text-[10px] uppercase tracking-widest text-[#9AA2AD]/70">Le modifiche sono visibili immediatamente sulla homepage dopo il salvataggio.</p>

            {/* V47 · Tabella Prezzi Dinamica */}
            <div className="md:col-span-2 mt-6">
                <PricingTable token={token} />
            </div>
        </div>
    );
}

function SendEmailMenu({ token, bookingId, onReset }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const send = async (tmpl) => {
        setBusy(tmpl);
        try {
            const r = await adminSendEmail(token, bookingId, tmpl);
            if (r.ok) toast.success(`Email "${tmpl}" inviata`);
            else toast.error(r.hint || `Errore: ${r.reason || "invio fallito"}`);
        } catch { toast.error("Errore rete"); }
        finally { setBusy(null); setOpen(false); }
    };
    const reset = async () => {
        setBusy("reset");
        try {
            await adminResetEmailStatus(token, bookingId);
            toast.success("Stato email azzerato per questa prenotazione");
            onReset && onReset();
        } catch { toast.error("Errore rete"); }
        finally { setBusy(null); setOpen(false); }
    };
    return (
        <div className="relative">
            <button data-testid={`btn-send-email-${bookingId}`} onClick={() => setOpen(v => !v)} className="text-[10px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A] transition-colors duration-150">✉ Email ▾</button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-10 bg-[#1C2026] border border-[#9AA2AD]/30 w-56 shadow-lg">
                    {[
                        { k: "receipt", l: "Ricevuta" },
                        { k: "reminder_completion", l: "Reminder completamento" },
                        { k: "checkin", l: "Check-in (3 gg)" },
                        { k: "day_before", l: "Vigilia" },
                    ].map(o => (
                        <button key={o.k} data-testid={`email-tmpl-${o.k}-${bookingId}`} onClick={() => send(o.k)} disabled={!!busy} className="block w-full text-left px-3 py-2 text-xs uppercase tracking-widest hover:bg-[#E2231A]/20 hover:text-[#F4F2ED] disabled:opacity-40">{busy === o.k ? "invio…" : o.l}</button>
                    ))}
                    <div className="border-t border-[#9AA2AD]/20" />
                    <button data-testid={`email-reset-${bookingId}`} onClick={reset} disabled={!!busy} className="block w-full text-left px-3 py-2 text-xs uppercase tracking-widest text-[#ca8a04] hover:bg-[#ca8a04]/20 hover:text-[#F4F2ED] disabled:opacity-40">{busy === "reset" ? "reset…" : "↺ Reset stato email"}</button>
                </div>
            )}
        </div>
    );
}


// ═══════════ FASE 1 — Diagnostica + azioni email di emergenza ═══════════

function EmailHealthPanel({ token }) {
    const [health, setHealth] = useState(null);
    const [testTo, setTestTo] = useState("");
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const load = () => adminEmailHealth(token).then(setHealth).catch(() => {});
    useEffect(() => { load(); }, [token]);

    const runTest = async () => {
        if (!testTo || !testTo.includes("@")) { toast.error("Inserisci un'email valida"); return; }
        setBusy(true);
        try {
            const r = await adminEmailTest(token, testTo);
            if (r.ok) toast.success(`Email di test inviata a ${testTo}`);
            else toast.error(r.hint || r.reason || "Invio fallito");
            load();
        } catch { toast.error("Errore rete"); }
        finally { setBusy(false); }
    };

    if (!health) return null;
    const ok = health.resend_api_key_set;
    const usingTestSender = (health.sender_email || "").includes("onboarding@resend.dev");
    const critical = !ok || usingTestSender || health.recent_email_failed > 0;
    return (
        <div data-testid="email-health-panel" className={`border-l-4 ${critical ? "border-[#E2231A]" : "border-[#16a34a]"} bg-[#1C2026] mb-4`}>
            <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-3">
                    <Activity size={16} className={critical ? "text-[#E2231A]" : "text-[#16a34a]"} strokeWidth={1.5} />
                    <span className="text-xs uppercase tracking-widest font-display">Diagnostica Email</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${ok ? "border-[#16a34a] text-[#16a34a]" : "border-[#E2231A] text-[#E2231A]"}`}>{ok ? "API OK" : "API MANCANTE"}</span>
                    {usingTestSender && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#ca8a04] text-[#ca8a04]">Sender di test</span>}
                    {health.recent_email_failed > 0 && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#E2231A] text-[#E2231A]">{health.recent_email_failed} fallite di recente</span>}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">{open ? "▲ Chiudi" : "▼ Apri"}</span>
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-[#9AA2AD]/10 pt-3 text-xs">
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">RESEND_API_KEY</div>
                            <div className="font-mono">{health.resend_api_key_prefix || <span className="text-[#E2231A]">— non impostata —</span>}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Sender (from)</div>
                            <div className="font-mono truncate">{health.sender_email}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">PUBLIC_APP_URL</div>
                            <div className="font-mono truncate">{health.public_url}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Recenti pagate (20)</div>
                            <div><span className="text-[#16a34a]">✓ {health.recent_email_ok}</span> · <span className="text-[#E2231A]">✗ {health.recent_email_failed}</span> · <span className="text-[#9AA2AD]">? {health.recent_paid_total - health.recent_email_ok - health.recent_email_failed}</span></div>
                        </div>
                    </div>
                    {health.last_email_result && !health.last_email_result.ok && (
                        <div className="bg-[#E2231A]/10 border border-[#E2231A]/30 p-3 mb-3">
                            <div className="text-[10px] uppercase tracking-widest text-[#E2231A] mb-1 flex items-center gap-1"><AlertTriangle size={12} /> Ultimo errore Resend</div>
                            <div className="font-mono text-[11px] break-all">{health.last_email_result.reason}</div>
                            {health.last_email_result.hint && <div className="text-[11px] mt-2 text-[#F4F2ED]">💡 {health.last_email_result.hint}</div>}
                        </div>
                    )}
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="flex-1 min-w-[220px]">
                            <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Test invio email</label>
                            <input data-testid="email-test-input" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="tua@email.com" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm" />
                        </div>
                        <button data-testid="email-test-btn" onClick={runTest} disabled={busy} className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"><Mail size={12} /> {busy ? "Invio…" : "Invia test"}</button>
                        <button onClick={load} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] px-3 py-2 text-xs uppercase tracking-widest"><RotateCcw size={12} className="inline" /> Refresh</button>
                        {health.recent_email_failed > 0 && (
                            <button
                                data-testid="btn-reset-failed-emails"
                                onClick={async () => {
                                    if (!window.confirm(`Sicuro di voler resettare lo stato di TUTTE le ${health.recent_email_failed} email fallite? Potrai ritentare l'invio.`)) return;
                                    try {
                                        const r = await adminResetAllFailedEmails(token);
                                        toast.success(`Reset ${r.reset_count} prenotazioni. Ora puoi ritentare.`);
                                        load();
                                    } catch { toast.error("Errore rete"); }
                                }}
                                className="border border-[#ca8a04] text-[#ca8a04] hover:bg-[#ca8a04]/10 px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                            ><RotateCcw size={12} /> Reset tutte le fallite ({health.recent_email_failed})</button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function EmailStatusCell({ booking }) {
    const b = booking;
    const receiptTs = b.email_receipt_sent_at;
    const reminderTs = b.email_reminder_last_sent_at;
    const result = b.email_receipt_result;
    if (receiptTs) {
        return (
            <div className="text-[11px]">
                <div className="text-[#16a34a] uppercase tracking-widest text-[10px]">✓ Ricevuta</div>
                <div className="text-[#9AA2AD] text-[10px]">{new Date(receiptTs).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                {reminderTs && <div className="text-[9px] text-[#9AA2AD]/70 mt-0.5">+ reminder {new Date(reminderTs).toLocaleDateString("it-IT")}</div>}
            </div>
        );
    }
    if (result && !result.ok) {
        return (
            <div className="text-[11px]">
                <div className="text-[#E2231A] uppercase tracking-widest text-[10px]">✗ Fallita</div>
                <div className="text-[9px] text-[#9AA2AD] truncate max-w-[140px]" title={result.reason}>{result.reason}</div>
            </div>
        );
    }
    return <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">— mai inviata</div>;
}

function BookingRow({ b, token, load }) {
    const colmaRef = useRef(null);
    const [missingChoice, setMissingChoice] = useState(null); // { docType } | null
    const [choiceBusy, setChoiceBusy] = useState(false);
    // V52.6 · click su doc mancante → mostra popover con 2 azioni: invita cliente | carica tu
    const openColma = (docType) => {
        setMissingChoice({ docType });
    };
    const docTypeLabels = {
        patente_fronte: "Patente FRONTE",
        patente_retro: "Patente RETRO",
        patente: "Patente",
        cf: "Codice Fiscale",
        firma_manleva: "Firma Manleva",
        manleva: "Manleva",
    };
    // Mappa etichette ChecklistItem → docType supportato da adminRequestDocReupload
    const inviteMap = {
        patente_fronte: "patente",
        patente_retro: "patente_retro",
        cf: "cf",
        firma_manleva: "firma_manleva",
        manleva: "firma_manleva",
    };
    const inviteCustomer = async () => {
        if (!missingChoice) return;
        const remote = inviteMap[missingChoice.docType] || missingChoice.docType;
        setChoiceBusy(true);
        try {
            const r = await adminRequestDocReupload(token, b.id, remote, "");
            toast.success(r.email_sent
                ? `📧 Email inviata a ${b.customer_email}`
                : `Richiesta salvata (email non inviata)`);
            setMissingChoice(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore invio invito");
        } finally { setChoiceBusy(false); }
    };
    const openAdminUpload = () => {
        setMissingChoice(null);
        colmaRef.current?.open?.();
    };
    return (
        <tr key={b.id} className="border-t border-[#9AA2AD]/10 hover:bg-[#1C2026]">
            {missingChoice && (
                <td colSpan={99} className="p-0">
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setMissingChoice(null)} data-testid={`missing-choice-modal-${b.id}`}>
                        <div className="bg-[#0F1114] border border-[#f59e0b]/50 max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Documento mancante · {b.customer_name || b.voucher_code}</div>
                            <h3 className="uppercase tracking-widest text-[#f59e0b] text-lg mt-1 mb-3">{docTypeLabels[missingChoice.docType] || missingChoice.docType}</h3>
                            <p className="text-xs text-[#9AA2AD] mb-4">Come vuoi procedere?</p>
                            <div className="grid grid-cols-1 gap-2">
                                <button onClick={inviteCustomer} disabled={choiceBusy || !b.customer_email}
                                    data-testid={`missing-invite-${b.id}`}
                                    className="w-full text-left p-3 border border-[#4B7BEA]/50 hover:bg-[#4B7BEA]/10 text-[#F4F2ED] disabled:opacity-40">
                                    <div className="text-xs uppercase tracking-widest text-[#4B7BEA]">📧 Invita cliente a caricare</div>
                                    <div className="text-[10px] text-[#9AA2AD] mt-1">Email con magic link al tunnel · il cliente carica in autonomia · il file arriva qui automaticamente</div>
                                    {!b.customer_email && <div className="text-[10px] text-[#E2231A] mt-1">⚠ Email cliente mancante</div>}
                                </button>
                                <button onClick={openAdminUpload} disabled={choiceBusy}
                                    data-testid={`missing-admin-${b.id}`}
                                    className="w-full text-left p-3 border border-[#f59e0b]/50 hover:bg-[#f59e0b]/10 text-[#F4F2ED] disabled:opacity-40">
                                    <div className="text-xs uppercase tracking-widest text-[#f59e0b]">📤 Carica tu (admin)</div>
                                    <div className="text-[10px] text-[#9AA2AD] mt-1">Apri "Colma Lacune" e carica il file per conto del cliente</div>
                                </button>
                            </div>
                            <div className="flex justify-end mt-4">
                                <button onClick={() => setMissingChoice(null)} className="text-[10px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#F4F2ED]" data-testid={`missing-cancel-${b.id}`}>Annulla</button>
                            </div>
                        </div>
                    </div>
                </td>
            )}
            <td className="p-3 font-display text-2xl text-[#E2231A]">{b.turno_progressivo ? `#${b.turno_progressivo}` : "—"}</td>
            <td className="p-3 font-display">
                {b.voucher_code}
                {b.is_test && <div className="text-[9px] uppercase tracking-widest text-[#f59e0b] border border-[#f59e0b]/50 px-1 py-0.5 mt-1 inline-block">TEST</div>}
            </td>
            <td className="p-3">
                <CustomerNameCell b={b} token={token} load={load} />
            </td>
            <td className="p-3"><WhatsAppCell tel={b.telefono} marketing={b.consenso_whatsapp_marketing} /></td>
            <td className="p-3 uppercase text-xs tracking-widest">
                {b.product_id}
                {b.product_id === "passenger_experience" && b.preferenza_auto && (
                    <div className={`text-[10px] mt-1 ${b.preferenza_auto === "EVO" ? "text-[#E2231A]" : b.preferenza_auto === "WRX" ? "text-[#4B7BEA]" : "text-[#9AA2AD]"}`}>→ {b.preferenza_auto}</div>
                )}
                {b.preferenza_fascia && b.preferenza_fascia !== "Indifferente" && <div className="text-[10px] text-[#9AA2AD] mt-0.5">{b.preferenza_fascia}</div>}
            </td>
            <td className="p-3">
                <div>€{((b.turno_paid_cents ?? b.amount) / 100).toFixed(2)}</div>
                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mt-0.5">turno</div>
            </td>
            <td className="p-3">
                {(b.media_paid_cents || 0) > 0 ? (
                    <>
                        <div className="text-[#16a34a] font-semibold" data-testid={`media-amount-${b.id}`}>€{(b.media_paid_cents / 100).toFixed(2)}</div>
                        <div className="text-[9px] uppercase tracking-widest text-[#16a34a]/70 mt-0.5">
                            {b.addon_media_bundle ? "bundle" : b.addon_video ? "video" : b.addon_foto ? "foto" : "upsell"}
                        </div>
                    </>
                ) : (
                    <div className="text-[10px] text-[#9AA2AD]/50">—</div>
                )}
            </td>
            <td className="p-3">
                <Badge status={b.status} />
                {b.status === "annullata" && (
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mt-1">
                        {b.cancel_source === "stripe" ? "Rimborsata via Stripe" : (b.cancel_reason || "Annullata manualmente")}
                        {b.refund_amount != null && <> · €{(b.refund_amount/100).toFixed(2)}</>}
                        {(b.refunded_at || b.cancelled_at) && <> · {new Date(b.refunded_at || b.cancelled_at).toLocaleDateString("it-IT")}</>}
                        {b.refund_id && <div className="text-[8px] font-mono text-[#9AA2AD]/60 truncate max-w-[130px]">{b.refund_id}</div>}
                    </div>
                )}
            </td>
            <td className="p-3">
                <BookingTimeline b={b} token={token} onQuickUpload={openColma} />
            </td>
            <td className="p-3">
                <AcceptanceColumn token={token} booking={b} onDone={load} />
            </td>
            <td className="p-3">
                <MediaCell token={token} booking={b} onDone={load} />
            </td>
            <td className="p-3">
                <EmailStatusCell booking={b} />
            </td>
            <td className="p-3">
                <div className="flex flex-col gap-1 min-w-[170px]">
                    {b.status !== "annullata" && (
                        <>
                            <EmergencyButtons token={token} booking={b} />
                            <div className="flex gap-1">
                                <a
                                    href={adminDossierPdfUrl(b.id, token)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`btn-plico-${b.id}`}
                                    title="Plico Cliente in PDF (server-side, può essere lento)"
                                    className="flex-1 text-[10px] uppercase tracking-widest border border-[#9AA2AD]/30 hover:border-[#4B7BEA] hover:text-[#4B7BEA] py-1.5 px-2 flex items-center justify-center gap-1 transition-colors duration-150"
                                ><FileArchive size={11} /> PDF</a>
                                <a
                                    href={`${adminDossierPdfUrl(b.id, token).replace('/dossier.pdf', '/dossier.html')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`btn-plico-html-${b.id}`}
                                    title="Plico stampabile (HTML → Ctrl+P → Salva come PDF). Consigliato: più veloce e affidabile."
                                    className="flex-1 text-[10px] uppercase tracking-widest border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] py-1.5 px-2 flex items-center justify-center gap-1 transition-colors duration-150"
                                >🖨 Stampa</a>
                            </div>
                            <SingleDocsPanel bookingId={b.id} token={token} booking={b} />
                            <BoardingPassPanel bookingId={b.id} token={token} booking={b} />
                            <MediaUtilityPanel bookingId={b.id} token={token} booking={b} onReload={load} />
                            <ColmaLacune ref={colmaRef} token={token} booking={b} onDone={load} />
                            <RefundButton token={token} bookingId={b.id} onDone={load} />
                            <CancelBookingButton token={token} bookingId={b.id} onDone={load} />
                        </>
                    )}
                    <SendEmailMenu token={token} bookingId={b.id} onReset={load} />
                    {b.status === "annullata" && <DeleteBookingButton token={token} booking={b} onDone={load} />}
                    {b.is_test && b.status !== "annullata" && <DeleteBookingButton token={token} booking={b} onDone={load} testLabel="Elimina TEST" />}
                </div>
            </td>
        </tr>
    );
}

function AcceptanceColumn({ token, booking, onDone }) {
    const [busy, setBusy] = useState(null);
    const b = booking;
    const prelimOk = !!b.prelim_at;
    const gateOk = !!b.gate_at;
    const isCancelled = b.status === "annullata";

    const doPrelim = async () => {
        const wristband = window.confirm(`${b.voucher_code} · ${b.customer_name}\n\nConfermi ACCETTAZIONE e consegna del braccialetto?\n\n[OK] = accettato + braccialetto consegnato\n[Annulla] = solo accettazione (nessun braccialetto)`);
        setBusy("h1");
        try {
            await adminCheckinPrelim(token, b.id, wristband);
            toast.success(`${b.voucher_code} accettato${wristband ? " · 🎗 braccialetto" : ""}`);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore check-in");
        } finally { setBusy(null); }
    };

    const doGate = async () => {
        if (!prelimOk) { toast.error("Prima esegui Check-in H1"); return; }
        if (!window.confirm(`${b.voucher_code} · ${b.customer_name}\n\nConfermi passaggio al GATE (H2)? Il cliente sale in auto.`)) return;
        setBusy("h2");
        try {
            await adminCheckinGate(token, b.id);
            toast.success(`${b.voucher_code} imbarcato al gate 🏁`);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore gate");
        } finally { setBusy(null); }
    };

    const undoGate = async () => {
        if (!window.confirm(`${b.voucher_code} · ${b.customer_name}\n\nAnnullare l'imbarco al Gate (H2)?\nIl cliente tornerà allo stato "Accettato" (H1 rimane valido).`)) return;
        setBusy("undo_h2");
        try {
            await adminCheckinReset(token, b.id, "gate");
            toast.success(`Imbarco annullato — ${b.voucher_code} torna a H1`);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore annullamento");
        } finally { setBusy(null); }
    };

    const undoPrelim = async () => {
        if (!window.confirm(`${b.voucher_code} · ${b.customer_name}\n\nAnnullare COMPLETAMENTE l'accettazione (H1)?\n${gateOk ? "⚠ Anche l'imbarco al Gate (H2) verrà annullato.\n\n" : ""}Il cliente tornerà allo stato "Da accettare".`)) return;
        setBusy("undo_h1");
        try {
            await adminCheckinReset(token, b.id, "prelim");
            toast.success(`Accettazione annullata — ${b.voucher_code} torna in attesa`);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore annullamento");
        } finally { setBusy(null); }
    };

    if (isCancelled) {
        return <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]/60">—</div>;
    }

    return (
        <div className="flex flex-col gap-1 min-w-[140px]" data-testid={`acceptance-${b.id}`}>
            <button
                type="button"
                onClick={prelimOk ? undoPrelim : doPrelim}
                disabled={busy === "h1" || busy === "undo_h1"}
                data-testid={`btn-checkin-h1-${b.id}`}
                title={prelimOk ? `Accettato ${fmtShort(b.prelim_at)}${b.wristband_given ? " · braccialetto ok" : ""} — click per ANNULLARE` : "Check-in Hostess 1 (accettazione + braccialetto)"}
                className={`text-[10px] uppercase tracking-widest py-1.5 px-2 flex items-center justify-center gap-1 transition-colors duration-150 ${prelimOk ? "bg-[#16a34a]/20 text-[#16a34a] border border-[#16a34a]/40 hover:bg-[#E2231A]/15 hover:text-[#E2231A] hover:border-[#E2231A]/50 cursor-pointer" : "bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] disabled:opacity-40"}`}
            >
                {busy === "undo_h1" ? "Annullo…" : prelimOk ? `✓ H1 ${fmtShort(b.prelim_at)}` : (busy === "h1" ? "…" : "Check-in H1")}
                {prelimOk && b.wristband_given && <span title="Braccialetto consegnato">🎗</span>}
            </button>
            <button
                type="button"
                onClick={gateOk ? undoGate : doGate}
                disabled={busy === "h2" || busy === "undo_h2" || (!prelimOk && !gateOk)}
                data-testid={`btn-checkin-h2-${b.id}`}
                title={gateOk ? `Imbarcato ${fmtShort(b.gate_at)} — click per ANNULLARE l'imbarco` : (prelimOk ? "Imbarco al Gate (Hostess 2) — cliente sale in auto" : "Prima esegui H1")}
                className={`text-[10px] uppercase tracking-widest py-1.5 px-2 flex items-center justify-center gap-1 transition-colors duration-150 ${gateOk ? "bg-[#16a34a]/20 text-[#16a34a] border border-[#16a34a]/40 hover:bg-[#E2231A]/15 hover:text-[#E2231A] hover:border-[#E2231A]/50 cursor-pointer" : (prelimOk ? "bg-[#4B7BEA] hover:bg-[#3B65C7] text-[#F4F2ED]" : "border border-[#9AA2AD]/30 text-[#9AA2AD]/50 cursor-not-allowed")} disabled:opacity-40`}
            >
                {busy === "undo_h2" ? "Annullo…" : gateOk ? `🏁 H2 ${fmtShort(b.gate_at)}` : (busy === "h2" ? "…" : "Gate H2")}
            </button>
            {(prelimOk || gateOk) && (
                <div className="text-[8px] uppercase tracking-widest text-[#9AA2AD]/60 text-center italic pt-0.5">click badge = annulla</div>
            )}
        </div>
    );
}

function EmergencyButtons({ token, booking }) {
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const b = booking;

    const buildLink = () => {
        const base = window.location.origin;
        return `${base}/manleva/${b.id}?t=${encodeURIComponent(b.access_token || "")}`;
    };

    const sendLink = async () => {
        if (!b.customer_email) { toast.error("Nessuna email cliente"); return; }
        setBusy(true);
        try {
            const r = await adminResendCompletion(token, b.id);
            if (r.ok) toast.success(`Link completamento inviato a ${b.customer_email}`);
            else toast.error(r.hint || r.reason || "Invio fallito");
        } catch { toast.error("Errore rete"); }
        finally { setBusy(false); }
    };

    const copyLink = async () => {
        const link = buildLink();
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success("Link copiato — pronto per WhatsApp");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            window.prompt("Copia il link:", link);
        }
    };

    return (
        <div className="flex gap-1">
            <button data-testid={`btn-send-link-${b.id}`} onClick={sendLink} disabled={busy} title="Invia via email il link per completare documenti/manleva" className="flex-1 text-[10px] uppercase tracking-widest bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 text-[#F4F2ED] py-1.5 px-2 flex items-center justify-center gap-1 transition-colors duration-150">
                <Mail size={11} /> {busy ? "…" : "Invia link"}
            </button>
            <button data-testid={`btn-copy-link-${b.id}`} onClick={copyLink} title="Copia link completamento per mandarlo via WhatsApp" className="text-[10px] uppercase tracking-widest border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] py-1.5 px-2 flex items-center gap-1 transition-colors duration-150">
                {copied ? <Check size={11} /> : <LinkIcon size={11} />} {copied ? "Ok" : "Copia"}
            </button>
        </div>
    );
}

const ColmaLacune = forwardRef(function ColmaLacune({ token, booking, onDone }, ref) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const [note, setNote] = useState("");
    const [method, setMethod] = useState("verbal");
    const b = booking;
    useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);
    // Editor testo per dati fiscali mancanti
    const [cf, setCf] = useState((b.fiscal_data?.codice_fiscale) || "");
    const [tel, setTel] = useState((b.fiscal_data?.telefono) || "");
    const [via, setVia] = useState((b.fiscal_data?.residenza_via) || "");
    const [citta, setCitta] = useState((b.fiscal_data?.residenza_citta) || "");
    const [nomeC, setNomeC] = useState(b.customer_name || "");
    const [emailC, setEmailC] = useState(b.customer_email || "");
    useEffect(() => {
        if (open) {
            setCf(b.fiscal_data?.codice_fiscale || "");
            setTel(b.fiscal_data?.telefono || "");
            setVia(b.fiscal_data?.residenza_via || "");
            setCitta(b.fiscal_data?.residenza_citta || "");
            setNomeC(b.customer_name || "");
            setEmailC(b.customer_email || "");
        }
    }, [open]);

    const uploadPat = async (e, side = "fronte") => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy(`pat_${side}`);
        try { await adminUploadPatente(token, b.id, f, side); toast.success(`Patente ${side} caricata`); onDone && onDone(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        finally { setBusy(null); }
    };
    const uploadCf = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy("cf");
        try { await adminUploadCfPhoto(token, b.id, f); toast.success("Foto CF caricata"); onDone && onDone(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        finally { setBusy(null); }
    };
    const signIt = async () => {
        if (!window.confirm(`Conferma manleva per ${b.customer_name}?\n\nMetodo: ${method}\nNota: ${note || "(nessuna)"}\n\nQuesta azione è tracciata con audit trail.`)) return;
        setBusy("sign");
        try { await adminSignManleva(token, b.id, method, note); toast.success("Manleva confermata"); onDone && onDone(); setOpen(false); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        finally { setBusy(null); }
    };
    const saveFiscal = async () => {
        const payload = {};
        const cfNorm = (cf || "").toUpperCase().replace(/\s/g, "");
        if (cfNorm.length && cfNorm.length !== 16) {
            if (!window.confirm(`Il CF "${cfNorm}" non ha 16 caratteri. Salvarlo comunque?`)) return;
        }
        payload.codice_fiscale = cfNorm;
        payload.telefono = tel;
        payload.residenza_via = via;
        payload.residenza_citta = citta;
        // Nome / Email (opzionali — vuoti = non toccare)
        if ((nomeC || "").trim() && nomeC.trim() !== (b.customer_name || "")) {
            payload.customer_name = nomeC.trim();
        }
        if ((emailC || "").trim() && emailC.trim().toLowerCase() !== (b.customer_email || "").toLowerCase()) {
            payload.customer_email = emailC.trim().toLowerCase();
        }
        setBusy("fiscal");
        try { await adminUpdateFiscal(token, b.id, payload); toast.success("Dati aggiornati"); onDone && onDone(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        finally { setBusy(null); }
    };

    return (
        <div className="relative">
            <button data-testid={`btn-colma-${b.id}`} onClick={() => setOpen(v => !v)} title="Compila / correggi patente, CF, nome, email, telefono, indirizzo" className="w-full text-[10px] uppercase tracking-widest border border-[#ca8a04]/40 hover:border-[#ca8a04] hover:text-[#ca8a04] py-1.5 px-2 flex items-center justify-center gap-1 transition-colors duration-150">
                🛠 Modifica
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-[#1C2026] border border-[#ca8a04]/60 w-80 p-3 shadow-lg max-h-[80vh] overflow-y-auto">
                    <div className="text-[10px] uppercase tracking-widest text-[#ca8a04] mb-2 font-display">Modifica dati cliente</div>

                    <label className="block mb-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1 flex items-center gap-2">
                            📎 Patente FRONTE
                            {b.has_license ? (
                                <a
                                    href={adminBookingDocUrl(b.id, "patente", token)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`btn-view-patente-fronte-${b.id}`}
                                    className="text-[#16a34a] hover:text-[#22c55e] underline text-[10px]"
                                    title="Apri patente fronte in una nuova scheda"
                                >✓ presente (apri)</a>
                            ) : (
                                <span className="text-[#E2231A]">✗ mancante</span>
                            )}
                        </div>
                        <div className="cursor-pointer inline-flex items-center gap-2 border border-[#9AA2AD]/40 hover:border-[#ca8a04] px-2 py-1.5 text-[10px] uppercase tracking-widest w-full">
                            {busy === "pat_fronte" ? "…" : (b.has_license ? "Sostituisci FRONTE" : "Carica FRONTE (JPG/PDF)")}
                            <input type="file" accept="image/*,application/pdf" onChange={(e) => uploadPat(e, "fronte")} className="hidden" data-testid={`input-patente-fronte-${b.id}`} />
                        </div>
                    </label>

                    <label className="block mb-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1 flex items-center gap-2">
                            📎 Patente RETRO
                            {b.has_license_retro ? (
                                <a
                                    href={adminBookingDocUrl(b.id, "patente_retro", token)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`btn-view-patente-retro-${b.id}`}
                                    className="text-[#16a34a] hover:text-[#22c55e] underline text-[10px]"
                                    title="Apri patente retro in una nuova scheda"
                                >✓ presente (apri)</a>
                            ) : (
                                <span className="text-[#E2231A]">✗ mancante</span>
                            )}
                        </div>
                        <div className="cursor-pointer inline-flex items-center gap-2 border border-[#9AA2AD]/40 hover:border-[#ca8a04] px-2 py-1.5 text-[10px] uppercase tracking-widest w-full">
                            {busy === "pat_retro" ? "…" : (b.has_license_retro ? "Sostituisci RETRO" : "Carica RETRO (JPG/PDF)")}
                            <input type="file" accept="image/*,application/pdf" onChange={(e) => uploadPat(e, "retro")} className="hidden" data-testid={`input-patente-retro-${b.id}`} />
                        </div>
                    </label>

                    <label className="block mb-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1 flex items-center gap-2">
                            📎 Foto CF
                            {b.has_cf_photo ? (
                                <a
                                    href={adminBookingDocUrl(b.id, "cf", token)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`btn-view-cf-${b.id}`}
                                    className="text-[#16a34a] hover:text-[#22c55e] underline text-[10px]"
                                    title="Apri codice fiscale in una nuova scheda"
                                >✓ presente (apri)</a>
                            ) : (
                                <span className="text-[#E2231A]">✗ mancante</span>
                            )}
                        </div>
                        <div className="cursor-pointer inline-flex items-center gap-2 border border-[#9AA2AD]/40 hover:border-[#ca8a04] px-2 py-1.5 text-[10px] uppercase tracking-widest w-full">
                            {busy === "cf" ? "…" : (b.has_cf_photo ? "Sostituisci foto CF" : "Carica foto codice fiscale")}
                            <input type="file" accept="image/*,application/pdf" onChange={uploadCf} className="hidden" />
                        </div>
                    </label>

                    <div className="border-t border-[#9AA2AD]/20 pt-2 mt-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">📝 Dati fiscali (testo)</div>
                        <label className="block mb-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5 flex items-center justify-between gap-2">
                                <span>Nome e Cognome cliente</span>
                                {b.has_license && (
                                    <button
                                        type="button"
                                        data-testid={`btn-ocr-in-editor-${b.id}`}
                                        disabled={busy === "ocr"}
                                        onClick={async () => {
                                            setBusy("ocr");
                                            try {
                                                const r = await adminOcrPatente(token, b.id, true);
                                                if (r.full_name) {
                                                    setNomeC(r.full_name);
                                                    toast.success(`Nome letto dalla patente: ${r.full_name}`);
                                                } else {
                                                    toast.warning("OCR non ha trovato il nome — inseriscilo a mano qui sotto");
                                                }
                                            } catch (err) {
                                                const d = err?.response?.data?.detail || err.message;
                                                let hint = "";
                                                if (typeof d === "string" && d.toLowerCase().includes("pdf")) hint = " · Ricarica la patente come JPG/PNG";
                                                else if (typeof d === "string" && d.toLowerCase().includes("non caricata")) hint = " · Prima carica la patente sopra";
                                                toast.error("OCR fallito: " + d + hint);
                                            } finally { setBusy(null); }
                                        }}
                                        className="text-[9px] uppercase tracking-widest border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-1.5 py-0.5 disabled:opacity-50 normal-case"
                                    >{busy === "ocr" ? "Lettura…" : "🔤 Leggi da patente"}</button>
                                )}
                            </div>
                            <input data-testid={`input-name-${b.id}`} value={nomeC} onChange={e => setNomeC(e.target.value)} placeholder="Mario Rossi" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#ca8a04] outline-none text-xs px-2 py-1.5" />
                        </label>
                        <label className="block mb-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5">Email cliente</div>
                            <input data-testid={`input-email-${b.id}`} value={emailC} onChange={e => setEmailC(e.target.value)} placeholder="mario@example.com" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#ca8a04] outline-none text-xs px-2 py-1.5" />
                        </label>
                        <label className="block mb-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5">Codice Fiscale {b.has_cf_text && <span className="text-[#16a34a]">· salvato</span>}</div>
                            <input
                                data-testid={`input-cf-${b.id}`}
                                value={cf}
                                onChange={e => setCf(e.target.value.toUpperCase())}
                                placeholder="RSSMRA85M01H501Z"
                                maxLength={16}
                                className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#ca8a04] outline-none text-xs px-2 py-1.5 font-mono tracking-wider uppercase"
                            />
                        </label>
                        <label className="block mb-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5">Telefono</div>
                            <input data-testid={`input-tel-${b.id}`} value={tel} onChange={e => setTel(e.target.value)} placeholder="+39 333 1234567" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#ca8a04] outline-none text-xs px-2 py-1.5" />
                        </label>
                        <div className="grid grid-cols-2 gap-1 mb-2">
                            <label className="block">
                                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5">Via</div>
                                <input value={via} onChange={e => setVia(e.target.value)} placeholder="Via Roma 1" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#ca8a04] outline-none text-xs px-2 py-1.5" />
                            </label>
                            <label className="block">
                                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5">Città</div>
                                <input value={citta} onChange={e => setCitta(e.target.value)} placeholder="Milano" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#ca8a04] outline-none text-xs px-2 py-1.5" />
                            </label>
                        </div>
                        <button data-testid={`btn-save-fiscal-${b.id}`} onClick={saveFiscal} disabled={busy === "fiscal"} className="w-full bg-[#ca8a04] hover:bg-[#a86e04] disabled:opacity-40 text-[#15181C] py-1.5 text-[10px] uppercase tracking-widest font-bold">
                            {busy === "fiscal" ? "..." : "Salva dati"}
                        </button>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-2 mt-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">✍️ Conferma manleva (override)</div>
                        <select value={method} onChange={e => setMethod(e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 text-xs px-2 py-1 mb-2">
                            <option value="verbal">Confermato via WhatsApp/telefono</option>
                            <option value="in_person">Firmato di persona (cartaceo)</option>
                            <option value="email">Confermato via email</option>
                        </select>
                        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Nota (opzionale)" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 text-xs px-2 py-1 mb-2" />
                        <button data-testid={`btn-admin-sign-${b.id}`} onClick={signIt} disabled={busy === "sign"} className="w-full bg-[#ca8a04] hover:bg-[#a86e04] disabled:opacity-40 text-[#15181C] py-1.5 text-[10px] uppercase tracking-widest font-bold">
                            {busy === "sign" ? "..." : "Conferma manleva admin"}
                        </button>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-2 mt-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">📄 Stampa rapida moduli</div>
                        <a
                            data-testid={`btn-print-missing-${b.id}`}
                            href={adminPrintMissingUrl(b.id, token)}
                            target="_blank"
                            rel="noreferrer"
                            title="Genera PDF con SOLO i moduli non firmati (manleva, GDPR, marketing, ecc.), pronto per firma manuale in pista"
                            className="w-full block text-center bg-[#4B7BEA] hover:bg-[#3D6BC7] text-[#F4F2ED] py-1.5 text-[10px] uppercase tracking-widest font-bold"
                        >
                            🖨 Stampa moduli da firmare
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
});

function WhatsAppCell({ tel, marketing }) {
    if (!tel) return <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD]/50">—</span>;
    // Sanitize per wa.me: solo cifre, senza + né spazi
    const digits = String(tel).replace(/[^\d]/g, "");
    const href = `https://wa.me/${digits}`;
    return (
        <div className="flex flex-col items-start gap-1">
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wa-cell-link"
                title="Apri chat WhatsApp con il cliente"
                className="inline-flex items-center gap-1 text-[#25D366] hover:text-[#1DA851] text-xs font-mono transition-colors duration-150"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.46 0 .1 5.36.1 11.95c0 2.1.55 4.16 1.6 5.97L0 24l6.24-1.64a11.9 11.9 0 0 0 5.8 1.48h.01c6.59 0 11.95-5.36 11.95-11.95a11.86 11.86 0 0 0-3.48-8.41Z"/></svg>
                {tel}
            </a>
            {marketing ? (
                <span className="text-[9px] uppercase tracking-widest text-[#25D366]" title="Ha dato consenso marketing WhatsApp">✓ marketing OK</span>
            ) : (
                <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]/60" title="Nessun consenso marketing WhatsApp">no marketing</span>
            )}
        </div>
    );
}


function EventView({ token }) {
    const [ev, setEv] = useState(null);
    const [caps, setCaps] = useState({});
    const [saving, setSaving] = useState(false);
    const [editingMeta, setEditingMeta] = useState(false);
    const [meta, setMeta] = useState({});

    const load = () => adminGetEvent(token).then(d => { setEv(d); setCaps(d.capacity_left || {}); setMeta({
        title: d.title || "", circuit: d.circuit || "", city: d.city || "", address: d.address || "",
        context: d.context || "", role: d.role || "", hours: d.hours || "", google_maps_url: d.google_maps_url || "",
    }); }).catch(() => toast.error("Errore caricamento"));
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = Object.fromEntries(Object.entries(caps).map(([k, v]) => [k, parseInt(v || 0, 10)]));
            await adminSetCapacity(token, payload);
            toast.success("Capienze aggiornate");
            load();
        } catch { toast.error("Errore salvataggio"); }
        finally { setSaving(false); }
    };

    const saveMeta = async () => {
        setSaving(true);
        try {
            await adminSetEventSettings(token, meta);
            toast.success("Dati evento salvati");
            setEditingMeta(false);
            load();
        } catch { toast.error("Errore salvataggio"); }
        finally { setSaving(false); }
    };

    if (!ev) return <div className="text-[#9AA2AD] py-8 text-center">Caricamento…</div>;

    const dt = new Date(ev.date);
    const dateStr = dt.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

    const PRODUCT_LABELS = {
        passenger_experience: "Passeggero (€29)",
        singola_evo: "Singola EVO X (€79)",
        singola_wrx: "Singola WRX (€79)",
        combo_duello: "Combo Duello (€129)",
    };

    return (
        <div className="space-y-6">
            <div className="border border-[#E2231A]/40 bg-[#1C2026] p-6" data-testid="event-card">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#9AA2AD]">L'evento</div>
                        <h2 className="font-display text-3xl uppercase mt-1">{ev.title || ev.context || "VELORA JDM Experience"}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="btn-edit-event-meta"
                            onClick={() => setEditingMeta(v => !v)}
                            className="border border-[#9AA2AD]/30 hover:border-[#4B7BEA] hover:text-[#4B7BEA] px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors duration-150"
                        >{editingMeta ? "Chiudi" : "✎ Modifica"}</button>
                        <span className={`px-3 py-1 border text-[10px] uppercase tracking-widest ${ev.prevendita_active ? "border-[#16a34a] text-[#16a34a]" : "border-[#9AA2AD] text-[#9AA2AD]"}`}>
                            {ev.prevendita_active ? "Prevendita attiva" : "Prevendita chiusa"}
                        </span>
                    </div>
                </div>

                {editingMeta && (
                    <div className="border border-[#4B7BEA]/40 bg-[#4B7BEA]/5 p-4 mb-4 grid md:grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Nome evento (titolo)</div>
                            <input data-testid="ev-input-title" value={meta.title} onChange={e => setMeta(m => ({ ...m, title: e.target.value }))} placeholder="VELORA JDM Experience 2026" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Contesto/Sotto-titolo</div>
                            <input value={meta.context} onChange={e => setMeta(m => ({ ...m, context: e.target.value }))} placeholder="JDMC & Friends 2026 — 7ª Edizione" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Circuito</div>
                            <input data-testid="ev-input-circuit" value={meta.circuit} onChange={e => setMeta(m => ({ ...m, circuit: e.target.value }))} placeholder="Circuito del Sele" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Città</div>
                            <input value={meta.city} onChange={e => setMeta(m => ({ ...m, city: e.target.value }))} placeholder="Battipaglia (SA)" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block md:col-span-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Indirizzo completo</div>
                            <input value={meta.address} onChange={e => setMeta(m => ({ ...m, address: e.target.value }))} placeholder="Via Fosso Pioppo 15, 84091 Battipaglia (SA)" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Orari pista</div>
                            <input value={meta.hours} onChange={e => setMeta(m => ({ ...m, hours: e.target.value }))} placeholder="09:00–13:00 · 14:00–18:00" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Ruolo / Note</div>
                            <input value={meta.role} onChange={e => setMeta(m => ({ ...m, role: e.target.value }))} placeholder="Partner presente con le proprie auto" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <label className="block md:col-span-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Link Google Maps</div>
                            <input value={meta.google_maps_url} onChange={e => setMeta(m => ({ ...m, google_maps_url: e.target.value }))} placeholder="https://maps.google.com/?q=..." className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                        </label>
                        <button data-testid="ev-save-meta" onClick={saveMeta} disabled={saving} className="md:col-span-2 bg-[#4B7BEA] hover:bg-[#3866d1] text-[#F4F2ED] py-2 text-xs uppercase tracking-widest font-display disabled:opacity-40">
                            {saving ? "Salvataggio…" : "💾 Salva dati evento"}
                        </button>
                    </div>
                )}
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Data</div>
                        <div className="text-lg">{dateStr}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Orari pista</div>
                        <div className="text-lg">{ev.hours || "—"}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Circuito</div>
                        <div className="text-lg">{ev.circuit}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Indirizzo</div>
                        <div className="text-lg">{ev.address || ev.city || "—"}</div>
                    </div>
                    <div className="md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Ruolo</div>
                        <div className="text-lg">{ev.role || "—"}</div>
                    </div>
                </div>
            </div>

            <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-2xl uppercase">Capienze rimanenti</h3>
                    <button data-testid="save-capacity" onClick={save} disabled={saving} className="bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">
                        <Save size={14} /> {saving ? "…" : "Salva capienze"}
                    </button>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-4">Puoi modificare i posti totali disponibili per ogni formula. Il valore rappresenta i posti RIMANENTI (scala automaticamente al pagamento).</p>
                <div className="grid md:grid-cols-4 gap-3">
                    {Object.keys(PRODUCT_LABELS).map(pid => (
                        <div key={pid} className="border border-[#9AA2AD]/20 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">{PRODUCT_LABELS[pid]}</div>
                            <input
                                data-testid={`capacity-${pid}`}
                                type="number"
                                min="0"
                                value={caps[pid] ?? 0}
                                onChange={e => setCaps(prev => ({ ...prev, [pid]: e.target.value }))}
                                className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 font-display text-3xl"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}


function DeleteBookingButton({ token, booking, onDone, testLabel }) {
    const [busy, setBusy] = useState(false);
    const isCancelled = booking.status === "annullata";
    const isTest = !!booking.is_test;
    const del = async () => {
        const warn = isTest
            ? `Eliminare definitivamente la prenotazione TEST ${booking.voucher_code}?`
            : isCancelled
                ? `Cancellare definitivamente la prenotazione ${booking.voucher_code} (già annullata)? Non può essere annullato.`
                : `⚠️ ATTENZIONE: ${booking.voucher_code} è ancora "${booking.status}".\n\nSe è una transazione reale devi prima RIMBORSARE su Stripe (bottone Rimborsa).\n\nProcedere comunque con la cancellazione hard-delete? Lo slot verrà liberato.`;
        if (!window.confirm(warn)) return;
        setBusy(true);
        try {
            const r = await adminDeleteBooking(token, booking.id, !isTest);
            toast.success(`Prenotazione eliminata${r.capacity_restored ? " · slot liberato" : ""}`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore eliminazione");
        } finally { setBusy(false); }
    };
    return (
        <button data-testid={`btn-delete-booking-${booking.id}`} onClick={del} disabled={busy} className={`text-[10px] uppercase tracking-widest ${isTest ? "text-[#f59e0b] hover:text-[#d97706]" : "text-[#9AA2AD] hover:text-[#E2231A]"} disabled:opacity-40 transition-colors duration-150 flex items-center gap-1`}>
            <Trash2 size={11} /> {busy ? "…" : (testLabel || "Elimina")}
        </button>
    );
}

function CleanupCancelledButton({ token, count, onDone }) {
    const [busy, setBusy] = useState(false);
    if (count === 0) return null;
    const run = async () => {
        if (!window.confirm(`Eliminare definitivamente ${count} prenotazioni ANNULLATE dal database?\n\nNon impatta Stripe (già rimborsate). Solo pulizia lista.`)) return;
        setBusy(true);
        try {
            const r = await adminCleanupCancelled(token, false);
            toast.success(`${r.deleted} prenotazioni annullate eliminate`);
            onDone();
        } catch { toast.error("Errore pulizia"); }
        finally { setBusy(false); }
    };
    return (
        <button data-testid="btn-cleanup-cancelled" onClick={run} disabled={busy} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">
            <Trash2 size={12} /> {busy ? "…" : `Pulisci annullate (${count})`}
        </button>
    );
}


function CancelBookingButton({ token, bookingId, onDone }) {
    const [busy, setBusy] = useState(false);
    const cancel = async () => {
        const reason = window.prompt(
            "Motivo dell'annullamento manuale?\n(es. 'Rimborsato via bonifico', 'Chargeback Stripe', 'Ospite non presentato')\n\nNessun addebito Stripe verrà effettuato — solo lo stato del booking cambia in ANNULLATA.\nLo slot viene liberato e la fattura disabilitata.",
            "Rimborso già effettuato manualmente"
        );
        if (!reason) return;
        const externalRefundId = window.prompt("(Opzionale) ID rimborso Stripe/bonifico per tracciabilità. Lascia vuoto se non applicabile.", "");
        setBusy(true);
        try {
            const r = await adminCancelBooking(token, bookingId, reason, externalRefundId);
            if (r.already_cancelled) toast.info("Già annullata");
            else toast.success("Annullata · slot liberato · fattura disabilitata");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore annullamento");
        } finally { setBusy(false); }
    };
    return (
        <button data-testid={`btn-cancel-manual-${bookingId}`} onClick={cancel} disabled={busy} className="text-[10px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#F4F2ED] disabled:opacity-40 transition-colors duration-150">
            {busy ? "…" : "Annulla manualmente"}
        </button>
    );
}


// ═══════════════════════════════════════════════════════════════════════════
// PlanningView V2 — Timeline UNICA (pista singola con alternanza EVO/WRX)
// • Combo espanso in 2 slot NON consecutivi (distanza ≥ 2 turni)
// • Passeggero = mezzo slot (6 min interno, non mostrato al cliente)
// • Contatore X/36 slot con barra progresso
// • Rigenerazione automatica intelligente
// ═══════════════════════════════════════════════════════════════════════════
function PlanningView({ token, onGlobalReload }) {
    const { eventId, eventLabel, events, select } = useEventScope();
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [busy, setBusy] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [editingSlot, setEditingSlot] = useState(null);

    const load = async () => {
        try {
            setLoadError("");
            const d = await adminPlanningV2Get(token);
            if (!d?.config || !Array.isArray(d.slots)) throw new Error("Risposta Planning non valida");
            setData(d);
            setCfg({
                turn_duration_min: d.config.turn_duration_min,
                pause_between_min: d.config.pause_between_min,
                passenger_duration_min: d.config.passenger_duration_min || 6,
                start_time: d.config.start_time,
                end_time: d.config.end_time || "18:00",
                break_start: d.config.break_start || "",
                break_end: d.config.break_end || "",
            });
        } catch (e) {
            setLoadError("Impossibile caricare il Planning. Riprova tra qualche istante.");
        }
    };
    useEffect(() => { load(); }, [token]);

    // Toggle vista Elenco / Planning
    const [viewMode, setViewMode] = useState(() => localStorage.getItem("velora_planning_view") || "elenco");
    useEffect(() => { localStorage.setItem("velora_planning_view", viewMode); }, [viewMode]);

    const saveConfig = async () => {
        setBusy(true);
        try {
            const oldCfg = data?.config || {};
            const timeFieldsChanged =
                Number(oldCfg.turn_duration_min) !== Number(cfg.turn_duration_min) ||
                Number(oldCfg.pause_between_min) !== Number(cfg.pause_between_min) ||
                Number(oldCfg.passenger_duration_min) !== Number(cfg.passenger_duration_min) ||
                (oldCfg.start_time || "") !== (cfg.start_time || "") ||
                (oldCfg.end_time || "") !== (cfg.end_time || "") ||
                (oldCfg.break_start || "") !== (cfg.break_start || "") ||
                (oldCfg.break_end || "") !== (cfg.break_end || "");
            const r = await adminPlanningV2Config(token, cfg);
            setData({ ...data, config: r.config });
            if (timeFieldsChanged && (data?.slots?.length || 0) > 0) {
                // Auto-rigenerazione: i tempi cambiati richiedono un nuovo calcolo degli slot
                if (window.confirm("Config salvata.\n\nHai cambiato durate/orari. Rigenero subito la timeline per applicarli a TUTTI i turni?\n\n(Gli slot lockati 🔒 restano intatti.)")) {
                    const rr = await adminPlanningV2Regenerate(token);
                    toast.success(`Config applicata · rigenerati ${rr.generated} slot · lockati ${rr.kept_locked}`);
                    await load();
                    if (onGlobalReload) await onGlobalReload();  // aggiorna KPI header (slot liberi/prenotati)
                } else {
                    toast.info("Config salvata · timeline NON rigenerata (clicca ⚡ Rigenera quando vuoi)");
                }
            } else {
                toast.success("Config salvata");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore config");
        } finally { setBusy(false); }
    };

    const saveConfigAndSync = async () => {
        // V54.6 · unico bottone: salva config + rigenera slot + refresh KPI
        // Non chiede conferma (l'utente ha già scelto esplicitamente questo bottone).
        setBusy(true);
        try {
            const r = await adminPlanningV2Config(token, cfg);
            setData({ ...data, config: r.config });
            const rr = await adminPlanningV2Regenerate(token);
            toast.success(`Config salvata e ${rr.generated} slot sincronizzati (${rr.kept_locked} lockati mantenuti)`);
            await load();
            if (onGlobalReload) await onGlobalReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore sincronizzazione");
        } finally { setBusy(false); }
    };

    const regenerate = async () => {
        if (!window.confirm("Rigenerare planning? Gli slot lockati 🔒 restano intatti, gli altri vengono ricalcolati.")) return;
        setBusy(true);
        try {
            const r = await adminPlanningV2Regenerate(token);
            toast.success(`Rigenerati ${r.generated} slot · lockati mantenuti ${r.kept_locked}`);
            await load();
            if (onGlobalReload) await onGlobalReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore rigenerazione");
        } finally { setBusy(false); }
    };

    if (loadError) return <div role="alert" data-testid="planning-load-error" className="p-5 border border-red-500/40 text-red-400 text-sm">{loadError}<button data-testid="planning-load-retry" onClick={load} className="ml-3 border border-current px-3 py-2 hover:bg-red-500/10 transition-colors">Riprova</button></div>;
    if (!data || !cfg) return <div data-testid="planning-loading" className="text-center py-12 text-[#9AA2AD]">Caricamento planning…</div>;

    if (eventId === "tutti") return <section data-testid="planning-all-events" className="space-y-4">
        <h2 data-testid="planning-all-title" className="text-base md:text-lg">Planning · Tutti gli eventi</h2>
        <p data-testid="planning-all-count" className="text-sm text-[#9AA2AD]">{data.slots.length} turni complessivi</p>
        <div className="grid md:grid-cols-2 gap-4">{events.map(event => <button key={event.id} data-testid={`planning-open-${event.id}`} onClick={() => select(event.id)} className="border border-[#9AA2AD]/25 p-5 text-left hover:border-[#E2231A] transition-colors"><strong className="block text-sm">{event.nome}</strong><span className="text-xs text-[#9AA2AD]">{data.slots.filter(s => s.event_id === event.id).length} turni · {event.stato}</span></button>)}</div>
    </section>;
    const slots = (data.slots || []).filter(s => s && !s.manual_photo_client && ["EVO", "WRX", "PASS"].includes(s.car))
        .sort((a, b) => String(a.start_time || "99:99").localeCompare(String(b.start_time || "99:99")));
    const validTime = value => typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
    const timedSlots = slots.filter(s => validTime(s.start_time) && validTime(s.end_time));
    const stats = data.stats || {};
    const unplanned = data.unplanned || [];

    // V54.7 · Calcolo live client-side: mentre l'utente digita gli input di config,
    // le card "Tempo lordo / Pausa / Netto / Slot Max" si aggiornano istantaneamente
    // senza dover cliccare Salva & Sincronizza. Semplice matematica.
    const _hhmmToMin = (t) => {
        if (!t || typeof t !== "string" || !t.includes(":")) return 0;
        const [h, m] = t.split(":").map(x => parseInt(x, 10) || 0);
        return h * 60 + m;
    };
    const liveGross = Math.max(0, _hhmmToMin(cfg.end_time) - _hhmmToMin(cfg.start_time));
    const liveBreak = (cfg.break_start && cfg.break_end)
        ? Math.max(0, _hhmmToMin(cfg.break_end) - _hhmmToMin(cfg.break_start))
        : 0;
    const liveNet = Math.max(0, liveGross - liveBreak);
    const slotDur = Number(cfg.turn_duration_min || 15) + Number(cfg.pause_between_min || 0);
    const liveIdealSlots = slotDur > 0 ? Math.floor(liveNet / slotDur) : 0;
    // Se la config è stata modificata rispetto ai valori del DB, mostra un badge
    // "Non salvata" sulle card
    const cfgDirty = data.config && (
        Number(data.config.turn_duration_min) !== Number(cfg.turn_duration_min) ||
        Number(data.config.pause_between_min) !== Number(cfg.pause_between_min) ||
        (data.config.start_time || "") !== (cfg.start_time || "") ||
        (data.config.end_time || "") !== (cfg.end_time || "") ||
        (data.config.break_start || "") !== (cfg.break_start || "") ||
        (data.config.break_end || "") !== (cfg.break_end || "")
    );

    const ideal = liveIdealSlots;
    const occupied = stats.occupied_slots || 0;
    const pct = stats.occupancy_pct || 0;

    return (
        <div className="space-y-4" data-testid="planning-view-v2">
            <p data-testid="planning-event-caption" className="text-xs text-[#9AA2AD]">Planning · {eventLabel}</p>
            {/* ═══════ CONFIG BAR ═══════ */}
            <section className="bg-[#1C2026] border-l-4 border-[#E2231A] p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <ConfigNum label="Turno pilota (min)" value={cfg.turn_duration_min} min={1} max={120} onChange={v => setCfg({...cfg, turn_duration_min: v})} testid="cfg-turn-duration" />
                    <ConfigNum label="Pausa tra clienti (min)" value={cfg.pause_between_min} min={0} max={60} onChange={v => setCfg({...cfg, pause_between_min: v})} testid="cfg-pause" />
                    <ConfigNum label="Passeggero (min interno)" value={cfg.passenger_duration_min} min={1} max={30} onChange={v => setCfg({...cfg, passenger_duration_min: v})} testid="cfg-pass" />
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Inizio giornata</label>
                        <input type="time" value={cfg.start_time} onChange={e => setCfg({...cfg, start_time: e.target.value})} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-2 py-1.5 text-sm font-display" data-testid="cfg-start-time" />
                    </div>
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Fine giornata</label>
                        <input type="time" value={cfg.end_time} onChange={e => setCfg({...cfg, end_time: e.target.value})} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-2 py-1.5 text-sm font-display" data-testid="cfg-end-time" />
                    </div>
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Pausa pranzo (da)</label>
                        <input type="time" value={cfg.break_start} onChange={e => setCfg({...cfg, break_start: e.target.value})} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-2 py-1.5 text-sm font-display" data-testid="cfg-break-start" />
                    </div>
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Pausa pranzo (a)</label>
                        <input type="time" value={cfg.break_end} onChange={e => setCfg({...cfg, break_end: e.target.value})} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-2 py-1.5 text-sm font-display" data-testid="cfg-break-end" />
                    </div>
                    <button
                        onClick={saveConfigAndSync}
                        disabled={busy}
                        data-testid="btn-save-sync"
                        className="bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-40 font-display flex items-center gap-2"
                        title="Salva la config e sincronizza tutti gli slot (PDF/XLSX coerenti con Planning)"
                    >💾⚡ {busy ? "Sincronizzo…" : "Salva & Sincronizza"}</button>
                    <button
                        onClick={saveConfig}
                        disabled={busy}
                        data-testid="btn-save-cfg"
                        className="border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-3 py-2 text-[10px] uppercase tracking-widest disabled:opacity-40"
                        title="Salva solo la config, senza rigenerare gli slot in Planning"
                    >💾 Solo salva</button>
                    <button
                        onClick={regenerate}
                        disabled={busy}
                        data-testid="btn-regenerate"
                        className="border border-[#E2231A]/60 hover:bg-[#E2231A]/10 text-[#E2231A] px-3 py-2 text-[10px] uppercase tracking-widest disabled:opacity-40"
                        title="Rigenera slot: applica config + alterna auto + sposta doppioni EVO/EVO o WRX/WRX a fine giornata (mantiene i lockati)"
                    >⚡ Rigenera</button>
                </div>
                <p className="text-[10px] text-[#9AA2AD] mt-3">
                    <b>💾⚡ Salva & Sincronizza</b> = salva la config + rigenera tutti gli slot in un colpo · <b>1 auto in pista alla volta</b> (EVO→WRX→EVO…) · Combo occupa 2 slot con almeno 2 turni di distanza · La pausa pranzo viene saltata.
                </p>
            </section>

            {/* ═══════ TIME CALCULATION BLOCK + PROGRESS BARS ═══════ */}
            <section className="bg-[#1C2026] p-5" data-testid="time-calc-block">
                {cfgDirty && (
                    <div className="mb-3 border border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b] text-[10px] uppercase tracking-widest px-3 py-2 flex items-center justify-between" data-testid="cfg-dirty-banner">
                        <span>⚠ Config modificata (preview live) · Clicca 💾⚡ Salva & Sincronizza per applicare ai turni</span>
                    </div>
                )}
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                    <TimeCard label="Tempo lordo" value={`${liveGross} min`} sub={`${Math.floor(liveGross / 60)}h ${liveGross % 60}m · ${cfg.start_time || "?"} → ${cfg.end_time || "?"}`} testid="tm-gross" />
                    <TimeCard label="Pausa pranzo" value={`${liveBreak} min`} sub={cfg.break_start && cfg.break_end ? `${cfg.break_start} → ${cfg.break_end}` : "nessuna pausa"} color="#f59e0b" testid="tm-break" />
                    <TimeCard label="Tempo netto pista" value={`${liveNet} min`} sub={`${Math.floor(liveNet / 60)}h ${liveNet % 60}m effettivi`} color="#16a34a" testid="tm-net" highlight />
                    <TimeCard label="Slot Max Prenotabili" value={`${liveIdealSlots}`} sub={`turno ${cfg.turn_duration_min}' + pausa ${cfg.pause_between_min}'`} color="#E2231A" testid="tm-slots" highlight />
                </div>

                {/* Barra SLOT LIBERI */}
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">🟢 Slot Liberi</div>
                        <div className="font-display text-lg text-[#16a34a]" data-testid="stat-free-slots">{Math.max(0, ideal - occupied)} <span className="text-xs text-[#9AA2AD]">/ {ideal}</span></div>
                    </div>
                    <div className="w-full h-3 bg-[#0f1116] border border-[#9AA2AD]/20 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#16a34a] to-[#4ade80] transition-all" style={{ width: `${ideal ? (Math.max(0, ideal - occupied) / ideal * 100) : 0}%` }} />
                    </div>
                </div>

                {/* Barra PRENOTAZIONI */}
                <div>
                    <div className="flex justify-between items-center mb-1">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">🔴 Prenotazioni</div>
                        <div className="font-display text-lg text-[#E2231A]" data-testid="stat-occupied-slots">{occupied} <span className="text-xs text-[#9AA2AD]">/ {ideal} · {ideal ? Math.round(occupied / ideal * 100) : 0}%</span></div>
                    </div>
                    <div className="w-full h-3 bg-[#0f1116] border border-[#9AA2AD]/20 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#E2231A] to-[#f59e0b] transition-all" style={{ width: `${ideal ? Math.min(100, Math.round(occupied / ideal * 100)) : 0}%` }} />
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-2">
                    <MiniStat label="EVO" value={stats.evo_count || 0} color="#E2231A" testid="s-evo" />
                    <MiniStat label="WRX" value={stats.wrx_count || 0} color="#4B7BEA" testid="s-wrx" />
                    <MiniStat label="Combo" value={stats.combo_count || 0} color="#16a34a" testid="s-combo" />
                    <MiniStat label="Passeggeri" value={stats.pass_count || 0} color="#f59e0b" testid="s-pass" />
                </div>
                <div className="text-[10px] text-[#9AA2AD] mt-3">
                    Fine giornata prevista: <b className="text-[#F4F2ED]">{stats.last_end_time || "—"}</b>
                </div>
            </section>

            {/* ═══════ WARNING NON PIANIFICATI ═══════ */}
            {unplanned.length > 0 && (
                <div className="border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3 text-xs text-[#f59e0b]" data-testid="unplanned-warning">
                    ⚠ {unplanned.length} prenotazione/i pagata/e non ancora nel planning: {unplanned.slice(0, 5).map(u => u.cliente).join(", ")}{unplanned.length > 5 ? "…" : ""}. Clicca "Rigenera Automatico".
                </div>
            )}

            {/* ═══════ EXPORT & CONDIVIDI (dentro Planning) ═══════ */}
            <PlanningExportBar token={token} />

            {/* ═══════ TOGGLE VISTA + TIMELINE ═══════ */}
            {timedSlots.length < slots.length && <p role="status" data-testid="planning-missing-times" className="text-sm text-amber-400">{slots.length - timedSlots.length} turni senza orario valido: disponibili in Elenco per la correzione.</p>}
            <div className="flex items-center gap-2 justify-end">
                <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Vista:</span>
                <button
                    onClick={() => setViewMode("elenco")}
                    data-testid="view-elenco"
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border ${viewMode === "elenco" ? "bg-[#E2231A] text-[#F4F2ED] border-[#E2231A]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:border-[#E2231A]"}`}
                >📋 Elenco</button>
                <button
                    onClick={() => setViewMode("planning")}
                    data-testid="view-planning"
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border ${viewMode === "planning" ? "bg-[#E2231A] text-[#F4F2ED] border-[#E2231A]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:border-[#E2231A]"}`}
                >📅 Planning</button>
            </div>

            {viewMode === "elenco" ? (
                <div className="border border-[#9AA2AD]/20 bg-[#15181C]">
                    <div className="bg-[#1C2026] px-4 py-2 border-b border-[#9AA2AD]/20 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                        <div className="col-span-1">Turno</div>
                        <div className="col-span-2">Orario</div>
                        <div className="col-span-1">Auto</div>
                        <div className="col-span-3">Cliente</div>
                        <div className="col-span-2">Voucher</div>
                        <div className="col-span-2">Note</div>
                        <div className="col-span-1 text-right">Azioni</div>
                    </div>
                    {slots.length === 0 && <div className="p-8 text-center text-[#9AA2AD] text-sm">Nessuno slot. Clicca "Rigenera Automatico" per generare gli slot dalle prenotazioni pagate.</div>}
                    {slots.map((s, idx) => <TimelineRow key={s.id} slot={s} idx={idx + 1} token={token} onEdit={setEditingSlot} onReload={load} />)}
                </div>
            ) : (
                <PlanningCalendarView slots={timedSlots} cfg={cfg} onEdit={setEditingSlot} />
            )}

            {editingSlot && (
                <SlotEditModal
                    slot={editingSlot}
                    token={token}
                    onClose={() => setEditingSlot(null)}
                    onSaved={async () => { setEditingSlot(null); await load(); }}
                />
            )}
        </div>
    );
}

// V54.3 · PlanningExportBar — pulsanti PDF / XLSX / CSV / Condividi
// PENSATA per stare DENTRO la pagina Planning, NON nella barra header.
function PlanningExportBar({ token }) {
    const [busy, setBusy] = React.useState(false);
    const downloadPdf = () => {
        window.open(adminPlanningPdfUrl(token), "_blank");
    };
    const downloadXlsx = async () => {
        setBusy(true);
        try {
            await adminPlanningXlsx(token);
        } catch { toast.error("Errore export XLSX"); } finally { setBusy(false); }
    };
    const downloadCsv = async () => {
        setBusy(true);
        try {
            await adminPlanningCsv(token);
            toast.success("CSV scaricato");
        } catch { toast.error("Errore export CSV"); } finally { setBusy(false); }
    };
    const shareIt = async () => {
        setBusy(true);
        try {
            const blob = await adminPlanningPdfBlob(token);
            const file = new File([blob], "velora_planning.pdf", { type: "application/pdf" });
            // Web Share API livello 2 (mobile) — Safari iOS / Chrome Android supportano files
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: "VELORA · Planning turni",
                    text: "Planning turni di giornata",
                    files: [file],
                });
                return;
            }
            // Fallback: copia link diretto negli appunti
            const url = adminPlanningPdfUrl(token);
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Link Planning PDF copiato negli appunti");
            } catch {
                // ultimo fallback: apri finestra con URL
                window.prompt("Copia il link del Planning PDF:", url);
            }
        } catch (e) {
            toast.error("Condivisione non disponibile: " + (e?.message || "errore"));
        } finally { setBusy(false); }
    };
    return (
        <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-3" data-testid="planning-export-bar">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#9AA2AD]">Esporta &amp; Condividi</div>
                    <div className="text-sm text-[#F4F2ED]">Planning turni — riepilogo giornata</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="btn-planning-pdf"
                        onClick={downloadPdf}
                        disabled={busy}
                        className="border border-[#E2231A]/60 hover:bg-[#E2231A]/10 text-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                        title="Apri PDF stampabile"
                    >
                        <FileText size={14} /> PDF
                    </button>
                    <button
                        data-testid="btn-planning-xlsx"
                        onClick={downloadXlsx}
                        disabled={busy}
                        className="border border-[#16a34a]/60 hover:bg-[#16a34a]/10 text-[#16a34a] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                        title="Scarica foglio Excel"
                    >
                        <Download size={14} /> XLSX
                    </button>
                    <button
                        data-testid="btn-planning-csv"
                        onClick={downloadCsv}
                        disabled={busy}
                        className="border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                        title="Scarica CSV modificabile"
                    >
                        <Download size={14} /> CSV
                    </button>
                    <button
                        data-testid="btn-planning-share"
                        onClick={shareIt}
                        disabled={busy}
                        className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                        title="Condividi su WhatsApp/Airdrop/appunti"
                    >
                        <LinkIcon size={14} /> Condividi
                    </button>
                    {/* V55.10 · Invio email personalizzata a tutti i partecipanti */}
                    <button
                        data-testid="btn-planning-email-all"
                        onClick={async () => {
                            if (!window.confirm("Inviare a TUTTI i partecipanti l'email con orario personalizzato, QR code, e istruzioni?\n\n(Chi ha già ricevuto verrà comunque ri-notificato)")) return;
                            setBusy(true);
                            try {
                                const r = await api.post("/admin/planning/email-participants", {}, { headers: { "x-admin-token": token }});
                                toast.success(`✓ Email inviate: ${r.data.sent} · Fallite: ${r.data.failed} · Saltati (senza email): ${r.data.skipped}`);
                            } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
                            finally { setBusy(false); }
                        }}
                        disabled={busy}
                        className="border border-[#f59e0b]/60 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                        title="Manda a ogni partecipante il suo orario + QR + istruzioni via email"
                    >
                        ✉️ Email a tutti
                    </button>
                    <a
                        data-testid="btn-planning-roster-pdf"
                        href={`${API}/admin/planning/roster.pdf?token=${encodeURIComponent(token)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border border-[#F4F2ED]/40 hover:bg-[#F4F2ED]/10 text-[#F4F2ED] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 no-underline"
                        title="Elenco partecipanti — solo nome/cognome/auto/ora (per stampa/pista)"
                    >
                        📋 Elenco Partecipanti
                    </a>
                </div>
            </div>
        </div>
    );
}

function TimelineRow({ slot, idx, token, onEdit, onReload }) {
    const [busy, setBusy] = useState(false);
    const carColor = { EVO: "#E2231A", WRX: "#4B7BEA", PASS: "#f59e0b" }[slot.car] || "#9AA2AD";
    const isCombo = !!slot.combo_pair_id;
    const toggleLock = async () => {
        setBusy(true);
        try {
            await adminPlanningV2SlotUpdate(token, slot.id, { locked: !slot.locked });
            await onReload();
        } catch { toast.error("Errore"); } finally { setBusy(false); }
    };
    const removeSlot = async () => {
        if (!window.confirm(`Rimuovere slot di ${slot.cliente}? La prenotazione resta ma va ripianificata.`)) return;
        setBusy(true);
        try {
            await adminPlanningV2SlotDelete(token, slot.id);
            await onReload();
        } catch { toast.error("Errore"); } finally { setBusy(false); }
    };
    // V55.9 · Sposta slot su/giù di 1 posizione (scambia con l'adiacente)
    const moveSlot = async (direction) => {
        setBusy(true);
        try {
            await adminPlanningV2SlotSwap(token, slot.id, direction);
            toast.success(direction === "up" ? "↑ Slot spostato su" : "↓ Slot spostato giù");
            await onReload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Scambio non consentito"); }
        finally { setBusy(false); }
    };
    return (
        <div
            className="grid grid-cols-12 gap-2 items-center px-4 py-2 border-b border-[#9AA2AD]/10 hover:bg-[#1C2026] transition-colors text-sm"
            style={{ borderLeftWidth: 4, borderLeftColor: carColor }}
            data-testid={`row-${slot.id}`}
        >
            <div className="col-span-1 font-display text-lg text-[#E2231A]" title="Ordine effettivo di chiamata in pista">#{idx}</div>
            <div className="col-span-2 font-display text-base" style={{ color: carColor }}>{slot.start_time || "Da pianificare"}{slot.end_time ? `–${slot.end_time}` : ""}</div>
            <div className="col-span-1"><span className="text-xs uppercase tracking-widest font-bold" style={{ color: carColor }}>{slot.car}</span></div>
            <div className="col-span-3">
                <div className="text-[#F4F2ED]">{slot.cliente || "?"}</div>
                <div className="text-[10px] text-[#9AA2AD]">{slot.telefono || "—"}</div>
            </div>
            <div className="col-span-2 font-mono text-[10px] text-[#9AA2AD]">
                {slot.turno_progressivo && (
                    <div className="text-[#f59e0b] font-bold" title="Numero voucher/QR consegnato al cliente (immutabile)">
                        #{slot.turno_progressivo} <span className="opacity-60 text-[9px]">voucher</span>
                    </div>
                )}
                <div>{slot.voucher}</div>
            </div>
            <div className="col-span-2 text-[10px]">
                {isCombo && <span className="bg-[#16a34a]/20 text-[#16a34a] px-1.5 py-0.5 uppercase tracking-widest">COMBO {slot.combo_leg}/2</span>}
                {/* V51 · Badge preferenza fascia AM/PM + warning se non rispettata */}
                {slot.preferenza_fascia && slot.preferenza_fascia !== "Indifferente" && (() => {
                    const hh = parseInt((slot.start_time || "00:00").split(":")[0], 10);
                    const isMorning = hh < 13;
                    const wantsMorning = slot.preferenza_fascia === "Mattina";
                    const respected = (wantsMorning && isMorning) || (!wantsMorning && !isMorning);
                    return (
                        <span
                            className={`ml-1 px-1.5 py-0.5 uppercase tracking-widest ${respected ? "bg-[#16a34a]/20 text-[#16a34a]" : "bg-[#E2231A]/20 text-[#E2231A] font-bold"}`}
                            title={respected ? `Preferenza ${slot.preferenza_fascia} rispettata` : `⚠️ Cliente preferiva ${slot.preferenza_fascia} ma è in ${isMorning ? "Mattina" : "Pomeriggio"}`}
                            data-testid={`pref-badge-${slot.id}`}
                        >
                            {respected ? "✓" : "⚠"} {slot.preferenza_fascia === "Mattina" ? "AM" : "PM"}
                        </span>
                    );
                })()}
                {slot.operator_name && <div className="text-[#9AA2AD] mt-0.5">👤 {slot.operator_name}</div>}
                {slot.notes && !isCombo && !slot.operator_name && <span className="text-[#9AA2AD]">{slot.notes}</span>}
            </div>
            <div className="col-span-1 flex gap-2 justify-end text-xs">
                {/* V55.9 · Frecce sposta su/giù */}
                <button onClick={() => moveSlot("up")} disabled={busy} title="Sposta su (scambia con lo slot precedente)" className="hover:text-[#f59e0b] font-bold" data-testid={`move-up-${slot.id}`}>▲</button>
                <button onClick={() => moveSlot("down")} disabled={busy} title="Sposta giù (scambia con lo slot successivo)" className="hover:text-[#f59e0b] font-bold" data-testid={`move-down-${slot.id}`}>▼</button>
                <button onClick={toggleLock} disabled={busy} title={slot.locked ? "Sblocca" : "Blocca"} className="hover:text-[#E2231A]" data-testid={`lock-${slot.id}`}>{slot.locked ? "🔒" : "🔓"}</button>
                {!slot.is_passenger && slot.car !== "PASS" && (
                    <button
                        onClick={async () => {
                            const target = slot.car === "EVO" ? "WRX" : "EVO";
                            if (!window.confirm(`Cambia auto di questo slot da ${slot.car} a ${target}?\n(La scelta viene salvata anche per le rigenerazioni future)`)) return;
                            try {
                                await adminPlanningV2SlotUpdate(token, slot.id, { car: target });
                                toast.success(`Slot cambiato in ${target}`);
                                onEdit && onEdit(null);  // trigger reload
                                if (typeof onReload === "function") onReload();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                        }}
                        disabled={busy}
                        title="Cambia vettura (persistente in rigenerazione)"
                        className="hover:text-[#f59e0b]"
                        data-testid={`swap-car-${slot.id}`}
                    >🔄</button>
                )}
                {slot.combo_leg && slot.combo_pair_id && (
                    <button
                        onClick={async () => {
                            const opts = ["EVO+WRX (Combo classico)", "EVO+EVO (Doppia EVO)", "WRX+WRX (Doppia WRX)"];
                            const choice = window.prompt(`Cambia formula del pacchetto:\n1 = ${opts[0]}\n2 = ${opts[1]}\n3 = ${opts[2]}\n\nInserisci 1, 2 o 3:`);
                            const map = { "1": "combo_duello", "2": "doppia_evo", "3": "doppia_wrx" };
                            const pid = map[(choice || "").trim()];
                            if (!pid) return;
                            const reason = window.prompt("Motivo del cambio (opzionale):") || "";
                            try {
                                await adminChangeProduct(token, slot.booking_id, pid, reason);
                                toast.success(`Formula cambiata → ${pid}. Rigenera per applicare.`);
                                if (typeof onReload === "function") onReload();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                        }}
                        disabled={busy}
                        title="Cambia intera formula pacchetto"
                        className="hover:text-[#4B7BEA]"
                        data-testid={`change-formula-${slot.id}`}
                    >📦</button>
                )}
                <button onClick={() => onEdit(slot)} title="Modifica" className="hover:text-[#4B7BEA]" data-testid={`edit-${slot.id}`}>✎</button>
                <button onClick={removeSlot} disabled={busy} title="Rimuovi" className="hover:text-[#E2231A]" data-testid={`del-${slot.id}`}>✕</button>
            </div>
        </div>
    );
}

function ConfigNum({ label, value, min, max, onChange, testid }) {
    return (
        <div>
            <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">{label}</label>
            <input type="number" min={min} max={max} value={value} onChange={e => onChange(parseInt(e.target.value) || 0)} className="w-20 bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-2 py-1.5 text-sm font-display" data-testid={testid} />
        </div>
    );
}

function MiniStat({ label, value, color, testid }) {
    return (
        <div className="border border-[#9AA2AD]/20 bg-[#15181C] p-2" data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">{label}</div>
            <div className="font-display text-xl" style={{ color: color || "#F4F2ED" }}>{value}</div>
        </div>
    );
}

function TimeCard({ label, value, sub, color, highlight, testid }) {
    return (
        <div className={`border ${highlight ? "border-[#E2231A]/40 bg-[#E2231A]/5" : "border-[#9AA2AD]/20 bg-[#15181C]"} p-3`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">{label}</div>
            <div className="font-display text-2xl mt-1" style={{ color: color || "#F4F2ED" }}>{value}</div>
            {sub && <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]/70 mt-1">{sub}</div>}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// PlanningCalendarView — Vista "Centro Estetico": Orari (Y) × Cabine Auto (X)
// Ogni cella mostra: Cliente + Accompagnatore + Auto (come una prenotazione appuntamento)
// ═══════════════════════════════════════════════════════════════════════════
function PlanningCalendarView({ slots, cfg, onEdit }) {
    const parseT = (s) => { if (typeof s !== "string" || !s.includes(":")) return NaN; const [h, m] = s.split(":").map(Number); return h * 60 + m; };
    const fmtT = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
    const startMin = parseT(cfg.start_time || "09:00");
    const endMin = parseT(cfg.end_time || "18:00");
    const breakStartMin = cfg.break_start ? parseT(cfg.break_start) : null;
    const breakEndMin = cfg.break_end ? parseT(cfg.break_end) : null;

    // Step reale slot (turn + pausa). Fallback 20 min.
    const step = Math.max(5, Number(cfg.turn_duration_min ?? 15) + Number(cfg.pause_between_min ?? 5));
    // Genera tick base ogni "step" min + aggiungi TUTTI gli start_time reali degli slot (per non perdere passeggeri 0.5)
    const tickSet = new Set();
    for (let t = startMin; t < endMin; t += step) tickSet.add(t);
    slots.forEach(s => {
        const st = parseT(s.start_time);
        if (st >= startMin && st < endMin) tickSet.add(st);
    });
    const ticks = Array.from(tickSet).sort((a, b) => a - b);

    const CABINE = [
        { key: "EVO", label: "🏎 AUTO 1 · EVO X", color: "#E2231A" },
        { key: "WRX", label: "🚗 AUTO 2 · WRX", color: "#4B7BEA" },
    ];

    // Slot che parte esattamente al tick per la cabina data
    const slotStartsAt = (tick, carKey) => slots.find(s => {
        if (s.car === "PASS") {
            // Passeggero appare nella colonna EVO per default
            return carKey === "EVO" && parseT(s.start_time) === tick;
        }
        return s.car === carKey && parseT(s.start_time) === tick;
    });
    // Verifica se il tick è "coperto" da uno slot (in mezzo)
    const isCoveredBy = (tick, carKey) => slots.find(s => {
        const car = s.car === "PASS" ? "EVO" : s.car;
        return car === carKey && parseT(s.start_time) < tick && tick < parseT(s.end_time);
    });

    return (
        <div className="border border-[#9AA2AD]/20 bg-[#15181C]" data-testid="planning-calendar">
            <div className="bg-[#1C2026] p-3 text-[10px] uppercase tracking-widest text-[#9AA2AD] border-b border-[#9AA2AD]/20">
                📅 Vista Cabine · Orari verticali × Auto{cfg.break_start && cfg.break_end ? ` · pausa ${cfg.break_start}-${cfg.break_end}` : ""}
            </div>

            {/* Header colonne cabine */}
            <div className="grid grid-cols-[100px_1fr_1fr] gap-0 sticky top-0 z-10 bg-[#1C2026] border-b border-[#9AA2AD]/20">
                <div className="p-3 text-[10px] uppercase tracking-widest text-[#9AA2AD] border-r border-[#9AA2AD]/20">ORARIO</div>
                {CABINE.map(c => (
                    <div key={c.key} className="p-3 text-[11px] uppercase tracking-widest font-bold text-center border-r border-[#9AA2AD]/20" style={{ color: c.color }}>
                        {c.label}
                    </div>
                ))}
            </div>

            {/* Righe orario */}
            {ticks.map(tick => {
                const isBreak = breakStartMin !== null && tick >= breakStartMin && tick < breakEndMin;
                if (isBreak) {
                    return (
                        <div key={tick} className="grid grid-cols-[100px_1fr_1fr] gap-0 border-b border-[#9AA2AD]/10">
                            <div className="p-2 font-mono text-xs text-[#f59e0b] border-r border-[#9AA2AD]/10">{fmtT(tick)}</div>
                            <div className="col-span-2 p-2 text-center text-[#f59e0b] uppercase tracking-widest text-xs bg-[#f59e0b]/5 border-r border-[#9AA2AD]/10">🍽 Pausa Pranzo</div>
                        </div>
                    );
                }
                return (
                    <div key={tick} className="grid grid-cols-[100px_1fr_1fr] gap-0 border-b border-[#9AA2AD]/10">
                        <div className="p-2 font-mono text-xs text-[#9AA2AD] border-r border-[#9AA2AD]/10 sticky left-0 bg-[#15181C]">{fmtT(tick)}</div>
                        {CABINE.map(cabina => {
                            const slot = slotStartsAt(tick, cabina.key);
                            const covered = !slot && isCoveredBy(tick, cabina.key);
                            if (slot) {
                                const isPass = slot.car === "PASS";
                                return (
                                    <button
                                        key={cabina.key}
                                        onClick={() => onEdit(slot)}
                                        className="p-2 text-left hover:bg-[#1C2026] transition-colors border-r border-[#9AA2AD]/10"
                                        style={{ borderLeft: `4px solid ${cabina.color}` }}
                                        data-testid={`cal-${cabina.key}-${tick}`}
                                    >
                                        <div className="text-[10px] font-mono" style={{ color: cabina.color }}>{slot.start_time}→{slot.end_time}</div>
                                        <div className="text-sm text-[#F4F2ED] font-medium mt-0.5">{slot.cliente || "?"}</div>
                                        {slot.operator_name && (
                                            <div className="text-[10px] text-[#9AA2AD] mt-0.5">👤 Accomp: <span className="text-[#F4F2ED]">{slot.operator_name}</span></div>
                                        )}
                                        <div className="flex gap-1 mt-1 flex-wrap">
                                            {isPass && <span className="text-[9px] bg-[#f59e0b]/20 text-[#f59e0b] px-1.5 py-0.5 uppercase tracking-widest">Passeggero</span>}
                                            {slot.combo_pair_id && <span className="text-[9px] bg-[#16a34a]/20 text-[#16a34a] px-1.5 py-0.5 uppercase tracking-widest">Combo {slot.combo_leg}/2</span>}
                                            {slot.locked && <span className="text-[9px]">🔒</span>}
                                        </div>
                                    </button>
                                );
                            }
                            if (covered) {
                                return <div key={cabina.key} className="border-r border-[#9AA2AD]/10 bg-[#1C2026]/40" />;
                            }
                            return (
                                <div key={cabina.key} className="p-2 text-center text-[10px] text-[#16a34a]/40 uppercase tracking-widest border-r border-[#9AA2AD]/10 border-dashed">
                                    — libero —
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

function SlotEditModal({ slot, token, onClose, onSaved }) {
    const [f, setF] = useState({
        start_time: slot.start_time,
        end_time: slot.end_time,
        car: slot.car,
        notes: slot.notes || "",
        operator_name: slot.operator_name || "",
    });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            await adminPlanningV2SlotUpdate(token, slot.id, f);
            toast.success("Slot aggiornato");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore salvataggio");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="slot-edit-modal">
            <div className="bg-[#15181C] border border-[#E2231A]/40 max-w-md w-[92%]" onClick={e => e.stopPropagation()}>
                <div className="bg-[#E2231A] px-4 py-3 flex items-center justify-between">
                    <div className="font-display text-sm uppercase tracking-widest text-[#F4F2ED]">Modifica Slot · {slot.cliente}</div>
                    <button onClick={onClose} className="text-[#F4F2ED] text-xl leading-none">×</button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Inizio</label>
                            <input type="time" value={f.start_time} onChange={e => setF({...f, start_time: e.target.value})} className="w-full bg-[#0f1116] border border-[#9AA2AD]/30 px-2 py-1.5 text-sm font-display" data-testid="edit-start-time" />
                        </div>
                        <div>
                            <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Fine</label>
                            <input type="time" value={f.end_time} onChange={e => setF({...f, end_time: e.target.value})} className="w-full bg-[#0f1116] border border-[#9AA2AD]/30 px-2 py-1.5 text-sm font-display" data-testid="edit-end-time" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Auto</label>
                        <select value={f.car} onChange={e => setF({...f, car: e.target.value})} className="w-full bg-[#0f1116] border border-[#9AA2AD]/30 px-2 py-1.5 text-sm" data-testid="edit-car">
                            <option value="EVO">EVO</option>
                            <option value="WRX">WRX</option>
                            <option value="PASS">Passeggero</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">👤 Accompagnatore / Operatore</label>
                        <input type="text" value={f.operator_name} onChange={e => setF({...f, operator_name: e.target.value})} placeholder="Nome accompagnatore (es. Alessio, Vittorio…)" className="w-full bg-[#0f1116] border border-[#9AA2AD]/30 px-2 py-1.5 text-sm" data-testid="edit-operator" />
                    </div>
                    <div>
                        <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Note</label>
                        <textarea rows={3} value={f.notes} onChange={e => setF({...f, notes: e.target.value})} className="w-full bg-[#0f1116] border border-[#9AA2AD]/30 px-2 py-1.5 text-sm resize-none" data-testid="edit-notes" />
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-[#9AA2AD]/20">
                        <button onClick={save} disabled={busy} className="flex-1 bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-widest text-[#F4F2ED]" data-testid="edit-save">{busy ? "…" : "💾 Salva"}</button>
                        <button onClick={onClose} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest text-[#F4F2ED]">Annulla</button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function StatsView({ token }) {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        adminAnalytics(token, days).then(d => { setData(d); setLoading(false); }).catch(() => { setLoading(false); toast.error("Errore statistiche"); });
    }, [days, token]);

    if (loading) return <div className="text-center py-12 text-[#9AA2AD]">Caricamento…</div>;
    if (!data) return null;

    const maxViews = Math.max(1, ...data.daily.map(d => d.views));

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs uppercase tracking-widest text-[#9AA2AD]">
                    Analytics · ultimi <b className="text-[#F4F2ED]">{data.days}</b> giorni
                </div>
                <div className="flex gap-2">
                    {[7, 30, 90].map(d => (
                        <button key={d} data-testid={`stats-range-${d}`} onClick={() => setDays(d)} className={`px-4 py-2 border uppercase tracking-widest text-xs transition-colors duration-150 ${days === d ? "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:text-[#F4F2ED]"}`}>{d}gg</button>
                    ))}
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <Stat tid="stat-total-views" icon={<Users strokeWidth={1.5} />} label="Visite totali" value={data.total_views} />
                <Stat tid="stat-unique-sessions" icon={<LayoutGrid strokeWidth={1.5} />} label="Sessioni uniche" value={data.total_sessions} />
            </div>

            {/* Grafico daily semplice a barre */}
            <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-5" data-testid="stats-chart">
                <div className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-4">Andamento giornaliero</div>
                <div className="flex items-end gap-1 h-40">
                    {data.daily.length === 0 && <div className="text-[#9AA2AD] text-sm">Nessun dato ancora. Le visite verranno tracciate da adesso.</div>}
                    {data.daily.map(d => (
                        <div key={d.day} className="flex-1 flex flex-col items-center min-w-0" title={`${d.day}: ${d.views} views, ${d.unique_sessions} sess.`}>
                            <div className="w-full bg-[#E2231A] hover:bg-[#F4F2ED] transition-colors duration-150" style={{ height: `${(d.views / maxViews) * 100}%`, minHeight: "2px" }}></div>
                            <div className="text-[8px] text-[#9AA2AD] mt-1 rotate-45 origin-left whitespace-nowrap">{d.day.slice(5)}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <StatsTable title="Pagine più viste" rows={data.top_paths} keyField="path" valField="views" />
                <StatsTable title="Da dove arrivano (referrer)" rows={data.top_referrers} keyField="referrer" valField="views" placeholder="Nessuna sorgente esterna ancora" />
                <StatsTable title="Campagne ads (UTM)" rows={data.top_utm.map(u => ({ path: `${u.source}${u.campaign ? " · " + u.campaign : ""}`, views: u.views }))} keyField="path" valField="views" placeholder="Nessuna campagna attiva. Usa link tipo ?utm_source=instagram&utm_campaign=lancio" />
            </div>

            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]/70">
                Privacy: IP anonimizzato (hash), nessun dato personale tracciato. Compatibile GDPR base.<br/>
                Per campagne pubblicitarie usa link con parametri UTM: <code className="bg-[#15181C] px-2 py-0.5 text-[#F4F2ED]">https://velorajdm.com/?utm_source=instagram&utm_campaign=lancio&utm_medium=stories</code>
            </div>
        </div>
    );
}

function StatsTable({ title, rows, keyField, valField, placeholder }) {
    return (
        <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-3">{title}</div>
            {rows.length === 0 ? (
                <div className="text-[#9AA2AD] text-xs italic">{placeholder || "—"}</div>
            ) : (
                <ul className="space-y-1 text-sm">
                    {rows.slice(0, 8).map((r, i) => (
                        <li key={i} className="flex justify-between gap-3 items-center border-b border-[#9AA2AD]/10 pb-1">
                            <span className="truncate text-xs text-[#F4F2ED]" title={r[keyField]}>{r[keyField] || "(directe)"}</span>
                            <span className="font-display text-lg text-[#E2231A]">{r[valField]}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// MediaUtilityPanel — Bottone invio link Foto+Video + Ri-upload patente
// ═══════════════════════════════════════════════════════════════════════════
const DOC_LABELS = {
    patente: "🪪 Patente (fronte + retro completa)",
    patente_retro: "📎 Patente — SOLO RETRO (fronte OK)",
    carta_identita: "🆔 Carta d'Identità",
    cf: "🧾 Codice Fiscale / Tessera",
    foto_viso: "🤳 Foto viso (selfie)",
    firma_manleva: "✍️ Firma Manleva",
    altro: "📎 Altro documento…",
};

// V50 — Rileva documenti mancanti (o URL con placeholder falso) dal booking
function detectMissingDocs(b) {
    if (!b) return [];
    const missing = [];
    if (!b.has_license && !b.patente_url && !b.patente_fronte_url) missing.push("patente");
    else if (!b.has_license_retro && !b.patente_retro_url) missing.push("patente_retro");
    if (!b.cf_photo_url && !b.has_cf_photo) missing.push("cf");
    if (!b.signature_dataurl && !b.manleva_signed_at) missing.push("firma_manleva");
    return missing;
}

function DocReuploadDropdown({ onSelect, busy, bookingId, booking }) {
    const [open, setOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const [customNote, setCustomNote] = useState("");

    const missing = detectMissingDocs(booking);

    const handleSelect = (docType) => {
        setOpen(false);
        if (docType === "altro") { setShowCustom(true); return; }
        onSelect(docType, "");
    };
    const submitCustom = () => {
        if (!customNote.trim()) { toast.error("Specifica quale documento serve"); return; }
        setShowCustom(false);
        onSelect("altro", customNote.trim());
        setCustomNote("");
    };

    // Colori differenziati: se doc MANCA → rosso lampeggiante; se doc PRESENTE (sostituzione) → arancione
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(v => !v)}
                disabled={busy}
                data-testid={`btn-request-doc-${bookingId}`}
                className={`w-full uppercase tracking-widest border py-1.5 px-2 disabled:opacity-40 flex items-center justify-between gap-1 ${missing.length ? "border-[#E2231A]/60 hover:bg-[#E2231A]/10 text-[#E2231A]" : "border-[#f59e0b]/40 hover:bg-[#f59e0b]/10 text-[#f59e0b]"}`}
            >
                <span>{missing.length ? `🔴 Richiedi Documento (${missing.length} mancanti)` : "📎 Richiedi Documento"}</span>
                <span className="text-[9px]">{open ? "▲" : "▼"}</span>
            </button>
            {open && (
                <div className="absolute left-0 right-0 top-full mt-1 z-10 border border-[#f59e0b]/40 bg-[#15181C] shadow-xl" data-testid={`doc-menu-${bookingId}`}>
                    {/* Sezione MANCANTI in cima (auto-detect) */}
                    {missing.length > 0 && (
                        <>
                            <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest bg-[#E2231A]/20 text-[#E2231A] border-b border-[#E2231A]/30">
                                🔴 Manca — clic per richiederli:
                            </div>
                            {missing.map(k => (
                                <button key={`m-${k}`} onClick={() => handleSelect(k)}
                                    className="w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#E2231A]/10 border-b border-[#9AA2AD]/10 text-[#F4F2ED] flex items-center gap-2"
                                    data-testid={`doc-missing-${k}`}>
                                    <span className="text-[#E2231A]">●</span> {DOC_LABELS[k] || k}
                                </button>
                            ))}
                            <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest bg-[#9AA2AD]/10 text-[#9AA2AD] border-b border-[#9AA2AD]/20">
                                Sostituisci / richiedi altro:
                            </div>
                        </>
                    )}
                    {Object.entries(DOC_LABELS).map(([k, l]) => (
                        <button key={k} onClick={() => handleSelect(k)}
                            className="w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#f59e0b]/10 border-b border-[#9AA2AD]/10 last:border-b-0"
                            data-testid={`doc-opt-${k}`}>
                            {l}
                        </button>
                    ))}
                </div>
            )}
            {showCustom && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="doc-custom-modal">
                    <div className="bg-[#15181C] border border-[#f59e0b]/40 p-4 max-w-md w-full">
                        <h3 className="uppercase tracking-widest text-[#f59e0b] text-sm mb-2">Altro documento</h3>
                        <p className="text-xs text-[#9AA2AD] mb-3">Specifica quale documento il cliente deve inviare (es. "Copia bonifico", "Certificato medico"…):</p>
                        <input type="text" value={customNote} onChange={e => setCustomNote(e.target.value)}
                            className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 text-[#F4F2ED] px-3 py-2 text-sm" autoFocus />
                        <div className="mt-3 flex gap-2 justify-end">
                            <button onClick={() => setShowCustom(false)} className="border border-[#9AA2AD]/40 text-[#9AA2AD] px-3 py-1.5 text-[10px] uppercase tracking-widest">Annulla</button>
                            <button onClick={submitCustom} className="bg-[#E2231A] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest">Invia richiesta</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function BoardingPassPanel({ bookingId, token, booking }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState(null);
    const API = process.env.REACT_APP_BACKEND_URL;
    const tokenQ = encodeURIComponent(token);
    const passUrl = `${window.location.origin}/pass/${booking.voucher_code}`;
    const pdfUrl = `${API}/api/admin/bookings/${bookingId}/pass.pdf?admin_token=${tokenQ}`;
    const pngUrl = `${API}/api/admin/bookings/${bookingId}/pass.png?admin_token=${tokenQ}`;

    const resend = async () => {
        if (!window.confirm(`Reinviare email con Boarding Pass a ${booking.customer_email}?`)) return;
        setBusy(true);
        try {
            await api.post(`/admin/bookings/${bookingId}/resend-pass-email`, null, { headers: { "x-admin-token": token } });
            toast.success(`Email pass inviata a ${booking.customer_email}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
        finally { setBusy(false); }
    };

    const loadHistory = async () => {
        setBusy(true);
        try {
            const r = await api.get(`/admin/bookings/${bookingId}/customer-history`, { headers: { "x-admin-token": token } });
            setHistory(r.data);
        } catch (e) { toast.error("Errore storico"); }
        finally { setBusy(false); }
    };

    return (
        <div className="border border-[#9AA2AD]/20 bg-[#0F1114]/50 p-1.5" data-testid={`boarding-pass-panel-${bookingId}`}>
            <button onClick={() => setOpen(v => !v)} className="w-full text-left text-[10px] uppercase tracking-widest text-[#f59e0b] flex items-center justify-between" data-testid={`bp-toggle-${bookingId}`}>
                <span>🎫 Boarding Pass · #{booking.turno_progressivo || "—"}</span><span>{open ? "▲" : "▼"}</span>
            </button>
            {open && (
                <div className="mt-2 grid grid-cols-2 gap-1">
                    <a href={passUrl} target="_blank" rel="noreferrer"
                        className="text-[9px] uppercase tracking-widest border border-[#4B7BEA]/40 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] py-1 px-2 flex items-center justify-center gap-1"
                        data-testid={`bp-view-${bookingId}`}
                        title="Anteprima Boarding Pass web (link pubblico)">👁 Vedi</a>
                    <a href={pdfUrl} target="_blank" rel="noreferrer"
                        className="text-[9px] uppercase tracking-widest border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] py-1 px-2 flex items-center justify-center gap-1"
                        data-testid={`bp-pdf-${bookingId}`}
                        title="Scarica PDF pass (100×150mm — biglietto stampabile)">🖨 PDF</a>
                    <a href={pngUrl} target="_blank" rel="noreferrer"
                        className="text-[9px] uppercase tracking-widest border border-[#ca8a04]/40 hover:bg-[#ca8a04]/10 text-[#ca8a04] py-1 px-2 flex items-center justify-center gap-1"
                        data-testid={`bp-png-${bookingId}`}
                        title="PNG social 1080×1350 (Instagram/WhatsApp)">📱 PNG</a>
                    <button onClick={resend} disabled={busy || !booking.customer_email}
                        className="text-[9px] uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] py-1 px-2 flex items-center justify-center gap-1 disabled:opacity-40"
                        data-testid={`bp-email-${bookingId}`}
                        title={booking.customer_email ? `Rinvia email a ${booking.customer_email}` : "Email cliente mancante"}>📧 Email</button>
                    <button onClick={loadHistory} disabled={busy || !booking.customer_email}
                        className="col-span-2 text-[9px] uppercase tracking-widest border border-[#9333ea]/40 hover:bg-[#9333ea]/10 text-[#9333ea] py-1 px-2 flex items-center justify-center gap-1 disabled:opacity-40"
                        data-testid={`bp-history-${bookingId}`}
                        title="Cerca altri booking con la stessa email">👤 Storico cliente</button>
                    {history && (
                        <div className="col-span-2 mt-2 border-t border-[#9AA2AD]/20 pt-2 text-[10px]">
                            {history.count === 0 ? (
                                <div className="text-[#9AA2AD]">Nessun altro booking per <span className="font-mono">{history.customer_email}</span></div>
                            ) : (
                                <div>
                                    <div className="text-[#9333ea] uppercase tracking-widest mb-1">{history.count} booking precedent{history.count === 1 ? "e" : "i"}:</div>
                                    {history.bookings.map(h => (
                                        <div key={h.id} className="flex justify-between border-b border-[#9AA2AD]/10 py-1">
                                            <span className="text-[#F4F2ED]">#{h.turno_progressivo || "—"} · {h.voucher_code}</span>
                                            <span className="text-[#9AA2AD]">{h.product_id} · {h.status}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function MediaUtilityPanel({ bookingId, token, booking, onReload }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [linkInfo, setLinkInfo] = useState(null);

    const hasMedia = !!(booking?.addon_media_bundle || booking?.addon_video || booking?.addon_foto);
    const mediaStatus = booking?.media_status || (hasMedia ? "paid_upfront" : "not_offered");
    const badgeConfig = {
        paid_upfront: { label: "Media ✓ PAID UP", color: "text-[#16a34a] border-[#16a34a]/40 bg-[#16a34a]/10" },
        paid_late: { label: "Media ✓ PAID POST", color: "text-[#16a34a] border-[#16a34a]/40 bg-[#16a34a]/10" },
        offered: { label: "Media OFFERTO", color: "text-[#f59e0b] border-[#f59e0b]/40 bg-[#f59e0b]/10" },
        not_offered: { label: "Media NO", color: "text-[#9AA2AD] border-[#9AA2AD]/30" },
    }[mediaStatus] || { label: mediaStatus, color: "text-[#9AA2AD]" };

    const inviaLink = async () => {
        setBusy(true);
        try {
            const r = await adminSendMediaLink(token, bookingId);
            setLinkInfo(r);
            toast.success(r.email_sent ? "Email inviata + link generato" : "Link generato (email non inviata)");
            if (onReload) await onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore invio link");
        } finally { setBusy(false); }
    };

    const richiediRipatente = async (docType, note) => {
        if (!window.confirm(`Confermi la richiesta di ricaricare "${DOC_LABELS[docType] || docType}"?\n\nIl cliente riceverà una email con link al tunnel.`)) return;
        setBusy(true);
        try {
            const r = await adminRequestDocReupload(token, bookingId, docType, note || "");
            toast.success(r.email_sent ? `Email inviata (${r.doc_label})` : `Richiesta salvata (${r.doc_label}) — email non inviata`);
            if (onReload) await onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };

    const copy = (text) => {
        try { navigator.clipboard.writeText(text); toast.success("Copiato"); } catch { toast.error("Copia non riuscita"); }
    };

    return (
        <div>
            <button
                onClick={() => setOpen(v => !v)}
                data-testid={`btn-media-util-toggle-${bookingId}`}
                className={`w-full text-[10px] uppercase tracking-widest border py-1.5 px-2 flex items-center justify-between transition-colors duration-150 ${badgeConfig.color}`}
                title="Gestisci Media Foto+Video e opzioni cliente"
            >
                <span>🎥 {badgeConfig.label}</span>
                <span className="text-[9px] text-[#9AA2AD]">{open ? "▲" : "▼"}</span>
            </button>
            {open && (
                <div className="mt-1 grid grid-cols-1 gap-1 border border-[#9AA2AD]/20 p-2 bg-[#15181C] text-[10px]" data-testid={`media-util-panel-${bookingId}`}>
                    {!hasMedia && (
                        <button
                            onClick={inviaLink}
                            disabled={busy}
                            data-testid={`btn-send-media-${bookingId}`}
                            className="uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#F4F2ED] py-1.5 px-2 disabled:opacity-40"
                        >📨 {busy ? "…" : "Invia link Foto+Video (email)"}</button>
                    )}
                    {linkInfo && (
                        <div className="border border-[#9AA2AD]/20 p-2 bg-[#0f1116] space-y-1">
                            <div className="text-[#9AA2AD] uppercase tracking-widest text-[9px]">Link generato:</div>
                            <div className="flex items-center gap-1">
                                <code className="flex-1 text-[10px] text-[#4B7BEA] break-all">{linkInfo.landing_url}</code>
                                <button onClick={() => copy(linkInfo.landing_url)} className="text-[9px] uppercase tracking-widest border border-[#9AA2AD]/30 px-1.5 py-0.5">📋</button>
                            </div>
                            {linkInfo.whatsapp_url && (
                                <a href={linkInfo.whatsapp_url} target="_blank" rel="noopener noreferrer" className="block text-center uppercase tracking-widest border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 py-1.5 px-2 mt-1" data-testid={`btn-wa-media-${bookingId}`}>📱 Apri WhatsApp</a>
                            )}
                        </div>
                    )}
                    {/* V50: sempre visibile, con auto-detect dei documenti mancanti */}
                    <DocReuploadDropdown onSelect={richiediRipatente} busy={busy} bookingId={bookingId} booking={booking} />
                    {hasMedia && <div className="text-center text-[#16a34a] uppercase tracking-widest py-1.5">✓ Media già acquistato</div>}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Pannello espandibile "PDF Firmati Singoli" — Manleva / Privacy / Condizioni / Maggiore età
// ═══════════════════════════════════════════════════════════════════════════
function SingleDocsPanel({ bookingId, token, booking }) {
    const [open, setOpen] = useState(false);
    const dich = booking?.dichiarazioni || {};
    const paid = ["pagata", "completata"].includes(booking?.status);
    const items = [
        { key: "manleva", label: "Manleva firmata", enabled: !!booking?.manleva_signed, hint: "Firma autografa + audit trail SHA-256" },
        { key: "privacy", label: "Privacy GDPR", enabled: !!dich.gdpr, hint: "Informativa art. 13 GDPR + consenso firmato" },
        { key: "condizioni", label: "Condizioni Commerciali", enabled: paid, hint: "Clausole ex art. 1341-1342 c.c. + Foro Benevento" },
        { key: "maggiorenne", label: "Dichiarazione Maggiore Età", enabled: !!dich.maggiorenne, hint: "Dichiarazione sostitutiva DPR 445/2000" },
    ];
    return (
        <div>
            <button
                onClick={() => setOpen(v => !v)}
                data-testid={`btn-single-docs-toggle-${bookingId}`}
                className="w-full text-[10px] uppercase tracking-widest border border-[#E2231A]/30 hover:bg-[#E2231A]/10 text-[#F4F2ED] py-1.5 px-2 flex items-center justify-between transition-colors duration-150"
                title="Apri PDF firmati singoli per ogni contratto"
            >
                <span>📑 PDF Firmati Singoli</span>
                <span className="text-[9px] text-[#9AA2AD]">{open ? "▲" : "▼"}</span>
            </button>
            {open && (
                <div className="mt-1 grid grid-cols-1 gap-1 border border-[#9AA2AD]/20 p-1.5 bg-[#15181C]" data-testid={`single-docs-list-${bookingId}`}>
                    {items.map(it => it.enabled ? (
                        <a
                            key={it.key}
                            href={adminSingleDocPdfUrl(bookingId, it.key, token)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`single-doc-${it.key}-${bookingId}`}
                            title={it.hint}
                            className="text-[10px] uppercase tracking-widest text-[#F4F2ED] hover:bg-[#E2231A]/20 hover:text-[#E2231A] py-1 px-2 flex items-center gap-1.5 transition-colors duration-150 border-l-2 border-[#16a34a]"
                        >
                            <span className="text-[#16a34a]">✓</span> {it.label}
                        </a>
                    ) : (
                        <div
                            key={it.key}
                            className="text-[10px] uppercase tracking-widest text-[#9AA2AD]/50 py-1 px-2 flex items-center gap-1.5 border-l-2 border-[#9AA2AD]/20 cursor-not-allowed"
                            title="Documento non ancora firmato/accettato"
                        >
                            <span className="text-[#9AA2AD]/40">✗</span> {it.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════════════════════
// Modal dettagli pagamento Stripe — apre al click sul badge "Pagato"
// ═══════════════════════════════════════════════════════════════════════════
function PaymentInfoModal({ bookingId, token, onClose }) {
    const [info, setInfo] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        let alive = true;
        adminBookingPaymentInfo(token, bookingId)
            .then(d => { if (alive) setInfo(d); })
            .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Errore rete"); });
        return () => { alive = false; };
    }, [bookingId, token]);

    const money = info?.amount_cents != null ? `${(info.amount_cents / 100).toFixed(2)} ${info.currency || "EUR"}` : "—";
    const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="payment-info-modal">
            <div className="bg-[#15181C] border border-[#E2231A]/40 max-w-lg w-[92%] max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
                <div className="bg-[#E2231A] px-4 py-3 flex items-center justify-between">
                    <div className="font-display text-sm uppercase tracking-widest text-[#F4F2ED]">Dettagli Pagamento</div>
                    <button onClick={onClose} data-testid="payment-info-close" className="text-[#F4F2ED] hover:text-[#15181C] text-xl leading-none">×</button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    {err && <div className="text-[#E2231A] text-xs bg-[#E2231A]/10 border border-[#E2231A]/30 p-3">{err}</div>}
                    {!info && !err && <div className="text-[#9AA2AD] text-xs">Caricamento…</div>}
                    {info && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Voucher" value={info.voucher} />
                                <Field label="Stato" value={(info.status || "—").toUpperCase()} highlight />
                                <Field label="Importo" value={money} highlight />
                                <Field label="Metodo" value={info.payment_method_brand ? `${info.payment_method_brand} •••• ${info.payment_method_last4 || ""}` : "—"} />
                                <Field label="Cliente" value={info.customer_name || "—"} />
                                <Field label="Email" value={info.customer_email || "—"} />
                                <Field label="Data pagamento" value={fmtDate(info.paid_at || info.created_at)} />
                                <Field label="Ricevuta n." value={info.receipt_number || "—"} />
                            </div>
                            <div className="border-t border-[#9AA2AD]/20 pt-3 space-y-1">
                                <Field label="Stripe Session ID" mono value={info.stripe_session_id || "—"} />
                                <Field label="Payment Intent" mono value={info.stripe_payment_intent || "—"} />
                            </div>
                            {info.stripe_error && (
                                <div className="text-[10px] text-[#f59e0b] bg-[#f59e0b]/10 border border-[#f59e0b]/30 p-2">
                                    ⚠ Impossibile recuperare live da Stripe: {info.stripe_error}
                                </div>
                            )}
                            <div className="flex gap-2 pt-3 border-t border-[#9AA2AD]/20">
                                {info.receipt_url ? (
                                    <a href={info.receipt_url} target="_blank" rel="noopener noreferrer" data-testid="btn-stripe-receipt" className="flex-1 text-center bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] px-4 py-2 text-xs uppercase tracking-widest">
                                        📄 Ricevuta Stripe ufficiale
                                    </a>
                                ) : (
                                    <div className="flex-1 text-center border border-[#9AA2AD]/30 text-[#9AA2AD] px-4 py-2 text-xs uppercase tracking-widest">Ricevuta non disponibile</div>
                                )}
                                <button onClick={onClose} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest text-[#F4F2ED]">Chiudi</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, highlight = false, mono = false }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-0.5">{label}</div>
            <div className={`${mono ? "font-mono text-[10px] break-all" : "text-xs"} ${highlight ? "text-[#E2231A] font-bold" : "text-[#F4F2ED]"}`}>{value}</div>
        </div>
    );
}


// Timeline stato cliente: mostra i milestone del flusso Pagato → Email → Doc → Man → Accett → Gate.
const fmtShort = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
};

function TimelineDot({ label, done, ts, warn = false, testid }) {
    const color = done ? "bg-[#16a34a] border-[#16a34a]" : warn ? "bg-transparent border-[#E2231A]" : "bg-transparent border-[#9AA2AD]/40";
    const textColor = done ? "text-[#F4F2ED]" : warn ? "text-[#E2231A]" : "text-[#9AA2AD]/70";
    return (
        <div className="flex flex-col items-center" title={`${label}${ts ? " · " + fmtShort(ts) : ""}`} data-testid={testid}>
            <div className={`w-3 h-3 rounded-full border-2 ${color}`} />
            <div className={`text-[8px] uppercase tracking-widest mt-1 ${textColor}`}>{label}</div>
            {done && ts && <div className="text-[8px] text-[#9AA2AD]/70 whitespace-nowrap">{fmtShort(ts)}</div>}
        </div>
    );
}

// Checklist compatta a colpo d'occhio: mostra ogni requisito con ✓/✗ colorato + tooltip.
function ChecklistItem({ label, done, warn = false, note = "", testid, onClick, href }) {
    const icon = done ? "✓" : warn ? "!" : "✗";
    const color = done ? "text-[#16a34a]" : warn ? "text-[#f59e0b]" : "text-[#E2231A]";
    const bg = done ? "bg-[#16a34a]/15" : warn ? "bg-[#f59e0b]/15" : "bg-[#E2231A]/10";
    const clickable = !!(onClick || href);
    const inner = (
        <>
            <span className={`${color} font-bold text-[11px] w-3 text-center`}>{icon}</span>
            <span className={`text-[9px] uppercase tracking-wide ${done ? "text-[#F4F2ED]" : color}`}>{label}</span>
        </>
    );
    const cls = `flex items-center gap-1.5 px-1.5 py-0.5 ${bg} ${clickable ? "cursor-pointer hover:brightness-125 hover:ring-1 hover:ring-[#F4F2ED]/30 transition" : ""}`;
    if (href) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className={cls} title={note || label} data-testid={testid}>{inner}</a>
        );
    }
    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={`${cls} text-left w-full`} title={note || label} data-testid={testid}>{inner}</button>
        );
    }
    return (
        <div className={cls} title={note || label} data-testid={testid}>{inner}</div>
    );
}

function BookingTimeline({ b, token, onQuickUpload }) {
    const paid = ["pagata", "completata"].includes(b.status);
    const emailOk = !!b.email_receipt_sent_at;
    const patenteFronte = !!b.has_license;
    const patenteRetro = !!b.has_license_retro;
    const cfImg = !!b.has_cf_photo;
    const cfText = !!b.has_cf_text;
    const cfOk = cfImg || cfText;
    const manlevaOk = !!b.manleva_signed;
    const dich = b.dichiarazioni || {};
    const gdpr = !!dich.gdpr;
    const immagini = !!dich.immagini;
    const magg = !!dich.maggiorenne;
    const prelimOk = !!b.prelim_at;
    const gateOk = !!b.gate_at;
    const waMkt = !!b.consenso_whatsapp_marketing;
    const isAdminOverride = b.manleva_signature_type === "admin_override";
    const fattura = b.richiede_fattura;

    // Popup dettagli pagamento (aperto al click sul badge Pagato)
    const [showPayment, setShowPayment] = useState(false);

    // Click su file presente → apre in nuova scheda. Click su mancante → trigger upload.
    const docHref = (kind, present) => (present && token) ? adminBookingDocUrl(b.id, kind, token) : null;
    const missingClick = (kind) => onQuickUpload ? () => onQuickUpload(kind) : undefined;
    // PDF singolo firmato (privacy / condizioni / manleva / maggiorenne)
    const singleDocHref = (docType) => token ? adminSingleDocPdfUrl(b.id, docType, token) : null;

    // ordine logico: soldi → contatto → docs → consensi legali → giornata evento → marketing
    return (
        <>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 w-[280px]" data-testid={`timeline-${b.id}`}>
                <ChecklistItem
                    label="Pagato"
                    done={paid}
                    note={paid ? `Pagato il ${fmtShort(b.created_at)} — click per ricevuta` : "Non pagato"}
                    testid={`c-paid-${b.id}`}
                    onClick={paid ? () => setShowPayment(true) : undefined}
                />
                <ChecklistItem label="Email ricevuta" done={emailOk} warn={paid && !emailOk} note={emailOk ? `Inviata ${fmtShort(b.email_receipt_sent_at)}` : "Email conferma non inviata"} testid={`c-email-${b.id}`} />
                <ChecklistItem
                    label="Patente FRONTE"
                    done={patenteFronte}
                    note={patenteFronte ? `Caricata ${fmtShort(b.patente_uploaded_at)} — click per aprire` : "Foto patente FRONTE mancante — click per caricare"}
                    testid={`c-pat-fronte-${b.id}`}
                    href={docHref("patente", patenteFronte)}
                    onClick={patenteFronte ? undefined : missingClick("patente_fronte")}
                />
                <ChecklistItem
                    label="Patente RETRO"
                    done={patenteRetro}
                    warn={patenteFronte && !patenteRetro}
                    note={patenteRetro ? "Retro caricato — click per aprire" : "Retro patente mancante — click per caricare"}
                    testid={`c-pat-retro-${b.id}`}
                    href={docHref("patente_retro", patenteRetro)}
                    onClick={patenteRetro ? undefined : missingClick("patente_retro")}
                />
                <ChecklistItem
                    label={cfImg ? "CF foto" : cfText ? "CF testo" : "CF"}
                    done={cfOk}
                    warn={cfText && !cfImg}
                    note={cfImg ? "Foto CF caricata — click per aprire" : cfText ? "CF dichiarato (senza foto) — click per caricare foto" : "CF mancante — click per aggiungere"}
                    testid={`c-cf-${b.id}`}
                    href={docHref("cf", cfImg)}
                    onClick={cfImg ? undefined : missingClick("cf")}
                />
                <ChecklistItem
                    label={isAdminOverride ? "Manleva (override)" : "Manleva firmata"}
                    done={manlevaOk}
                    note={manlevaOk ? `Firmata ${fmtShort(b.manleva_signed_at)} — click per PDF firmato` : "Manleva non firmata — usa Colma lacune"}
                    testid={`c-man-${b.id}`}
                    href={manlevaOk ? singleDocHref("manleva") : null}
                    onClick={manlevaOk ? undefined : missingClick("manleva")}
                />
                <ChecklistItem
                    label="Privacy/GDPR"
                    done={gdpr}
                    warn={manlevaOk && !gdpr}
                    note={gdpr ? "Consenso GDPR accettato — click per PDF firmato" : "GDPR non accettato"}
                    testid={`c-gdpr-${b.id}`}
                    href={gdpr ? singleDocHref("privacy") : null}
                />
                <ChecklistItem
                    label="Cond. Commerciali"
                    done={paid}
                    note={paid ? "Accettate al checkout Stripe — click per PDF firmato con clausole ex art. 1341 c.c." : "Non ancora accettate"}
                    testid={`c-cond-${b.id}`}
                    href={paid ? singleDocHref("condizioni") : null}
                />
                <ChecklistItem
                    label="Maggiorenne"
                    done={magg}
                    warn={manlevaOk && !magg}
                    note={magg ? "Dichiarazione maggiore età OK — click per PDF firmato" : "Non dichiarato"}
                    testid={`c-magg-${b.id}`}
                    href={magg ? singleDocHref("maggiorenne") : null}
                />
                <ChecklistItem label="Foto/Video" done={immagini} warn={false} note={immagini ? "Consenso uso immagini ok" : "Consenso immagini NO"} testid={`c-img-${b.id}`} />
                <ChecklistItem label="WhatsApp Mkt" done={waMkt} warn={false} note={waMkt ? "Consenso WhatsApp marketing ✓" : "Nessun consenso WhatsApp"} testid={`c-wa-${b.id}`} />
                {fattura ? (
                    <ChecklistItem label={b.fattura_emessa ? "Fattura emessa" : "Fattura richiesta"} done={b.fattura_emessa} warn={!b.fattura_emessa} note={b.fattura_emessa ? "Emessa" : "Da emettere"} testid={`c-fat-${b.id}`} />
                ) : (
                    <ChecklistItem label="Ricevuta" done={paid} note="Nessuna fattura richiesta" testid={`c-ric-${b.id}`} />
                )}
            </div>
            {showPayment && <PaymentInfoModal bookingId={b.id} token={token} onClose={() => setShowPayment(false)} />}
        </>
    );
}

// --- Bottone Crea Prenotazione TEST ---
function CreateTestBookingButton({ token, onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [product, setProduct] = useState("singola_evo");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");

    const create = async () => {
        setBusy(true);
        try {
            const r = await adminCreateTestBooking(token, {
                product_id: product,
                customer_name: name || undefined,
                customer_email: email || undefined,
            });
            toast.success(`Booking TEST creato — voucher ${r.voucher_code}`);
            const tunnelUrl = `${window.location.origin}/manleva/${r.id}?token=${r.access_token}`;
            try { await navigator.clipboard?.writeText(tunnelUrl); toast.info("Link tunnel copiato negli appunti"); }
            catch { toast.info(`Link tunnel: ${tunnelUrl}`); }
            setOpen(false);
            setName(""); setEmail("");
            onDone?.();
        } catch { toast.error("Errore creazione TEST"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="btn-create-test-booking"
                    className="border border-[#f59e0b]/60 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"
                    title="Crea prenotazione TEST (non consuma capacità)"
                ><TestTube2 size={14} /> Booking TEST</button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C2026] border-[#9AA2AD]/20 text-[#F4F2ED] max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase text-2xl">Crea Booking TEST</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-[#9AA2AD]">Non consuma capacità evento. Nessun pagamento Stripe. Utile per testare tunnel/manleva/email.</p>
                <div className="space-y-3 py-2">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD] block mb-1">Formula</label>
                        <select data-testid="test-product" value={product} onChange={e => setProduct(e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm uppercase">
                            <option value="singola_evo">Singola EVO</option>
                            <option value="singola_wrx">Singola WRX</option>
                            <option value="doppia">Doppia</option>
                            <option value="passenger_experience">Passenger Experience</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD] block mb-1">Nome (opz.)</label>
                        <input data-testid="test-name" value={name} onChange={e => setName(e.target.value)} placeholder="TEST Cliente" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD] block mb-1">Email (opz.)</label>
                        <input data-testid="test-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="test+xyz@velorajdm.com" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm" />
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid="test-cancel" onClick={() => setOpen(false)} className="border border-[#9AA2AD]/30 px-4 py-2 text-xs uppercase tracking-widest">Annulla</button>
                    <button data-testid="test-create-confirm" onClick={create} disabled={busy} className="bg-[#f59e0b] hover:bg-[#d97706] text-[#15181C] px-4 py-2 text-xs uppercase tracking-widest font-display disabled:opacity-50">{busy ? "Creazione…" : "Crea TEST"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// --- D-Day Live Dashboard ---
function DDayLiveView({ token }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [autorefresh, setAutorefresh] = useState(true);
    const [gateAlert, setGateAlert] = useState(null); // { voucher, time }

    const load = async () => {
        try {
            const d = await adminDdayLive(token);
            setData(d);
        } catch { toast.error("Errore caricamento D-Day"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [token]);
    useEffect(() => {
        if (!autorefresh) return;
        const iv = setInterval(load, 15000);
        return () => clearInterval(iv);
    }, [autorefresh, token]);

    const beep = (freq = 880, dur = 200) => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const o = ctx.createOscillator(); const g = ctx.createGain();
            o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
            g.gain.value = 0.15; o.start(); setTimeout(() => { o.stop(); ctx.close(); }, dur);
        } catch { /* ignore */ }
    };

    const doPrelim = async (r) => {
        if (!r.has_license || !r.manleva_signed) {
            toast.error(`${r.voucher_code}: manca ${!r.has_license ? "patente" : ""}${!r.has_license && !r.manleva_signed ? " e " : ""}${!r.manleva_signed ? "firma manleva" : ""}`);
            beep(220, 300);
            return;
        }
        const wristband = window.confirm(`${r.voucher_code} · ${r.customer_name}\n\nConfermi ACCETTAZIONE e consegna del braccialetto?\n\n[OK] = accettato + braccialetto consegnato\n[Annulla] = solo accettazione, senza braccialetto`);
        setBusy(r.id);
        try {
            await adminCheckinPrelim(token, r.id, wristband);
            toast.success(`${r.voucher_code} accettato${wristband ? " · 🎗 braccialetto consegnato" : ""}`);
            beep(880, 150);
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Errore";
            toast.error(msg);
            beep(220, 400);
        } finally { setBusy(null); }
    };

    const doGate = async (r) => {
        setBusy(r.id);
        try {
            const res = await adminCheckinGate(token, r.id);
            if (res.already) {
                setGateAlert({ voucher: r.voucher_code, name: r.customer_name, time: fmtShort(res.gate_at) });
                beep(220, 800);
            } else {
                toast.success(`🏁 ${r.voucher_code} IMBARCATO — buona pista!`);
                beep(1200, 250);
                setTimeout(() => beep(1600, 250), 150);
            }
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Errore";
            toast.error(msg);
            beep(220, 400);
        } finally { setBusy(null); }
    };

    const doReset = async (r, stage) => {
        if (!window.confirm(`Reset ${stage === "gate" ? "imbarco" : "accettazione"} per ${r.voucher_code}?${stage === "prelim" ? "\n\nATTENZIONE: annulla anche eventuale imbarco (cascata)." : ""}`)) return;
        setBusy(r.id);
        try {
            await adminCheckinReset(token, r.id, stage);
            toast.info(`Reset ${stage} · ${r.voucher_code}`);
            await load();
        } catch { toast.error("Errore reset"); }
        finally { setBusy(null); }
    };

    if (loading || !data) return <div className="text-center py-12 text-[#9AA2AD]">Caricamento D-Day…</div>;

    const total = data.total || 0;
    const inArrivo = data.in_arrivo || [];
    const accettati = data.accettati || [];
    const imbarcati = data.imbarcati || [];
    const pct = total > 0 ? Math.round((imbarcati.length / total) * 100) : 0;

    return (
        <div className="space-y-6" data-testid="dday-live-view">
            {gateAlert && (
                <div className="fixed inset-0 z-50 bg-[#E2231A]/95 flex items-center justify-center p-4 backdrop-blur" data-testid="gate-alert-overlay">
                    <div className="bg-[#15181C] border-4 border-[#F4F2ED] p-8 text-center max-w-md">
                        <AlertTriangle size={64} className="mx-auto mb-4 text-[#F4F2ED]" />
                        <div className="font-display text-4xl uppercase mb-2 text-[#F4F2ED]">⛔ Già imbarcato</div>
                        <div className="text-lg mb-2 text-[#F4F2ED]">{gateAlert.voucher} · {gateAlert.name}</div>
                        <div className="text-sm text-[#F4F2ED]/80 mb-6">Aveva già completato l'imbarco alle <b>{gateAlert.time}</b>.</div>
                        <button data-testid="gate-alert-dismiss" onClick={() => setGateAlert(null)} className="bg-[#F4F2ED] text-[#15181C] px-6 py-3 uppercase tracking-widest font-display">Chiudi</button>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-4 gap-3">
                <div className="border border-[#9AA2AD]/40 bg-[#1C2026] p-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] flex items-center gap-2"><Users size={14} /> In arrivo</div>
                    <div className="font-display text-5xl mt-2" data-testid="dday-in-arrivo-count">{inArrivo.length}</div>
                </div>
                <div className="border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] flex items-center gap-2"><Check size={14} /> Accettati</div>
                    <div className="font-display text-5xl mt-2 text-[#f59e0b]" data-testid="dday-accettati-count">{accettati.length}</div>
                </div>
                <div className="border border-[#16a34a]/40 bg-[#16a34a]/5 p-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#16a34a] flex items-center gap-2"><Car size={14} /> Imbarcati</div>
                    <div className="font-display text-5xl mt-2 text-[#16a34a]" data-testid="dday-imbarcati-count">{imbarcati.length}</div>
                </div>
                <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] flex items-center gap-2"><Activity size={14} /> Completati</div>
                    <div className="font-display text-5xl mt-2">{pct}%</div>
                    <div className="mt-2 h-1 bg-[#15181C] overflow-hidden">
                        <div className="h-full bg-[#16a34a] transition-all duration-300" style={{ width: `${pct}%` }} />
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#9AA2AD] cursor-pointer">
                    <input type="checkbox" checked={autorefresh} onChange={e => setAutorefresh(e.target.checked)} data-testid="dday-autorefresh" />
                    Auto-refresh 15s
                </label>
                <button data-testid="dday-refresh" onClick={load} className="border border-[#9AA2AD]/30 hover:border-[#E2231A] px-3 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-2"><RotateCcw size={12} /> Aggiorna</button>
                <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]/70 ml-auto">
                    Flusso: <b className="text-[#9AA2AD]">In arrivo</b> → Hostess 1 → <b className="text-[#f59e0b]">Accettati (+braccialetto)</b> → Hostess 2 → <b className="text-[#16a34a]">Imbarcati</b>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <DDayColumn
                    title="In arrivo"
                    subtitle="Hostess 1 · Accettazione"
                    color="#9AA2AD"
                    rows={inArrivo}
                    emptyMsg={total === 0 ? "Nessuna prenotazione." : "Nessuno in attesa 👌"}
                    testid="dday-col-in-arrivo"
                    renderAction={(r) => (
                        <button
                            data-testid={`btn-prelim-${r.id}`}
                            onClick={() => doPrelim(r)}
                            disabled={busy === r.id || !r.has_license || !r.manleva_signed}
                            title={!r.has_license || !r.manleva_signed ? "Blocco: manca patente o firma manleva" : "Accetta e consegna braccialetto"}
                            className="bg-[#f59e0b] hover:bg-[#d97706] disabled:bg-[#9AA2AD]/20 disabled:text-[#9AA2AD] text-[#15181C] px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:cursor-not-allowed flex items-center gap-1"
                        ><Check size={12} /> Accetta</button>
                    )}
                />
                <DDayColumn
                    title="Accettati"
                    subtitle="Hostess 2 · Gate imbarco"
                    color="#f59e0b"
                    rows={accettati}
                    emptyMsg="Nessuno in coda al Gate"
                    testid="dday-col-accettati"
                    renderMeta={(r) => r.wristband_given && <span className="text-[#f59e0b]">🎗 braccialetto</span>}
                    renderAction={(r) => (
                        <div className="flex flex-col gap-1">
                            <button
                                data-testid={`btn-gate-${r.id}`}
                                onClick={() => doGate(r)}
                                disabled={busy === r.id}
                                className="bg-[#16a34a] hover:bg-[#15803d] text-[#F4F2ED] px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-50 flex items-center gap-1"
                            ><Car size={12} /> Imbarca</button>
                            <button
                                data-testid={`btn-reset-prelim-${r.id}`}
                                onClick={() => doReset(r, "prelim")}
                                className="border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] hover:bg-[#E2231A]/5 text-[#9AA2AD] px-2 py-1 text-[9px] uppercase tracking-widest flex items-center gap-1 whitespace-nowrap"
                                title="Errore? Annulla l'accettazione e riporta questo cliente in colonna 'In arrivo'"
                            ><RotateCcw size={10} /> Annulla accettazione</button>
                        </div>
                    )}
                />
                <DDayColumn
                    title="Imbarcati"
                    subtitle="In pista o completati"
                    color="#16a34a"
                    rows={imbarcati}
                    emptyMsg="Nessuno ancora in pista"
                    testid="dday-col-imbarcati"
                    renderMeta={(r) => r.gate_at && <span className="text-[#16a34a]">🏁 {fmtShort(r.gate_at)}</span>}
                    renderAction={(r) => (
                        <button
                            data-testid={`btn-reset-gate-${r.id}`}
                            onClick={() => doReset(r, "gate")}
                            className="border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] hover:bg-[#E2231A]/5 text-[#9AA2AD] px-2 py-1.5 text-[9px] uppercase tracking-widest flex items-center gap-1 whitespace-nowrap"
                            title="Errore? Annulla l'imbarco e riporta questo cliente in colonna 'Accettati'"
                        ><RotateCcw size={10} /> Annulla imbarco</button>
                    )}
                />
            </div>
        </div>
    );
}

function DDayColumn({ title, subtitle, color, rows, emptyMsg, testid, renderMeta, renderAction }) {
    const copyPass = async (voucher) => {
        const url = `${window.location.origin}/pass/${voucher}`;
        try { await navigator.clipboard.writeText(url); toast.success(`Link Pass ${voucher} copiato`); }
        catch { toast.info(url); }
    };
    return (
        <div className="border" style={{ borderColor: `${color}55` }} data-testid={testid}>
            <div className="px-4 py-2 flex items-baseline justify-between" style={{ backgroundColor: `${color}15` }}>
                <div>
                    <div className="text-xs uppercase tracking-widest font-display" style={{ color }}>{title} · {rows.length}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">{subtitle}</div>
                </div>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
                {rows.length === 0 && <div className="p-6 text-center text-[#9AA2AD] text-xs">{emptyMsg}</div>}
                {rows.map(r => (
                    <div key={r.id} className="border-t border-[#9AA2AD]/10 p-3 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display text-2xl leading-none" style={{ color }}>#{r.turno_progressivo}</span>
                                <button
                                    onClick={() => copyPass(r.voucher_code)}
                                    className="font-display text-base bg-[#15181C] border border-[#9AA2AD]/40 hover:border-[#E2231A] px-2 py-0.5 hover:text-[#E2231A] transition-colors duration-150"
                                    title="Copia link Boarding Pass — invialo al cliente via WhatsApp"
                                    data-testid={`dday-copy-pass-${r.id}`}
                                >{r.voucher_code}</button>
                                {r.planned_time && <span className="text-[10px] px-1.5 py-0.5 border border-[#9AA2AD]/40 uppercase tracking-widest">{r.planned_time}</span>}
                            </div>
                            <div className="text-sm text-[#F4F2ED] truncate mt-1">{r.customer_name}</div>
                            <div className="text-[10px] text-[#9AA2AD] uppercase tracking-widest flex gap-2 flex-wrap mt-0.5">
                                <span>{r.product_id}</span>
                                {r.assigned_car && <span className={r.assigned_car === "EVO" ? "text-[#E2231A]" : "text-[#4B7BEA]"}>→ {r.assigned_car}</span>}
                                {!r.has_license && <span className="text-[#f59e0b]">no patente</span>}
                                {!r.manleva_signed && <span className="text-[#f59e0b]">no manleva</span>}
                                {renderMeta && renderMeta(r)}
                            </div>
                        </div>
                        {renderAction(r)}
                    </div>
                ))}
            </div>
        </div>
    );
}




// --- Media cell in tabella prenotazioni: semaforo + azioni distribuzione ---
function MediaCell({ token, booking, onDone }) {
    const [busy, setBusy] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const purchased = booking.addon_foto || booking.addon_video || booking.addon_media_bundle || booking.media_upsell_purchased;
    const total = (booking.media_photos_count || 0) + (booking.media_videos_count || 0);
    const delivered = !!booking.media_delivered_at;

    let color = "#9AA2AD", label = "—";
    if (total === 0) { color = "#E2231A"; label = "Vuoto"; }
    else if (!delivered) { color = "#f59e0b"; label = `${total} pronti`; }
    else { color = "#16a34a"; label = `${total} inviati`; }

    const passUrl = `${window.location.origin}/pass/${booking.voucher_code}`;
    const openPass = () => window.open(passUrl, "_blank");
    const copyPass = async () => {
        try { await navigator.clipboard.writeText(passUrl); toast.success("Link Pass copiato"); }
        catch { toast.info(passUrl); }
    };

    const sendEmail = async (template) => {
        setBusy(true);
        try {
            const r = await adminMediaDeliver(token, booking.id, true, template);
            if (r?.email_result?.ok) toast.success(`Email "${template}" inviata`);
            else toast.error(r?.email_result?.hint || r?.email_result?.reason || "Errore invio");
            setMenuOpen(false);
            onDone?.();
        } catch { toast.error("Errore rete"); }
        finally { setBusy(false); }
    };

    const openWhatsApp = async (template) => {
        setBusy(true);
        try {
            const r = await adminWhatsAppLink(token, booking.id, template);
            window.open(r.wa_url, "_blank");
            toast.info("WhatsApp aperto — invia manualmente");
            setMenuOpen(false);
        } catch { toast.error("Errore link WhatsApp"); }
        finally { setBusy(false); }
    };

    return (
        <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" title={`${booking.media_photos_count || 0} foto · ${booking.media_videos_count || 0} video${purchased ? " · pacchetto comprato" : " · upsell"}`}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span style={{ color }}>{label}</span>
                {purchased ? <span className="text-[#16a34a]">🎥</span> : <span className="text-[#f59e0b]">💰</span>}
            </div>
            <button
                data-testid={`media-menu-${booking.id}`}
                onClick={() => setMenuOpen(v => !v)}
                className="mt-1 text-[9px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A]"
            >Azioni ▾</button>
            {menuOpen && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-[#1C2026] border border-[#9AA2AD]/30 w-52 shadow-lg">
                    <button onClick={openPass} className="block w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#E2231A]/20">Apri Pass</button>
                    <button onClick={copyPass} className="block w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#E2231A]/20">Copia link Pass</button>
                    <div className="border-t border-[#9AA2AD]/10" />
                    {purchased ? (
                        <>
                            <button onClick={() => sendEmail("media_ready")} disabled={busy} className="block w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#E2231A]/20 disabled:opacity-40">✉ Email HD pronti</button>
                            <button onClick={() => openWhatsApp("media_ready")} disabled={busy} className="block w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#25D366]/20 text-[#25D366]">📱 WhatsApp HD</button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => sendEmail("media_upsell")} disabled={busy} className="block w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#E2231A]/20 disabled:opacity-40">✉ Email proposta upsell</button>
                            <button onClick={() => openWhatsApp("media_upsell")} disabled={busy} className="block w-full text-left px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#25D366]/20 text-[#25D366]">📱 WhatsApp upsell</button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// --- Calcolatore dinamico slot Planning ---
// Logica: dato l'orario di inizio reale + durata slot + pausa pranzo, calcola gli orari per ogni turno.
// Se il totale sfora la finestra 09-13 + 14-18, alerta con quanti minuti mancano e suggerisce nuova durata.
function SlotCalculator({ rows, onApply }) {
    const [startTime, setStartTime] = useState("09:00");
    const [slotMin, setSlotMin] = useState(15);
    const [lunchStart, setLunchStart] = useState("13:00");
    const [lunchEnd, setLunchEnd] = useState("14:00");
    const [dayEnd, setDayEnd] = useState("18:00");

    // Parse HH:MM → minuti dal 00:00
    const parse = (s) => {
        const [h, m] = (s || "0:0").split(":").map(x => parseInt(x, 10) || 0);
        return h * 60 + m;
    };
    const fmt = (min) => {
        min = Math.max(0, Math.round(min));
        const h = Math.floor(min / 60), m = min % 60;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };

    // Genera slot ordinati per turno_progressivo, saltando pausa pranzo
    const slots = useMemo(() => {
        const sortedIds = [...rows].sort((a, b) => (a.turno_progressivo || 0) - (b.turno_progressivo || 0)).map(r => r.id);
        const result = {};
        let t = parse(startTime);
        const lStart = parse(lunchStart), lEnd = parse(lunchEnd), end = parse(dayEnd);
        for (const id of sortedIds) {
            // Se lo slot corrente cade nella pausa pranzo, salta a lunch_end
            if (t >= lStart && t < lEnd) t = lEnd;
            result[id] = fmt(t);
            t += slotMin;
        }
        return { slots: result, lastEnd: t, sortedIds };
    }, [rows, startTime, slotMin, lunchStart, lunchEnd, dayEnd]);

    const totalRows = rows.length;
    const lastEnd = slots.lastEnd;
    const end = parse(dayEnd);
    const overflow = lastEnd - end;
    const workMin = (parse(lunchStart) - parse(startTime)) + (end - parse(lunchEnd));
    // Regola di business: MAX 15 min per turno (l'auto va lasciata raffreddare, €79 non copre 1h ininterrotta)
    const MAX_SLOT_MIN = 15;
    const suggestedSlotRaw = totalRows > 0 ? Math.floor(workMin / totalRows) : slotMin;
    // Se in overbooking: taglia lo slot ma clamp a min 5 e max 15
    const suggestedSlot = Math.max(5, Math.min(MAX_SLOT_MIN, suggestedSlotRaw));

    // Preview ordinati per il rendering
    const preview = rows.length
        ? [...rows].sort((a, b) => (a.turno_progressivo || 0) - (b.turno_progressivo || 0)).map(r => ({ ...r, suggested: slots.slots[r.id] }))
        : [];

    const applySuggestion = () => {
        // Rispetta max 15 min (regola business auto)
        setSlotMin(Math.max(5, Math.min(15, suggestedSlot)));
    };

    const applyPlanned = () => {
        // Costruisci un array ordinato posizionale: slots[i] = suggerito per rows[i] (nell'ordine attuale)
        const arr = rows.map(r => slots.slots[r.id] || null);
        onApply(arr);
    };

    return (
        <div className="border border-[#4B7BEA]/40 bg-[#4B7BEA]/5 p-4" data-testid="slot-calculator">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#4B7BEA] font-display">🕒 Calcolatore Turni Dinamico</span>
                <span className="text-[10px] text-[#9AA2AD]">— {totalRows} turni · 09-13 + 14-18</span>
            </div>
            <div className="grid md:grid-cols-5 gap-2 mb-3">
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Inizio reale</div>
                    <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="slot-start-input" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Durata slot (min, max 15)</div>
                    <input type="number" min="5" max="15" value={slotMin} onChange={e => setSlotMin(Math.min(15, Math.max(5, parseInt(e.target.value) || 15)))} data-testid="slot-duration-input" className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Pausa da</div>
                    <input type="time" value={lunchStart} onChange={e => setLunchStart(e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Pausa a</div>
                    <input type="time" value={lunchEnd} onChange={e => setLunchEnd(e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Fine giornata</div>
                    <input type="time" value={dayEnd} onChange={e => setDayEnd(e.target.value)} className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-sm px-2 py-1.5" />
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3 mb-3">
                <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-3">
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Ultimo turno finisce alle</div>
                    <div className={`font-display text-2xl mt-1 ${overflow > 0 ? "text-[#E2231A]" : "text-[#16a34a]"}`} data-testid="slot-last-end">{fmt(lastEnd)}</div>
                </div>
                {overflow > 0 ? (
                    <div className="border border-[#E2231A]/60 bg-[#E2231A]/10 p-3">
                        <div className="text-[9px] uppercase tracking-widest text-[#E2231A]">⚠ OVERBOOKING</div>
                        <div className="font-display text-2xl text-[#E2231A] mt-1" data-testid="slot-overflow">+{overflow} min oltre {dayEnd}</div>
                        <div className="text-[10px] text-[#F4F2ED] mt-1">
                            {suggestedSlot < slotMin
                                ? <>Riduci a <b>{suggestedSlot} min</b>/turno (max 15) per rientrare</>
                                : <>Anche a slot minimo (5 min) non entra. <b>Serve più giornata</b> o annulla qualcuno.</>}
                        </div>
                    </div>
                ) : (
                    <div className="border border-[#16a34a]/40 bg-[#16a34a]/10 p-3">
                        <div className="text-[9px] uppercase tracking-widest text-[#16a34a]">✓ OK</div>
                        <div className="font-display text-2xl text-[#16a34a] mt-1">{-overflow} min di margine</div>
                        <div className="text-[10px] text-[#9AA2AD] mt-1">
                            {slotMin >= MAX_SLOT_MIN
                                ? "Slot al massimo (15 min). Margine per pause auto & imprevisti."
                                : <>Puoi allungare fino a <b>{MAX_SLOT_MIN} min</b>/turno (max fisiologico auto)</>}
                        </div>
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    {overflow > 0 && (
                        <button data-testid="slot-apply-suggestion" onClick={applySuggestion} className="border border-[#f59e0b] bg-[#f59e0b]/10 hover:bg-[#f59e0b] hover:text-[#15181C] text-[#f59e0b] px-3 py-2 text-[10px] uppercase tracking-widest">
                            Applica {suggestedSlot} min/turno
                        </button>
                    )}
                    <button data-testid="slot-apply-planned" onClick={applyPlanned} disabled={totalRows === 0} className="bg-[#4B7BEA] hover:bg-[#3866d1] text-[#F4F2ED] px-3 py-2 text-[10px] uppercase tracking-widest disabled:opacity-40">
                        Applica orari alla lista sotto
                    </button>
                </div>
            </div>

            <details className="border-t border-[#9AA2AD]/10 pt-2">
                <summary className="text-[10px] uppercase tracking-widest text-[#9AA2AD] cursor-pointer">Anteprima orari suggeriti (primi 15)</summary>
                <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-1 text-[10px]">
                    {preview.slice(0, 15).map(r => (
                        <div key={r.id} className="bg-[#15181C] border border-[#9AA2AD]/20 px-2 py-1 flex items-center gap-1">
                            <span className="text-[#4B7BEA] font-display">#{r.turno_progressivo}</span>
                            <span className="font-mono ml-auto">{r.suggested}</span>
                        </div>
                    ))}
                </div>
            </details>
        </div>
    );
}



// ═══════════════════════════════════════════════════════════════════════════
// MarketingView — Tab "Marketing" nell'admin
// Editable: Pixel ID · Verifica dominio Meta · Link funnel · Social · Creative
// ═══════════════════════════════════════════════════════════════════════════
function MarketingView({ token }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pixelId, setPixelId] = useState("");
    const [domainVerification, setDomainVerification] = useState("");
    const [funnels, setFunnels] = useState([]);
    const [socials, setSocials] = useState([]);
    const [creatives, setCreatives] = useState([]);
    const [notes, setNotes] = useState("");

    const load = async () => {
        try {
            const d = await adminGetMarketing(token);
            setData(d);
            setPixelId(d.settings.pixel_id || "");
            setDomainVerification(d.settings.meta_domain_verification || "");
            setFunnels(d.settings.funnel_links || []);
            setSocials(d.settings.social_links || []);
            setCreatives(d.settings.ads_creatives || []);
            setNotes(d.settings.ads_notes || "");
        } catch (e) {
            toast.error("Errore caricamento marketing settings");
        }
    };
    useEffect(() => { load(); }, [token]);

    const save = async (fields) => {
        setBusy(true);
        try {
            await adminUpdateMarketing(token, fields);
            toast.success("Salvato");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore salvataggio");
        } finally { setBusy(false); }
    };

    const copyToClipboard = (text) => {
        try {
            navigator.clipboard.writeText(text);
            toast.success("Copiato negli appunti");
        } catch { toast.error("Copia non riuscita"); }
    };

    if (!data) return <div className="text-[#9AA2AD] text-sm py-8">Caricamento…</div>;

    const publicOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const pixelInstalled = !!(pixelId && pixelId.length >= 10);
    const stats = data.stats || {};
    const revenueEur = ((stats.voucher_revenue_cents || 0) / 100).toFixed(2);

    return (
        <div className="space-y-6" data-testid="marketing-view">
            {/* ═══════ BLOCCO 1: META PIXEL ═══════ */}
            <section className="border-l-4 border-[#E2231A] bg-[#1C2026] p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-display text-lg uppercase tracking-widest">📊 Meta Pixel (Facebook / Instagram Ads)</h3>
                        <p className="text-xs text-[#9AA2AD] mt-1">Il Pixel ID va inserito qui — l'app lo legge dinamicamente, no bisogno di ridoployare</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${pixelInstalled ? "border-[#16a34a] text-[#16a34a]" : "border-[#E2231A] text-[#E2231A]"}`}>
                        {pixelInstalled ? "✓ Attivo" : "✗ Non installato"}
                    </span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Pixel ID (15-16 cifre)</label>
                        <div className="flex gap-2">
                            <input
                                data-testid="input-pixel-id"
                                value={pixelId}
                                onChange={e => setPixelId(e.target.value.replace(/\D/g, ""))}
                                placeholder="789456123456789"
                                className="flex-1 bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm font-mono"
                            />
                            <button
                                data-testid="btn-save-pixel"
                                onClick={() => save({ pixel_id: pixelId })}
                                disabled={busy}
                                className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-widest"
                            >{busy ? "…" : "Salva"}</button>
                        </div>
                        <p className="text-[9px] text-[#9AA2AD] mt-1">Attivo dopo il prossimo caricamento pagina</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Verifica Dominio Meta (content del meta-tag)</label>
                        <div className="flex gap-2">
                            <input
                                data-testid="input-domain-verification"
                                value={domainVerification}
                                onChange={e => setDomainVerification(e.target.value)}
                                placeholder='abc123def456xyz789  o  <meta content="..." />'
                                className="flex-1 bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-3 py-2 text-sm font-mono"
                            />
                            <button
                                data-testid="btn-save-dv"
                                onClick={() => save({ meta_domain_verification: domainVerification })}
                                disabled={busy}
                                className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-widest"
                            >{busy ? "…" : "Salva"}</button>
                        </div>
                        <p className="text-[9px] text-[#9AA2AD] mt-1">Iniettato in {"<head>"} come meta tag facebook-domain-verification</p>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                    <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="uppercase tracking-widest border border-[#4B7BEA]/40 text-[#4B7BEA] hover:bg-[#4B7BEA]/10 px-3 py-1.5">🔗 Meta Business Suite</a>
                    <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noopener noreferrer" className="uppercase tracking-widest border border-[#4B7BEA]/40 text-[#4B7BEA] hover:bg-[#4B7BEA]/10 px-3 py-1.5">📈 Gestione Eventi</a>
                    <a href="https://business.facebook.com/settings/domains" target="_blank" rel="noopener noreferrer" className="uppercase tracking-widest border border-[#4B7BEA]/40 text-[#4B7BEA] hover:bg-[#4B7BEA]/10 px-3 py-1.5">🌐 Sicurezza Brand · Domini</a>
                </div>
            </section>

            {/* ═══════ BLOCCO 2: STATISTICHE FUNNEL ═══════ */}
            <section className="border-l-4 border-[#16a34a] bg-[#1C2026] p-5">
                <h3 className="font-display text-lg uppercase tracking-widest mb-4">💰 Statistiche Funnel</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Voucher Venduti" value={stats.vouchers_sold || 0} testid="stat-vouchers-sold" />
                    <StatCard label="Voucher Riscattati" value={stats.vouchers_redeemed || 0} testid="stat-vouchers-redeemed" />
                    <StatCard label="Tasso Conversione" value={`${stats.voucher_conversion_pct || 0}%`} testid="stat-voucher-conversion" />
                    <StatCard label="Revenue Voucher" value={`€${revenueEur}`} highlight testid="stat-voucher-revenue" />
                </div>
                {stats.booking_breakdown && stats.booking_breakdown.length > 0 && (
                    <div className="mt-5">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">Bestseller prenotazioni</div>
                        <div className="flex flex-wrap gap-2">
                            {stats.booking_breakdown.slice(0, 8).map(b => (
                                <div key={b.product_id} className="border border-[#9AA2AD]/20 px-3 py-1.5 text-xs">
                                    <span className="uppercase font-mono text-[10px] text-[#9AA2AD]">{b.product_id || "?"}</span>
                                    <span className="ml-2 text-[#E2231A] font-bold">{b.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {/* ═══════ BLOCCO 3: LINK FUNNEL ═══════ */}
            <section className="border-l-4 border-[#4B7BEA] bg-[#1C2026] p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg uppercase tracking-widest">🎯 Link Funnel & Landing</h3>
                    <button
                        onClick={() => save({ funnel_links: funnels })}
                        disabled={busy}
                        data-testid="btn-save-funnels"
                        className="text-xs uppercase tracking-widest border border-[#E2231A] hover:bg-[#E2231A]/10 text-[#F4F2ED] px-3 py-1.5 disabled:opacity-40"
                    >💾 Salva modifiche</button>
                </div>
                <div className="space-y-3">
                    {funnels.map((f, idx) => {
                        const fullUrl = `${publicOrigin}${f.path}`;
                        return (
                            <div key={f.key} className="border border-[#9AA2AD]/20 bg-[#15181C] p-3" data-testid={`funnel-${f.key}`}>
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <input
                                        value={f.label}
                                        onChange={e => setFunnels(funnels.map((x, i) => i === idx ? {...x, label: e.target.value} : x))}
                                        className="flex-1 min-w-[200px] bg-transparent border-b border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-1 py-1 text-sm font-display uppercase tracking-widest"
                                    />
                                    <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#9AA2AD] cursor-pointer">
                                        <input type="checkbox" checked={f.enabled !== false} onChange={e => setFunnels(funnels.map((x, i) => i === idx ? {...x, enabled: e.target.checked} : x))} className="accent-[#E2231A]" />
                                        {f.enabled !== false ? "Attivo" : "Nascosto"}
                                    </label>
                                </div>
                                <div className="flex flex-wrap gap-2 items-center mb-2">
                                    <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Path:</span>
                                    <input
                                        value={f.path}
                                        onChange={e => setFunnels(funnels.map((x, i) => i === idx ? {...x, path: e.target.value.startsWith("/") ? e.target.value : "/" + e.target.value} : x))}
                                        className="flex-1 min-w-[150px] bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-2 py-1 text-xs font-mono"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-2 items-center mb-2 text-xs text-[#F4F2ED]">
                                    <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">URL:</span>
                                    <code className="flex-1 min-w-[200px] font-mono text-xs text-[#4B7BEA] break-all">{fullUrl}</code>
                                    <button onClick={() => copyToClipboard(fullUrl)} data-testid={`btn-copy-${f.key}`} className="text-[10px] uppercase tracking-widest border border-[#9AA2AD]/30 hover:border-[#E2231A] hover:text-[#E2231A] px-2 py-1">📋 Copia</button>
                                    <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest border border-[#16a34a]/40 text-[#16a34a] hover:bg-[#16a34a]/10 px-2 py-1">🔗 Apri</a>
                                </div>
                                <input
                                    value={f.description || ""}
                                    onChange={e => setFunnels(funnels.map((x, i) => i === idx ? {...x, description: e.target.value} : x))}
                                    placeholder="Breve descrizione..."
                                    className="w-full bg-transparent border-b border-[#9AA2AD]/10 focus:border-[#E2231A] outline-none px-1 py-1 text-xs text-[#9AA2AD]"
                                />
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ═══════ BLOCCO 4: SOCIAL LINKS ═══════ */}
            <section className="border-l-4 border-[#E4405F] bg-[#1C2026] p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg uppercase tracking-widest">📱 Account Social</h3>
                    <button
                        onClick={() => save({ social_links: socials })}
                        disabled={busy}
                        data-testid="btn-save-socials"
                        className="text-xs uppercase tracking-widest border border-[#E2231A] hover:bg-[#E2231A]/10 text-[#F4F2ED] px-3 py-1.5 disabled:opacity-40"
                    >💾 Salva modifiche</button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    {socials.map((s, idx) => (
                        <div key={s.key} className="border border-[#9AA2AD]/20 bg-[#15181C] p-3">
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">{s.label}</div>
                            <input
                                value={s.handle || ""}
                                onChange={e => setSocials(socials.map((x, i) => i === idx ? {...x, handle: e.target.value} : x))}
                                placeholder="@handle"
                                className="w-full mb-1 bg-transparent border-b border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-1 py-1 text-sm"
                            />
                            <input
                                value={s.url || ""}
                                onChange={e => setSocials(socials.map((x, i) => i === idx ? {...x, url: e.target.value} : x))}
                                placeholder="URL completo (https://...)"
                                className="w-full bg-transparent border-b border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-1 py-1 text-xs font-mono text-[#4B7BEA]"
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* ═══════ BLOCCO 5: CREATIVE & COPY ═══════ */}
            <section className="border-l-4 border-[#f59e0b] bg-[#1C2026] p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-lg uppercase tracking-widest">✍️ Creative & Copy Ads</h3>
                    <button
                        onClick={() => save({ ads_creatives: creatives })}
                        disabled={busy}
                        data-testid="btn-save-creatives"
                        className="text-xs uppercase tracking-widest border border-[#E2231A] hover:bg-[#E2231A]/10 text-[#F4F2ED] px-3 py-1.5 disabled:opacity-40"
                    >💾 Salva modifiche</button>
                </div>
                <div className="space-y-3">
                    {creatives.map((c, idx) => (
                        <div key={c.key} className="border border-[#9AA2AD]/20 bg-[#15181C] p-3">
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">{c.label}</div>
                                <button onClick={() => copyToClipboard(c.text)} className="text-[10px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A]">📋 Copia</button>
                            </div>
                            <textarea
                                value={c.text || ""}
                                onChange={e => setCreatives(creatives.map((x, i) => i === idx ? {...x, text: e.target.value} : x))}
                                rows={2}
                                className="w-full bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-2 py-1.5 text-sm resize-none"
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* ═══════ BLOCCO 6: NOTE PERSONALI ═══════ */}
            <section className="border-l-4 border-[#9AA2AD]/40 bg-[#1C2026] p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-lg uppercase tracking-widest">📝 Note Marketing (private)</h3>
                    <button
                        onClick={() => save({ ads_notes: notes })}
                        disabled={busy}
                        data-testid="btn-save-notes"
                        className="text-xs uppercase tracking-widest border border-[#E2231A] hover:bg-[#E2231A]/10 text-[#F4F2ED] px-3 py-1.5 disabled:opacity-40"
                    >💾 Salva</button>
                </div>
                <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={5}
                    placeholder="Idee campagne, budget mensile, link Higgsfield, palette colori, appunti riunioni..."
                    className="w-full bg-[#15181C] border border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-3 py-2 text-sm resize-y"
                />
                <div className="text-[9px] text-[#9AA2AD] mt-1">
                    Ultima modifica: {data.settings.updated_at ? new Date(data.settings.updated_at).toLocaleString("it-IT") : "mai"}
                </div>
            </section>
        </div>
    );
}

function StatCard({ label, value, highlight = false, testid }) {
    const { eventLabel } = useEventScope();
    label = `${label} · ${eventLabel}`;
    return (
        <div className={`border ${highlight ? "border-[#E2231A]/40 bg-[#E2231A]/5" : "border-[#9AA2AD]/20 bg-[#15181C]"} p-3`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">{label}</div>
            <div className={`font-display text-2xl ${highlight ? "text-[#E2231A]" : "text-[#F4F2ED]"}`}>{value}</div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// V48.1 · ManualBookingModal — Prenotazione manuale con pagamento Cash/Bonifico (bypass Stripe)
// ═══════════════════════════════════════════════════════════════════════════
const PRODUCT_PRICES = {
    passeggero: 3900, singola_evo: 9900, singola_wrx: 9900, combo_duello: 15800,
};
function ManualBookingModal({ token, onClose, onSaved }) {
    const [f, setF] = useState({
        nome: "", cognome: "", email: "", telefono: "", codice_fiscale: "",
        indirizzo: "", citta: "", cap: "",
        product_id: "singola_evo", amount_cents: 9900,
        payment_method: "cash", payment_notes: "",
        booking_reason: "normale", omaggio_reason: "",   // V52.8 · motivo omaggio (sponsor/mediapress/staff/pilota/altro)
        preferenza_auto: null, preferenza_fascia: null,
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    const isOmaggio = f.payment_method === "omaggio";

    const setProduct = (pid) => {
        setF(prev => ({ ...prev, product_id: pid, amount_cents: prev.payment_method === "omaggio" ? 0 : (PRODUCT_PRICES[pid] || prev.amount_cents) }));
    };

    // V52.8 · Quando cambia payment_method a omaggio → azzera importo e setta booking_reason
    const setPaymentMethod = (v) => {
        setF(prev => ({
            ...prev,
            payment_method: v,
            amount_cents: v === "omaggio" ? 0 : (prev.amount_cents || PRODUCT_PRICES[prev.product_id] || 9900),
            booking_reason: v === "omaggio" ? `omaggio_${prev.omaggio_reason || "generico"}` : "normale",
        }));
    };
    const setOmaggioReason = (v) => {
        setF(prev => ({ ...prev, omaggio_reason: v, booking_reason: prev.payment_method === "omaggio" ? `omaggio_${v || "generico"}` : prev.booking_reason }));
    };

    const save = async () => {
        if (!f.nome || !f.cognome || !f.email) { toast.error("Nome, cognome ed email obbligatori"); return; }
        if (!isOmaggio && (!f.amount_cents || f.amount_cents < 100)) { toast.error("Importo non valido"); return; }
        if (isOmaggio && !f.omaggio_reason) { toast.error("Seleziona un motivo omaggio (es. sponsor)"); return; }
        setBusy(true);
        try {
            // Rimuove il campo omaggio_reason locale prima di inviare (il backend usa booking_reason)
            const { omaggio_reason: _om, ...payload } = f;
            const r = await adminCreateManualBooking(token, payload);
            toast.success(r.omaggio ? `🎁 Omaggio creato! Voucher ${r.voucher}` : `Prenotazione creata! Voucher ${r.voucher}`);
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="manual-booking-modal">
            <div className="bg-[#15181C] border border-[#4B7BEA]/40 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#9AA2AD]/20 p-4 flex items-center justify-between z-10">
                    <div>
                        <h3 className="font-display text-lg text-[#F4F2ED]">➕ Nuova Prenotazione Manuale</h3>
                        <div className="text-[10px] text-[#9AA2AD]">Bypass Stripe · Contanti / POS / Bonifico / <span className="text-[#f59e0b]">Omaggio</span> (fuori revenue)</div>
                    </div>
                    <button onClick={onClose} className="text-[#9AA2AD] hover:text-[#F4F2ED]"><X size={18} /></button>
                </div>
                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1 border-b border-[#9AA2AD]/10 pb-1">Anagrafica cliente</div></div>
                        <MField label="Nome *" value={f.nome} onChange={v => set("nome", v)} tid="mb-nome" />
                        <MField label="Cognome *" value={f.cognome} onChange={v => set("cognome", v)} tid="mb-cognome" />
                        <MField label="Email *" type="email" value={f.email} onChange={v => set("email", v)} tid="mb-email" />
                        <MField label="Telefono" value={f.telefono} onChange={v => set("telefono", v)} tid="mb-tel" />
                        <MField label="Codice Fiscale" value={f.codice_fiscale} onChange={v => set("codice_fiscale", v.toUpperCase())} mono tid="mb-cf" />
                        <MField label="Indirizzo" value={f.indirizzo} onChange={v => set("indirizzo", v)} tid="mb-via" />
                        <MField label="Città" value={f.citta} onChange={v => set("citta", v)} tid="mb-citta" />
                        <MField label="CAP" value={f.cap} onChange={v => set("cap", v)} tid="mb-cap" />

                        <div className="col-span-2 pt-2"><div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1 border-b border-[#9AA2AD]/10 pb-1">Pacchetto & Pagamento</div></div>
                        <MField label="Pacchetto" select
                            options={[
                                { v: "passeggero", l: "Passeggero (€39)" },
                                { v: "singola_evo", l: "Singola EVO X (€99)" },
                                { v: "singola_wrx", l: "Singola WRX (€99)" },
                                { v: "combo_duello", l: "Combo Duello (€158)" },
                            ]}
                            value={f.product_id} onChange={setProduct} tid="mb-product" />
                        <MField label={isOmaggio ? "Importo (auto = €0)" : "Importo pagato (€)"} type="number"
                            value={f.amount_cents / 100}
                            onChange={v => set("amount_cents", Math.round(parseFloat(v || 0) * 100))}
                            tid="mb-amount" />
                        <MField label="Metodo pagamento" select
                            options={[
                                { v: "cash", l: "💵 Contanti" },
                                { v: "pos", l: "💳 POS (carta fisica)" },
                                { v: "bonifico", l: "🏦 Bonifico bancario" },
                                { v: "omaggio", l: "🎁 Omaggio (fuori revenue)" },
                            ]}
                            value={f.payment_method} onChange={setPaymentMethod} tid="mb-pay-method" />
                        {isOmaggio ? (
                            <MField label="Motivo omaggio *" select
                                options={[
                                    { v: "", l: "— Seleziona motivo —" },
                                    { v: "sponsor", l: "🤝 Sponsor" },
                                    { v: "mediapress", l: "📰 Media / Press" },
                                    { v: "influencer", l: "📱 Influencer / Creator" },
                                    { v: "staff", l: "👥 Staff / Collaboratore" },
                                    { v: "pilota", l: "🏁 Pilota / Test drive" },
                                    { v: "amico", l: "❤️ Ospite personale" },
                                    { v: "concorso", l: "🎯 Vincitore concorso / giveaway" },
                                    { v: "altro", l: "📎 Altro" },
                                ]}
                                value={f.omaggio_reason} onChange={setOmaggioReason} tid="mb-omaggio-reason" />
                        ) : (
                            <MField label="Note pagamento" value={f.payment_notes} onChange={v => set("payment_notes", v)} placeholder="es. rif. bonifico, nome intestatario…" tid="mb-notes" />
                        )}
                        {isOmaggio && (
                            <div className="col-span-2">
                                <MField label="Note omaggio (visibile in admin, non al cliente)" value={f.payment_notes} onChange={v => set("payment_notes", v)}
                                    placeholder="es. Sponsor GT Radial 2026 · contatto Marco 333…" tid="mb-omaggio-notes" />
                                <div className="mt-2 border border-[#f59e0b]/30 bg-[#f59e0b]/5 p-2 text-[10px] text-[#f59e0b] flex items-start gap-2">
                                    <span>🎁</span>
                                    <span>Questa prenotazione sarà creata con importo €0 e <strong>NON</strong> apparirà nei revenue delle stat (Incasso Turni/Foto). Il cliente riceve regolare voucher, boarding pass, planning e tunnel come tutti gli altri.</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="sticky bottom-0 bg-[#1C2026] border-t border-[#9AA2AD]/20 p-3 flex justify-end gap-2">
                    <button onClick={onClose} className="border border-[#9AA2AD]/40 text-[#9AA2AD] px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="mb-cancel">Annulla</button>
                    <button onClick={save} disabled={busy} className={`${isOmaggio ? "bg-[#f59e0b]" : "bg-[#E2231A]"} disabled:opacity-50 text-white px-4 py-2 text-[11px] uppercase tracking-widest`} data-testid="mb-save">
                        {busy ? "Creazione…" : (isOmaggio ? "🎁 Crea Omaggio" : "Crea Prenotazione")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function MField({ label, value, onChange, type = "text", mono, placeholder, select, options, tid }) {
    const cls = `w-full bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-3 py-2 text-sm ${mono ? "font-mono" : ""}`;
    return (
        <div>
            <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">{label}</label>
            {select ? (
                <select className={cls} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={tid}>
                    {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
            ) : (
                <input type={type} className={cls} value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={tid} />
            )}
        </div>
    );
}


// V53 · PhotographyMatrixView — Matrice Fotografo (righe=clienti, colonne=turni)
// role: "admin" (vede tutto) | "photographer" (limitato, no email/tel + no paid manuale)
function PhotographyMatrixView({ token, role = "admin" }) {
    const [data, setData] = useState({ rows: [], stats: null });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | pending | paid | link_sent | verbal
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    // V56.5 · Config Cloudinary + upload state per la matrice (tasto 📷 Carica sempre visibile)
    const [config, setConfig] = useState(null);
    const [uploadingKey, setUploadingKey] = useState(null);
    // V59 · CCN: email in copia per verificare che le consegne arrivino
    const [bcc, setBcc] = useState("");
    const [bccInput, setBccInput] = useState("");
    const [savingBcc, setSavingBcc] = useState(false);

    const loadBcc = async () => {
        try { const r = await getMediaDeliverySettings(token); setBcc(r.bcc || ""); setBccInput(r.bcc || ""); } catch { /* noop */ }
    };
    const saveBcc = async () => {
        setSavingBcc(true);
        try {
            const r = await setMediaDeliverySettings(token, bccInput.trim());
            setBcc(r.bcc || "");
            toast.success(r.bcc ? `📋 Copia (CCN) attiva: ${r.bcc}` : "CCN rimosso");
        } catch (e) { toast.error(e?.response?.data?.detail || "Email CCN non valida"); }
        finally { setSavingBcc(false); }
    };

    // Toast chiaro: COSA è stato mandato, A CHI, se in copia CCN, ed ESITO reale (Resend)
    const notifyDelivery = (r, verb = "Inviato") => {
        const parts = [];
        if (r.photos) parts.push(`${r.photos} foto`);
        if (r.videos) parts.push(`${r.videos} video`);
        if (r.big_link) parts.push(`link video HD`);
        const cosa = parts.length ? parts.join(" + ") : "nessun file";
        if (r.email_sent) {
            toast.success(`✅ ${verb}: ${cosa} → ${r.to}${r.bcc ? ` (copia CCN a ${r.bcc})` : ""}`);
        } else {
            toast.error(`⛔ NON partita a ${r.to}: ${r.email_error || "errore Resend"}. Il cliente NON ha ricevuto.`);
        }
    };

    // V59 · Invii massivi + drill-down contatori
    const [bulkBusy, setBulkBusy] = useState(false);
    const [drill, setDrill] = useState(null); // { title, items: [...] }

    const bulkSendPaid = async () => {
        if (!window.confirm("📤 CONSEGNARE i file a TUTTI i clienti che hanno PAGATO?\n\nInvia foto + video + link nei turni non ancora consegnati.\nSALTA automaticamente chi ha GIÀ ricevuto (niente doppioni).")) return;
        setBulkBusy(true);
        try {
            const r = await mediaBulkSendLinks(token, false);
            toast.success(`📤 Consegnati: ${r.sent} · Falliti: ${r.failed} · Saltati (già ricevuti): ${r.skipped}`);
            setDrill({ title: `Consegna ai PAGANTI · ${r.sent} inviati / ${r.failed} falliti / ${r.skipped} già ricevuti`, items: (r.details || []).map(d => ({ cliente: d.cliente, email: d.to, badge: d.status, note: d.reason || "" })) });
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio massivo"); }
        finally { setBulkBusy(false); }
    };
    const bulkInvite = async () => {
        if (!window.confirm("📨 INVITARE A PAGARE tutti i clienti con foto/video pronti che NON hanno ancora pagato?\n\nInvia l'anteprima FILIGRANATA + N video pronti + tasto per pagare 25€ e sbloccare l'HD.\nNON consegna contenuti: solo l'invito.")) return;
        setBulkBusy(true);
        try {
            const r = await mediaBulkInvite(token);
            toast.success(`📨 Inviti inviati: ${r.sent} · Falliti: ${r.failed} · Saltati: ${r.skipped}`);
            setDrill({ title: `Inviti a pagare · ${r.sent} inviati / ${r.failed} falliti / ${r.skipped} saltati`, items: (r.details || []).map(d => ({ cliente: d.cliente, email: d.to, badge: d.status, note: d.reason || "" })) });
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio massivo"); }
        finally { setBulkBusy(false); }
    };

    // Aggiunta cliente manuale (walk-in / contatto diretto) → compare in matrice con gli stessi tasti
    const [addClient, setAddClient] = useState(null); // null=chiuso · {customer_name,customer_email,telefono,car}
    const [addingClient, setAddingClient] = useState(false);
    const submitManualClient = async () => {
        const f = addClient || {};
        if (!(f.customer_name || "").trim()) { toast.error("Inserisci il nome"); return; }
        if (!((f.customer_email || "").includes("@"))) { toast.error("Inserisci un'email valida"); return; }
        setAddingClient(true);
        try {
            const r = await mediaCreateManualClient(token, {
                customer_name: (f.customer_name || "").trim(),
                customer_email: (f.customer_email || "").trim(),
                telefono: (f.telefono || "").trim(),
                car: (f.car || "FOTO").trim(),
            });
            toast.success(`✅ Cliente aggiunto: ${r.customer_name} (turno #${r.turno_progressivo})`);
            setAddClient(null);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione cliente"); }
        finally { setAddingClient(false); }
    };

    // Drill-down: click su un contatore → elenco destinatari in quello stato
    const openDrill = (statusKey, label) => {
        const items = [];
        for (const row of (data.rows || [])) {
            for (const s of (row.slots || [])) {
                const st = s.media?.status || "not_offered";
                const match = statusKey === "paid" ? (st === "paid" || st === "delivered") : st === statusKey;
                if (!match) continue;
                const ld = s.media?.last_delivery;
                const li = s.media?.last_invite;
                items.push({
                    cliente: row.cliente,
                    email: row.email || "—",
                    note: `${s.car || ""} ${s.start_time || ""}`.trim()
                        + (li ? ` · link: ${li.email_sent ? "✅ inviato" : "⛔ NON partito"}` : "")
                        + (ld ? ` · consegna: ${ld.email_sent ? "✅ inviata" : "⛔ NON partita"}` : "")
                        + (row.big_transfer_sent_at && (st === "paid" || st === "delivered") ? " · link HD inviato" : ""),
                    badge: st,
                });
            }
        }
        setDrill({ title: `${label} · ${items.length} destinatari`, items });
    };

    const load = async () => {
        setLoading(true);
        try {
            const [r, c] = await Promise.all([
                role === "photographer" ? photographerMediaMatrix(token) : adminMediaMatrix(token),
                photographerConfig(token).catch(() => null),
            ]);
            setData({ rows: r.rows || [], stats: r.stats || null });
            if (c) setConfig(c);
        } catch (e) { toast.error("Errore caricamento matrice"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); loadBcc(); }, [token]); // eslint-disable-line

    // V56.5 · Upload file Cloudinary con HEIC auto-conversion + compressione + messaggi errore chiari
    const uploadFiles = async (bookingId, slotId, voucherCode, files) => {
        if (!config) { toast.error("Config Cloudinary in caricamento… riprova tra 1 sec"); return; }
        if (!files || !files.length) return;
        setUploadingKey(`${bookingId}-${slotId}`);
        let ok = 0, fail = 0, failMsgs = [];
        for (let f of Array.from(files)) {
            if (!f || !f.size) { fail++; failMsgs.push(`${f?.name || "file"}: vuoto`); continue; }
            // HEIC iPhone → JPG lato browser
            if (/\.(heic|heif)$/i.test(f.name) || /heic|heif/i.test(f.type)) {
                try {
                    toast.info(`🔄 Converto ${f.name} da HEIC a JPG…`);
                    const heic2any = (await import("heic2any")).default;
                    const blob = await heic2any({ blob: f, toType: "image/jpeg", quality: 0.9 });
                    f = new File([blob], f.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
                } catch (e) { fail++; failMsgs.push(`${f.name}: conversione HEIC fallita`); continue; }
            }
            const isVideoInit = f.type.startsWith("video/") || /\.(mp4|mov|webm|avi)$/i.test(f.name);
            if (!isVideoInit && f.size > 3 * 1024 * 1024) {
                try {
                    const imageCompression = (await import("browser-image-compression")).default;
                    const cmp = await imageCompression(f, { maxSizeMB: 3, maxWidthOrHeight: 2560, useWebWorker: true, initialQuality: 0.92 });
                    f = new File([cmp], f.name, { type: cmp.type });
                } catch (e) { /* fallback originale */ }
            }
            const isVideo = f.type.startsWith("video/") || /\.(mp4|mov|webm|avi)$/i.test(f.name);
            const kind = isVideo ? "video" : "photo";
            const MAX_MB = isVideo ? 100 : 20;
            const sizeMB = f.size / (1024 * 1024);
            if (sizeMB > MAX_MB) { fail++; failMsgs.push(`${f.name}: ${sizeMB.toFixed(1)}MB > max ${MAX_MB}MB (usa WeTransfer)`); continue; }
            const fd = new FormData();
            fd.append("file", f);
            fd.append("upload_preset", config.upload_preset);
            fd.append("folder", `velora_bookings/${voucherCode || bookingId}/${slotId}`);
            fd.append("context", `booking_id=${bookingId}|slot_id=${slotId}|voucher=${voucherCode || ""}`);
            try {
                const res = await fetch(config.upload_url, { method: "POST", body: fd });
                if (!res.ok) {
                    let msg = `HTTP ${res.status}`;
                    try { const err = await res.json(); if (err.error?.message) msg = err.error.message; } catch {}
                    if (/format.*not allowed/i.test(msg)) msg = "formato non ammesso (converti in JPG/MP4)";
                    if (/File size too large/i.test(msg)) msg = "troppo pesante per Cloudinary (usa WeTransfer)";
                    fail++; failMsgs.push(`${f.name}: ${msg}`);
                    continue;
                }
                const j = await res.json();
                await photographerAddMedia(token, bookingId, {
                    kind, secure_url: j.secure_url, public_id: j.public_id,
                    width: j.width, height: j.height, bytes: j.bytes, format: j.format, duration: j.duration,
                    slot_id: slotId,
                });
                ok++;
            } catch (e) { fail++; failMsgs.push(`${f.name}: ${e.message || "errore rete"}`); }
        }
        setUploadingKey(null);
        if (ok) toast.success(`📷 ${ok} file caricati sul turno`);
        if (fail) { toast.error(`${fail} file falliti · ${failMsgs[0] || ""}`, { duration: 8000 }); console.warn("Upload failures:", failMsgs); }
        await load();
    };

    const maxSlots = Math.max(1, ...data.rows.map(r => r.n_slots || 0));

    const filtered = data.rows.filter(row => {
        const q = search.trim().toLowerCase();
        if (q && !((row.cliente || "").toLowerCase().includes(q) || (row.voucher_code || "").toLowerCase().includes(q) || (row.email || "").toLowerCase().includes(q))) return false;
        if (filter === "all") return true;
        return row.slots.some(s => {
            if (filter === "pending") return s.media.status === "not_offered";
            if (filter === "paid") return s.media.status === "paid" || s.media.status === "delivered";
            if (filter === "link_sent") return s.media.status === "link_sent";
            if (filter === "verbal") return s.media.status === "verbal";
            if (filter === "rejected") return s.media.status === "rejected";
            return true;
        });
    });

    const doAction = async (action, bookingId, slotId, extra) => {
        setBusy(true);
        try {
            if (action === "verbal")       await mediaSlotSetStatus(token, bookingId, slotId, { status: "verbal" });
            else if (action === "reject")  await mediaSlotSetStatus(token, bookingId, slotId, { status: "rejected" });
            else if (action === "reset")   await mediaSlotSetStatus(token, bookingId, slotId, { status: "not_offered" });
            else if (action === "mark_paid") {
                // V55.8 · Cash/POS/manuale: 2 step — conferma pagamento + opzione auto-consegna
                const method = window.prompt(
                    "💰 CONFERMA INCASSO MANUALE\n\n" +
                    "Come ha pagato il cliente?\n" +
                    "• CASH = contanti\n" +
                    "• POS = carta al terminale\n" +
                    "• BONIFICO = bonifico bancario\n" +
                    "• OMAGGIO = regalo/promo (0€)\n" +
                    "• ANNULLA per uscire",
                    "CASH"
                );
                if (!method) { setBusy(false); return; }
                const m = String(method).trim().toUpperCase();
                if (!["CASH","POS","BONIFICO","OMAGGIO"].includes(m)) {
                    toast.error("Metodo non valido");
                    setBusy(false); return;
                }
                await mediaSlotSetStatus(token, bookingId, slotId, {
                    status: "paid",
                    payment_source: m.toLowerCase(),
                });
                toast.success(`✓ Segnato come PAGATO via ${m}`);
                // Chiedi se inviare subito il link di consegna
                if (window.confirm("📬 Vuoi inviare SUBITO al cliente il link download foto/video (senza filigrana)?\n\nSì = email automatica\nNo = potrai farlo dopo con il bottone Consegna")) {
                    await mediaSlotDeliver(token, bookingId, slotId);
                    toast.success("📬 Link download inviato al cliente");
                }
            }
            else if (action === "reassign_here") {
                // V53.3 · sposta il pacchetto paid dall'altro slot a questo
                const fromSlotId = extra?.from_slot_id;
                if (!fromSlotId) { toast.error("Manca slot di partenza"); setBusy(false); return; }
                if (!window.confirm("🔄 Sposta il pacchetto media PAGATO su questo turno?\nL'altro turno tornerà 'Non offerto'.")) { setBusy(false); return; }
                await mediaReassignPaid(token, bookingId, fromSlotId, slotId);
                toast.success("🔄 Pacchetto riassegnato a questo turno");
            }
            else if (action === "invite") {
                // V53.1 · richiesta autorizzazione esplicita per ogni email
                if (!window.confirm("📧 Autorizzi l'invio dell'email di invito acquisto media al cliente per questo turno?")) { setBusy(false); return; }
                const r = await mediaSlotSendInvite(token, bookingId, slotId);
                toast.success(r.email_sent ? "📧 Email inviata al cliente" : "Salvato · email non inviata (verifica Resend)");
            }
            else if (action === "deliver") {
                if (!window.confirm("📬 Autorizzi la CONSEGNA media al cliente?\nInvia email con link foto/video in alta risoluzione (senza watermark).")) { setBusy(false); return; }
                const extraMsg = window.prompt(
                    "✍️ MESSAGGIO PER IL CLIENTE (opzionale)\n\n" +
                    "Testo aggiunto SOPRA il testo base dell'email. Lascia vuoto per solo testo standard.",
                    ""
                );
                if (extraMsg === null) { setBusy(false); return; }
                const r = await mediaSlotDeliver(token, bookingId, slotId, extraMsg.trim(), "all");
                notifyDelivery(r, "Consegnato");
            }
            else if (action === "copy_link") {
                const r = await mediaSlotCopyLink(token, bookingId, slotId);
                await navigator.clipboard.writeText(r.landing_url);
                toast.success("🔗 Link copiato negli appunti");
            }
            // V56 · Preview watermark pubblica in nuova tab
            else if (action === "preview") {
                window.open(`${window.location.origin}/aggiungi-media/${bookingId}?slot=${slotId}`, "_blank", "noopener,noreferrer");
            }
            // V56 · Consegna MANUALE bypass Stripe (omaggio/cash/POS/bonifico) — supporta only_kind
            else if (action === "manual_deliver" || action === "manual_deliver_photos" || action === "manual_deliver_videos") {
                const only_kind = action === "manual_deliver_photos" ? "photos" : action === "manual_deliver_videos" ? "videos" : "all";
                const targetLabel = only_kind === "photos" ? "SOLO FOTO" : only_kind === "videos" ? "SOLO VIDEO" : "TUTTO (foto + video)";
                // V56.3 · Alert extra se cliente ha RIFIUTATO esplicitamente (flag d del PDF)
                const slotObj = extra?._slotObj;
                const wasRejected = slotObj?.media?.status === "rejected";
                if (wasRejected) {
                    if (!window.confirm("⚠️ ATTENZIONE\n\nQuesto cliente ha RIFIUTATO esplicitamente le foto in pista.\n\nProcedere comunque con l'invio bypassando Stripe?")) { setBusy(false); return; }
                }
                const src = window.prompt(
                    `📸 CONSEGNA ${targetLabel} · BYPASS STRIPE\n\n` +
                    `Marcherà il turno come PAGATO e invierà email di consegna senza filigrana.\n\n` +
                    `Motivazione (per contabilità):\n` +
                    `• omaggio\n• cash\n• pos\n• bonifico\n• test\n• altro (nel campo digita quello che vuoi)\n\n` +
                    `Digita la motivazione:`,
                    "omaggio"
                );
                if (!src) { setBusy(false); return; }
                const source = String(src).trim().toLowerCase() || "altro";
                const extraMsg = window.prompt(
                    "✍️ MESSAGGIO PER IL CLIENTE (opzionale)\n\n" +
                    "Scrivi qui un testo che verrà aggiunto SOPRA il testo base dell'email.\n" +
                    "Lascia vuoto per inviare solo il testo standard.",
                    ""
                );
                if (extraMsg === null) { setBusy(false); return; }
                const r = await mediaSlotManualDeliver(token, bookingId, slotId, source, "", only_kind, extraMsg.trim());
                notifyDelivery(r, "Inviato (bypass Stripe)");
            }
            // Reinvio puro (riarmabile) · turno già pagato/consegnato — non tocca pagamento/contabilità
            else if (action === "redeliver_photos" || action === "redeliver_videos" || action === "redeliver_all") {
                const only_kind = action === "redeliver_photos" ? "photos" : action === "redeliver_videos" ? "videos" : "all";
                const label = only_kind === "photos" ? "SOLO FOTO" : only_kind === "videos" ? "SOLO VIDEO" : "FOTO + VIDEO";
                const extraMsg = window.prompt(
                    `🔄 REINVIA ${label} AL CLIENTE\n\n` +
                    "Rimanda l'email di consegna (utile se il cliente non l'ha ricevuta o ha cambiato email).\n" +
                    "Non modifica pagamento né contabilità.\n\n" +
                    "✍️ Messaggio extra (opzionale) da aggiungere sopra il testo base:",
                    ""
                );
                if (extraMsg === null) { setBusy(false); return; }
                const r = await mediaSlotRedeliver(token, bookingId, slotId, extraMsg.trim(), only_kind);
                notifyDelivery(r, "Reinviato");
            }
            // V56 · Riassegna pacchetto pagato a un altro turno (extra.slots + extra.other_slots)
            else if (action === "reassign_pick") {
                const others = extra?.other_slots || [];
                if (others.length === 0) { toast.info("Il cliente ha un solo turno"); setBusy(false); return; }
                const listaTxt = others.map((s, i) => `${i + 1}) ${s.car || "?"} ${s.start_time || ""}`).join("\n");
                const answer = window.prompt(
                    `🔀 RIASSEGNA PACCHETTO PAGATO\n\n` +
                    `Cliente: ${extra?.cliente || ""} · ${others.length + 1} turni\n\n` +
                    `Il pacchetto è ora assegnato a questo turno.\n` +
                    `A quale altro turno vuoi spostarlo?\n\n` +
                    listaTxt + `\n\nDigita il numero (1-${others.length}):`,
                    "1"
                );
                if (!answer) { setBusy(false); return; }
                const idx = parseInt(answer, 10) - 1;
                if (isNaN(idx) || idx < 0 || idx >= others.length) { toast.error("Numero non valido"); setBusy(false); return; }
                const toSlot = others[idx];
                if (!window.confirm(`Sposto il pacchetto pagato su:\n\n${toSlot.car || "?"} ${toSlot.start_time || ""}\n\nQuesto turno tornerà "non offerto".`)) { setBusy(false); return; }
                await mediaReassignPaid(token, bookingId, slotId, toSlot.slot_id);
                toast.success(`🔀 Pacchetto spostato su ${toSlot.car} ${toSlot.start_time}`);
            }
            // V56.2 · Elimina un singolo file dalla galleria (foto/video) — resta su Cloudinary ma sparisce dal booking
            else if (action === "delete_media") {
                if (!extra?.public_id || !extra?.kind) { setBusy(false); return; }
                if (!window.confirm(`Rimuovere questo ${extra.kind === 'photo' ? 'FOTO' : 'VIDEO'} dalla galleria del cliente?\n\n(Il file resta su Cloudinary, ma sparisce dalla dashboard e dalle email cliente)`)) { setBusy(false); return; }
                await photographerDeleteMedia(token, bookingId, extra.public_id, extra.kind);
                toast.success(`🗑 ${extra.kind === 'photo' ? 'Foto' : 'Video'} rimosso`);
            }
            if (action !== "copy_link" && action !== "preview") await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore azione"); }
        finally { setBusy(false); }
    };

    const runFollowUp = async () => {
        setBusy(true);
        try {
            // Prima chiama dry_run per mostrare anteprima
            const dry = await mediaFollowUpBulk(token, true);
            if (dry.would_email === 0) { toast.info("Nessun cliente eleggibile al follow-up in questo momento"); setBusy(false); return; }
            const ok = window.confirm(
                `📮 FOLLOW-UP VENDITE\n\n` +
                `Verranno inviate ${dry.would_email} email a clienti con turni non pagati che hanno foto/video già caricati.\n\n` +
                `Autorizzi l'invio?`
            );
            if (!ok) { setBusy(false); return; }
            const r = await mediaFollowUpBulk(token, false);
            toast.success(`📮 Inviate ${r.sent_count}/${r.targets_emails} email di follow-up`);
            if (r.failed_count > 0) toast.error(`${r.failed_count} email fallite`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore follow-up"); }
        finally { setBusy(false); }
    };

    // V59 · WeTransfer/SwissTransfer MULTI-LINK: aggiungi/rimuovi/invia link per il booking
    const wetransferAddLink = async (bookingId, currentLinks = []) => {
        const url = window.prompt(
            "🔗 AGGIUNGI LINK VIDEO HD (WeTransfer/SwissTransfer)\n\n" +
            "Incolla un link (es. https://we.tl/... o https://swisstransfer.com/...).\n" +
            "Puoi aggiungerne PIÙ D'UNO (es. un link per ogni video). Verranno inviati tutti insieme nella stessa email.",
            ""
        );
        if (url === null) return;
        const clean = (url || "").trim();
        if (!clean) return;
        const note = window.prompt("📝 Nota per questo link (opzionale, es. 'Video giro EVO'):", "") || "";
        const links = [...currentLinks.map(l => ({ url: l.url, note: l.note || "" })), { url: clean, note: note.trim() }];
        setBusy(true);
        try {
            await photographerSetBigTransferLinks(token, bookingId, links);
            toast.success(`🔗 Link salvato (${links.length} totali)`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Link non valido"); }
        finally { setBusy(false); }
    };
    const wetransferRemoveLink = async (bookingId, currentLinks, idx) => {
        if (!window.confirm("Rimuovere questo link?")) return;
        const links = currentLinks.filter((_, i) => i !== idx).map(l => ({ url: l.url, note: l.note || "" }));
        setBusy(true);
        try {
            await photographerSetBigTransferLinks(token, bookingId, links);
            toast.success(links.length ? `Link rimosso (${links.length} rimasti)` : "Tutti i link rimossi");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };
    const wetransferSend = async (bookingId, alreadySent = false) => {
        if (!window.confirm(`${alreadySent ? "REINVIARE" : "Inviare"} al cliente l'email con SOLO il link video HD (senza foto)?\n\nUsalo se non stai consegnando anche foto Cloudinary.`)) return;
        const extraMsg = window.prompt(
            "✍️ MESSAGGIO PER IL CLIENTE (opzionale)\n\n" +
            "Testo aggiunto SOPRA il testo base dell'email del link video HD.\nLascia vuoto per solo testo standard.",
            ""
        );
        if (extraMsg === null) return;
        setBusy(true);
        try {
            const r = await photographerSendBigTransfer(token, bookingId, extraMsg.trim());
            if (r.email_sent) toast.success(`✅ ${alreadySent ? "Reinviato" : "Inviato"} link Video HD → ${r.sent_to}${r.bcc ? ` (copia CCN a ${r.bcc})` : ""}`);
            else toast.error(`⛔ NON partita a ${r.sent_to}: ${r.email_error || "errore Resend"}`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio email"); }
        finally { setBusy(false); }
    };

    const s = data.stats;

    return (
        <div className="space-y-4" data-testid="media-matrix-view">
            {/* V56 · Box istruzioni permanente — allineato al menu Fotografo */}
            <PhotographerInstructionsBox />

            <div className="border border-[#9AA2AD]/20 bg-[#0F1114] p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h2 className="uppercase tracking-widest text-[#F4F2ED] text-lg flex items-center gap-2">
                            <span className="text-[#E2231A]">📸</span> Matrice Fotografo · Media per Turno
                        </h2>
                        <p className="text-[10px] text-[#9AA2AD] mt-1">Ogni cliente ha N turni · abbina foto/video, spunta pagamenti verbali, invia link Stripe · media bloccati finché non arriva pagamento</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setAddClient({ customer_name: "", customer_email: "", telefono: "", car: "FOTO" })}
                            data-testid="matrix-add-client"
                            className="border border-[#16a34a]/60 bg-[#16a34a]/10 hover:bg-[#16a34a]/20 text-[#16a34a] px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold">
                            ➕ Aggiungi cliente
                        </button>
                        <button onClick={load} disabled={loading} className="border border-[#4B7BEA]/40 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40" data-testid="matrix-reload">
                            {loading ? "…" : "↻ Ricarica"}
                        </button>
                    </div>
                </div>

                {/* Form aggiunta cliente manuale */}
                {addClient && (
                    <div className="mt-3 p-3 border border-[#16a34a]/40 bg-[#16a34a]/5 space-y-2" data-testid="add-client-form">
                        <div className="text-[11px] uppercase tracking-widest text-[#16a34a] font-semibold">➕ Nuovo cliente (walk-in / contatto diretto)</div>
                        <p className="text-[10px] text-[#9AA2AD]">Compare subito in matrice con gli stessi tasti: carica foto/video, "fai pagare" (Stripe) e consegna automatica dopo il pagamento.</p>
                        <div className="flex flex-wrap gap-2">
                            <input type="text" value={addClient.customer_name} onChange={(e) => setAddClient({ ...addClient, customer_name: e.target.value })}
                                placeholder="Nome e cognome *" data-testid="add-client-name"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#16a34a] px-3 py-1.5 text-xs outline-none min-w-[200px] flex-1" />
                            <input type="email" value={addClient.customer_email} onChange={(e) => setAddClient({ ...addClient, customer_email: e.target.value })}
                                placeholder="Email *" data-testid="add-client-email"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#16a34a] px-3 py-1.5 text-xs outline-none min-w-[200px] flex-1" />
                            <input type="tel" value={addClient.telefono} onChange={(e) => setAddClient({ ...addClient, telefono: e.target.value })}
                                placeholder="Telefono" data-testid="add-client-phone"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#16a34a] px-3 py-1.5 text-xs outline-none min-w-[150px]" />
                            <select value={addClient.car} onChange={(e) => setAddClient({ ...addClient, car: e.target.value })}
                                data-testid="add-client-car"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#16a34a] px-3 py-1.5 text-xs outline-none">
                                <option value="FOTO">Auto (generico)</option>
                                <option value="EVO">EVO</option>
                                <option value="WRX">WRX</option>
                                <option value="PASS">Passeggero</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={submitManualClient} disabled={addingClient} data-testid="add-client-submit"
                                className="border border-[#16a34a]/60 bg-[#16a34a]/15 hover:bg-[#16a34a]/25 text-[#16a34a] px-4 py-1.5 text-[10px] uppercase tracking-widest font-semibold disabled:opacity-40">
                                {addingClient ? "Creo…" : "✅ Crea cliente"}
                            </button>
                            <button onClick={() => setAddClient(null)} disabled={addingClient} data-testid="add-client-cancel"
                                className="border border-[#9AA2AD]/30 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-4 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40">
                                Annulla
                            </button>
                        </div>
                    </div>
                )}

                {/* V53.1 · Follow-up + info autorizzazione email */}
                {role === "admin" && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap p-2 border border-[#f59e0b]/25 bg-[#f59e0b]/5">
                        <div className="flex-1 text-[10px] text-[#f59e0b]/90">
                            🔒 <strong>Autorizzazione email:</strong> ogni invio email al cliente (invito acquisto, consegna media, follow-up) richiede conferma esplicita da questo menu — nessun invio automatico.
                        </div>
                        <button onClick={runFollowUp} disabled={busy} data-testid="matrix-follow-up"
                            className="border border-[#f59e0b]/60 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40">
                            📮 Follow-up vendite (24h)
                        </button>
                    </div>
                )}

                {/* V59 · CCN — email in copia per verificare che le consegne arrivino davvero */}
                <div className="mt-3 flex items-center gap-2 flex-wrap p-2 border border-[#4B7BEA]/30 bg-[#4B7BEA]/5" data-testid="matrix-bcc-bar">
                    <span className="text-[10px] uppercase tracking-widest text-[#4B7BEA] font-semibold">📋 Copia in CCN</span>
                    <span className="text-[10px] text-[#9AA2AD]">ricevi tu una copia di OGNI consegna media (foto/video/link) per controllare che arrivi:</span>
                    <input type="email" value={bccInput} onChange={(e) => setBccInput(e.target.value)}
                        placeholder="tua@email.com (vuoto = disattiva)"
                        data-testid="matrix-bcc-input"
                        className="bg-[#0B0D10] border border-[#4B7BEA]/40 focus:border-[#4B7BEA] px-3 py-1.5 text-xs outline-none min-w-[220px]" />
                    <button onClick={saveBcc} disabled={savingBcc} data-testid="matrix-bcc-save"
                        className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40">
                        {savingBcc ? "…" : "Salva"}
                    </button>
                    {bcc && <span className="text-[10px] text-[#16a34a]" data-testid="matrix-bcc-active">✓ attiva: {bcc}</span>}
                </div>

                {/* V59 · Invii massivi (hover = testo esatto) */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button onClick={bulkSendPaid} disabled={bulkBusy} data-testid="bulk-send-paid"
                        title="manda tutti i link a chi ha pagato"
                        className="text-[11px] uppercase tracking-widest border border-[#16a34a]/60 bg-[#16a34a]/10 hover:bg-[#16a34a]/20 text-[#16a34a] px-3 py-2 disabled:opacity-40 font-semibold">
                        📤 Consegna ai paganti
                    </button>
                    <button onClick={bulkInvite} disabled={bulkBusy} data-testid="bulk-invite"
                        title="manda l'invito a pagare (anteprima filigranata) a tutti quelli con foto/video pronti che non hanno pagato"
                        className="text-[11px] uppercase tracking-widest border border-[#f59e0b]/60 bg-[#f59e0b]/10 hover:bg-[#f59e0b]/20 text-[#f59e0b] px-3 py-2 disabled:opacity-40 font-semibold">
                        📨 Invita a pagare
                    </button>
                    {bulkBusy && <span className="text-[10px] text-[#9AA2AD] animate-pulse">invio in corso…</span>}
                    <span className="text-[9px] text-[#9AA2AD]/70">💡 Passa il mouse sui tasti per il testo completo dell'azione</span>
                </div>

                {s && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4">
                        <StatCell label="Clienti" value={s.clients} color="#F4F2ED" />
                        <StatCell label="Turni totali" value={s.total_slots} color="#F4F2ED" />
                        <StatCell label="Pagati" value={s.paid} color="#16a34a" testId="stat-paid" onClick={() => openDrill("paid", "PAGATI")} />
                        <StatCell label="Verbali" value={s.verbal} color="#f59e0b" testId="stat-verbal" onClick={() => openDrill("verbal", "VERBALI (confermato in pista, non pagato)")} />
                        <StatCell label="Link Stripe inviati" value={s.link_sent} color="#4B7BEA" testId="stat-link-sent" onClick={() => openDrill("link_sent", "LINK STRIPE INVIATI (in attesa pagamento)")} />
                        <StatCell label="Non offerti" value={s.not_offered} color="#9AA2AD" testId="stat-not-offered" onClick={() => openDrill("not_offered", "NON OFFERTI")} />
                    </div>
                )}
            </div>

            {/* V59 · Modal drill-down destinatari */}
            {drill && (
                <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={() => setDrill(null)} data-testid="drill-modal">
                    <div className="bg-[#0F1114] border border-[#9AA2AD]/30 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b border-[#9AA2AD]/20 sticky top-0 bg-[#0F1114]">
                            <div className="text-sm font-semibold text-[#F4F2ED]">{drill.title}</div>
                            <button onClick={() => setDrill(null)} data-testid="drill-close" className="text-[#9AA2AD] hover:text-[#F4F2ED] text-xl leading-none">×</button>
                        </div>
                        <div className="p-4 space-y-1">
                            {drill.items.length === 0 && <div className="text-[#9AA2AD] text-sm text-center py-8">Nessun destinatario in questa categoria.</div>}
                            {drill.items.map((it, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 py-2 px-2 border-b border-[#9AA2AD]/10 text-xs" data-testid={`drill-item-${i}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[#F4F2ED] font-medium truncate">{it.cliente}</div>
                                        <div className="text-[#9AA2AD] truncate">{it.email}{it.note ? ` · ${it.note}` : ""}</div>
                                    </div>
                                    {it.badge && <span className={`text-[9px] uppercase px-2 py-0.5 border ${it.badge === "sent" || it.badge === "paid" || it.badge === "delivered" ? "border-[#16a34a]/50 text-[#16a34a]" : it.badge === "failed" ? "border-[#E2231A]/50 text-[#E2231A]" : "border-[#f59e0b]/50 text-[#f59e0b]"}`}>{it.badge}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="🔍 Cerca cliente / voucher / email…"
                    className="flex-1 min-w-[200px] bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none text-xs px-3 py-2"
                    data-testid="matrix-search" />
                <div className="flex gap-1 flex-wrap">
                    {[
                        { v: "all", l: "Tutti" },
                        { v: "pending", l: "○ Non offerti" },
                        { v: "verbal", l: "✋ Verbali" },
                        { v: "link_sent", l: "📧 Link inviato" },
                        { v: "paid", l: "✓ Pagati" },
                        { v: "rejected", l: "✕ Rifiutati" },
                    ].map(b => (
                        <button key={b.v} onClick={() => setFilter(b.v)}
                            data-testid={`matrix-filter-${b.v}`}
                            className={`text-[10px] uppercase tracking-widest px-2 py-1.5 border ${filter === b.v ? "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:border-[#9AA2AD]/60"}`}>
                            {b.l}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="text-center py-12 text-[#9AA2AD]">Caricamento…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-[#9AA2AD]/20 p-12 text-center text-[#9AA2AD]">
                    Nessun cliente da mostrare. La matrice si popola quando ci sono turni pianificati nel tab <strong>Planning</strong>.
                </div>
            ) : (
                <div className="overflow-auto max-h-[70vh] border border-[#9AA2AD]/10" data-testid="matrix-scroll">
                    <table className="w-full text-xs" data-testid="matrix-table">
                        <thead>
                            <tr className="bg-[#1C2026] text-[#9AA2AD] uppercase tracking-widest text-[10px] sticky top-0 z-10">
                                <th className="p-2 text-left">#</th>
                                <th className="p-2 text-left">Cliente</th>
                                <th className="p-2 text-left">Voucher</th>
                                {Array.from({ length: maxSlots }).map((_, i) => (
                                    <th key={i} className="p-2 text-center min-w-[220px] border-l border-[#9AA2AD]/10">Turno #{i + 1}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(row => (
                                <React.Fragment key={row.booking_id}>
                                    {/* V53.3 · Banner "chiedi cliente per quale turno" */}
                                    {row.needs_assignment_choice && (
                                        <tr className="border-t border-[#f59e0b]/40 bg-[#f59e0b]/10" data-testid={`matrix-warn-${row.booking_id}`}>
                                            <td colSpan={3 + maxSlots} className="p-2 text-[#f59e0b] text-[11px]">
                                                ⚠️ <strong>{row.cliente}</strong> ha pagato <strong>1 pacchetto media</strong> ma ha <strong>{row.n_slots} turni</strong>.
                                                Chiedi al cliente <strong>per quale turno lo vuole</strong>: usa <em>"🔄 Sposta qui"</em> se ha scelto l'altro turno.
                                                Se ne vuole <strong>anche un altro</strong>, invia il carrello con <em>"🛒 Vendi 2° pacchetto"</em> (📧 Invita) sull'altra colonna.
                                            </td>
                                        </tr>
                                    )}
                                    <tr className="border-t border-[#9AA2AD]/10 hover:bg-[#15181C]" data-testid={`matrix-row-${row.booking_id}`}>
                                        <td className="p-2 text-[#E2231A] font-bold">{row.turno_progressivo || "—"}</td>
                                        <td className="p-2">
                                            <div className="text-[#F4F2ED] font-semibold">{row.cliente}</div>
                                            {role === "admin" && row.email && <div className="text-[9px] text-[#9AA2AD] font-mono">{row.email}</div>}
                                        </td>
                                        <td className="p-2 text-[#9AA2AD] font-mono text-[10px]">{row.voucher_code}</td>
                                        {Array.from({ length: maxSlots }).map((_, i) => {
                                            const slot = row.slots[i];
                                            if (!slot) return <td key={i} className="p-2 border-l border-[#9AA2AD]/10 text-center text-[#9AA2AD]/30">—</td>;
                                            return (
                                                <td key={i} className="p-2 border-l border-[#9AA2AD]/10 align-top">
                                                    <MediaSlotCell slot={slot} role={role} bookingId={row.booking_id} busy={busy}
                                                        rowContext={row}
                                                        uploadingKey={uploadingKey}
                                                        onUpload={(files) => uploadFiles(row.booking_id, slot.slot_id, row.voucher_code, files)}
                                                        onAction={(action, extra) => doAction(action, row.booking_id, slot.slot_id, extra)} />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* V56 · Barra WeTransfer/SwissTransfer per il booking (video HD >100MB) */}
                                    <tr className="bg-[#0B0D10]" data-testid={`matrix-wetransfer-${row.booking_id}`}>
                                        <td colSpan={3 + maxSlots} className="p-2 border-t border-[#9AA2AD]/5">
                                            <div className="flex items-center gap-3 flex-wrap text-[10px]">
                                                <span className="uppercase tracking-widest text-[#9AA2AD]">🔗 Link Video HD (WeTransfer/SwissTransfer):</span>
                                                {(row.big_transfer_links || []).map((lk, li) => (
                                                    <span key={li} className="flex items-center gap-1 border border-[#E2231A]/30 bg-[#E2231A]/5 px-1.5 py-0.5" data-testid={`wt-link-${row.booking_id}-${li}`}>
                                                        <span className="text-[9px] text-[#9AA2AD]">#{li + 1}</span>
                                                        <a href={lk.url} target="_blank" rel="noreferrer"
                                                            className="text-[#E2231A] hover:underline break-all max-w-[220px] truncate"
                                                            title={lk.note ? `${lk.url}\n📝 ${lk.note}` : lk.url}>{lk.url}</a>
                                                        <button onClick={() => wetransferRemoveLink(row.booking_id, row.big_transfer_links || [], li)} disabled={busy}
                                                            data-testid={`wt-remove-${row.booking_id}-${li}`}
                                                            className="text-[9px] text-[#E2231A] hover:bg-[#E2231A]/10 px-1">✕</button>
                                                    </span>
                                                ))}
                                                <button onClick={() => wetransferAddLink(row.booking_id, row.big_transfer_links || [])} disabled={busy}
                                                    data-testid={`wt-add-${row.booking_id}`}
                                                    className="text-[9px] uppercase tracking-widest border border-[#E2231A]/50 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1 disabled:opacity-40">
                                                    {(row.big_transfer_links || []).length ? "+ Aggiungi altro link" : "+ Incolla link WeTransfer/SwissTransfer"}
                                                </button>
                                                {(row.big_transfer_links || []).length > 0 && (
                                                    <>
                                                        <button onClick={() => wetransferSend(row.booking_id, !!row.big_transfer_sent_at)} disabled={busy}
                                                            title={row.big_transfer_sent_at ? "Reinvia i link WeTransfer (riarmabile · es. cliente ha cambiato email)" : "Invia SOLO i link WeTransfer (usalo se non ci sono foto Cloudinary da mandare)"}
                                                            data-testid={`wt-send-${row.booking_id}`}
                                                            className="text-[9px] uppercase tracking-widest border border-[#f59e0b]/50 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-2 py-1 disabled:opacity-40">
                                                            {row.big_transfer_sent_at ? "🔄 Reinvia Video HD" : "✉ Invia solo Video HD"}
                                                        </button>
                                                        {row.big_transfer_sent_at && <span className="text-[#16a34a] text-[9px]">✓ inviato</span>}
                                                    </>
                                                )}
                                                <span className="text-[9px] text-[#9AA2AD]/70 ml-auto">
                                                    💡 Puoi mettere PIÙ link (uno per video). Vengono inclusi tutti nell'email di consegna HD
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const StatCell = ({ label, value, color, testId, onClick }) => (
    <div className={`border border-[#9AA2AD]/20 bg-[#15181C] p-2 text-center ${onClick ? "cursor-pointer hover:border-[#9AA2AD]/60 hover:bg-[#1C2026] transition-colors" : ""}`}
        data-testid={testId} onClick={onClick} title={onClick ? "Clicca per vedere l'elenco dei destinatari" : undefined}>
        <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">{label}</div>
        <div className="text-lg font-bold" style={{ color }}>{value}</div>
        {onClick && <div className="text-[8px] text-[#9AA2AD]/60 mt-0.5">👁 dettaglio</div>}
    </div>
);

// V56.2 · Miniature media caricate nella cella turno.
// V56.6 · Default NITIDE (senza filigrana) — il fotografo/admin deve vedere le foto reali.
//         Prop `watermarked` opzionale per la variante filigranata (usata nella lightbox).
function SlotThumbs({ photos = [], videos = [], onDelete, watermarked = false, onOpen }) {
    const [expand, setExpand] = useState(false);
    const items = [
        ...photos.map(p => ({ ...p, kind: "photo" })),
        ...videos.map(v => ({ ...v, kind: "video" })),
    ];
    if (!items.length) return null;
    const MAX = 6;
    const shown = expand ? items : items.slice(0, MAX);
    const extra = items.length - shown.length;

    // Thumb 160x160 auto quality. Se watermarked=true applica filigrana Cloudinary grande diagonale.
    const thumb = (secure_url) => {
        if (!secure_url || !secure_url.includes("/image/upload/")) return secure_url;
        if (watermarked) {
            return secure_url.replace(
                "/image/upload/",
                "/image/upload/w_160,h_160,c_fill,q_auto,f_auto/l_text:Impact_44_bold:VELORA,co_rgb:E2231A,o_75,a_-28,g_center/"
            );
        }
        return secure_url.replace("/image/upload/", "/image/upload/w_160,h_160,c_fill,q_auto,f_auto/");
    };
    const fmtDur = (d) => {
        if (!d) return "";
        const s = Math.round(d);
        const m = Math.floor(s / 60);
        return `${m}:${String(s % 60).padStart(2, "0")}`;
    };

    return (
        <div className="space-y-1" data-testid={`slot-thumbs${watermarked ? "-wm" : ""}`}>
            <div className="grid grid-cols-3 gap-0.5">
                {shown.map((it, idx) => (
                    <div key={it.public_id || idx} className="relative group aspect-square bg-[#15181C] border border-[#9AA2AD]/20 overflow-hidden">
                        {it.kind === "photo" ? (
                            <button type="button"
                                onClick={(e) => { e.preventDefault(); onOpen && onOpen(idx); }}
                                title={onOpen ? "Click: apre galleria grande" : "Apri originale"}
                                className="block w-full h-full">
                                <img src={thumb(it.secure_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </button>
                        ) : (
                            <button type="button"
                                onClick={(e) => { e.preventDefault(); onOpen && onOpen(idx); }}
                                title="Apri video"
                                className="w-full h-full flex flex-col items-center justify-center text-[#9AA2AD] bg-black">
                                <span className="text-lg">▶</span>
                                <span className="text-[8px] uppercase tracking-widest">{fmtDur(it.duration)}</span>
                            </button>
                        )}
                        {onDelete && !watermarked && (
                            <button onClick={(e) => { e.stopPropagation(); onDelete(it.public_id, it.kind); }}
                                className="absolute top-0.5 right-0.5 bg-[#E2231A] hover:bg-[#C11B15] text-white w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[10px]"
                                title="Rimuovi">
                                ✕
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {extra > 0 && !expand && (
                <button onClick={() => setExpand(true)} className="w-full text-[9px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#F4F2ED] border border-[#9AA2AD]/20 py-0.5">
                    +{extra} altri · vedi tutti
                </button>
            )}
            {expand && items.length > MAX && (
                <button onClick={() => setExpand(false)} className="w-full text-[9px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#F4F2ED] border border-[#9AA2AD]/20 py-0.5">
                    ▲ Riduci
                </button>
            )}
        </div>
    );
}

// V56.6 · Lightbox media grande — mostra tutte le foto/video del turno grandi.
// Toggle "💧 Filigrana ON/OFF" per vedere la versione filigranata (come la vede il cliente).
function MediaLightbox({ photos = [], videos = [], initialIndex = 0, initialWatermarked = false, onClose }) {
    const items = [
        ...photos.map(p => ({ ...p, kind: "photo" })),
        ...videos.map(v => ({ ...v, kind: "video" })),
    ];
    const [idx, setIdx] = useState(Math.min(initialIndex, Math.max(0, items.length - 1)));
    const [wm, setWm] = useState(initialWatermarked);
    if (!items.length) return null;
    const cur = items[idx];
    const isImg = cur.kind === "photo";
    // URL grande con/senza filigrana
    const bigUrl = (() => {
        if (!isImg) return cur.secure_url;
        if (!cur.secure_url.includes("/image/upload/")) return cur.secure_url;
        if (wm) return cur.secure_url.replace(
            "/image/upload/",
            "/image/upload/q_auto,f_auto/l_text:Impact_180_bold:VELORA%20JDM,co_rgb:E2231A,o_65,a_-24,g_center/"
        );
        return cur.secure_url.replace("/image/upload/", "/image/upload/q_auto,f_auto/");
    })();
    return (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="media-lightbox"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            {/* Toolbar */}
            <div className="flex items-center justify-between p-3 gap-3 border-b border-[#E2231A]/30 bg-black">
                <div className="text-xs uppercase tracking-widest text-[#F4F2ED]">
                    {isImg ? "📷 Foto" : "🎬 Video"} {idx + 1} / {items.length}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setWm(w => !w)}
                        data-testid="lightbox-toggle-wm"
                        className={`text-[10px] uppercase tracking-widest border px-3 py-1.5 ${
                            wm ? "border-[#E2231A] bg-[#E2231A]/20 text-[#E2231A]" : "border-[#9AA2AD]/50 hover:bg-[#9AA2AD]/10 text-[#9AA2AD]"
                        }`}
                        title={wm ? "Modalità FILIGRANATA (vede il cliente prima di pagare)" : "Modalità NITIDA (originale HD)"}>
                        {wm ? "💧 Filigrana ON" : "👁️ Nitida"}
                    </button>
                    <a href={cur.secure_url} download target="_blank" rel="noreferrer"
                        className="text-[10px] uppercase tracking-widest border border-[#9AA2AD]/50 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-3 py-1.5"
                        title="Scarica originale HD (nitido, senza filigrana)">
                        ⬇ Scarica
                    </a>
                    <button onClick={onClose} data-testid="lightbox-close"
                        className="text-[10px] uppercase tracking-widest border border-[#E2231A]/50 hover:bg-[#E2231A]/10 text-[#E2231A] px-3 py-1.5">
                        ✕ Chiudi
                    </button>
                </div>
            </div>
            {/* Body */}
            <div className="flex-1 flex items-center justify-center p-4 relative">
                {isImg ? (
                    <img src={bigUrl} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                    <video src={cur.secure_url} controls autoPlay className="max-w-full max-h-full" />
                )}
                {items.length > 1 && (
                    <>
                        <button onClick={() => setIdx(i => (i - 1 + items.length) % items.length)}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#E2231A] text-white w-12 h-12 flex items-center justify-center text-xl">‹</button>
                        <button onClick={() => setIdx(i => (i + 1) % items.length)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#E2231A] text-white w-12 h-12 flex items-center justify-center text-xl">›</button>
                    </>
                )}
            </div>
            {/* Bottom strip */}
            <div className="border-t border-[#9AA2AD]/20 bg-black p-2 overflow-x-auto">
                <div className="flex gap-1">
                    {items.map((it, i) => {
                        const t = it.secure_url && it.secure_url.includes("/image/upload/")
                            ? it.secure_url.replace("/image/upload/", "/image/upload/w_80,h_80,c_fill,q_auto,f_auto/")
                            : it.secure_url;
                        return (
                            <button key={i} onClick={() => setIdx(i)}
                                className={`w-14 h-14 flex-shrink-0 border ${i === idx ? "border-[#E2231A]" : "border-[#9AA2AD]/20"} overflow-hidden`}>
                                {it.kind === "photo"
                                    ? <img src={t} alt="" className="w-full h-full object-cover" />
                                    : <div className="w-full h-full bg-black flex items-center justify-center text-[#9AA2AD]">▶</div>}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// V56 · Box istruzioni permanente per il tab Fotografo (identico a Photographer.jsx)
function PhotographerInstructionsBox() {
    const [open, setOpen] = useState(true);
    return (
        <div data-testid="photog-instructions" className="border border-[#E2231A]/40 bg-[#E2231A]/5">
            <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#E2231A]/10">
                <span className="text-[#E2231A] text-lg">ℹ️</span>
                <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-[#E2231A]">📖 GUIDA FOTOGRAFO · Cause & Effect</div>
                    <div className="text-[10px] text-[#9AA2AD] mt-1">Clicca per {open ? "chiudere" : "aprire"} — ogni tasto è spiegato qui</div>
                </div>
                <div className="text-[#E2231A]">{open ? "▼" : "▶"}</div>
            </button>
            {open && (
                <div className="border-t border-[#E2231A]/30 p-5 space-y-4 text-xs text-[#F4F2ED]">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">⚡ FLUSSO IN PISTA</div>
                        <ol className="space-y-2 list-decimal list-inside text-[#9AA2AD] leading-relaxed">
                            <li>Fermi il cliente in pista dopo il giro → clicca <strong className="text-[#f59e0b]">🎤 Intervista in Pista</strong>: popup SÌ/NO.</li>
                            <li>Se SÌ → stato <strong className="text-[#f59e0b]">🎤 INTERVISTA</strong>. Carica foto/video (verranno mostrate <strong>con filigrana</strong>).</li>
                            <li>Clicca <strong className="text-[#4B7BEA]">💳 Invia Stripe</strong>: cliente riceve email con preview filigranata + link Stripe.</li>
                            <li>Appena paga → 🤖 <strong className="text-[#16a34a]">AUTO-CONSEGNA</strong>: email finale parte da sola con foto HD senza filigrana + link WeTransfer se salvato.</li>
                        </ol>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">🚦 STATI DEL TURNO</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                            <div><span className="inline-block w-3 h-3 rounded-full bg-[#9AA2AD] mr-2 align-middle" /><strong>○ NON OFFERTO</strong> — nessuna azione fatta.</div>
                            <div><span className="inline-block w-3 h-3 rounded-full bg-[#f59e0b] mr-2 align-middle" /><strong>🎤 INTERVISTA</strong> — cliente ha detto SÌ, devi mandare Stripe.</div>
                            <div><span className="inline-block w-3 h-3 rounded-full bg-[#4B7BEA] mr-2 align-middle" /><strong>📧 LINK INVIATO</strong> — email Stripe partita, aspetti pagamento.</div>
                            <div><span className="inline-block w-3 h-3 rounded-full bg-[#16a34a] mr-2 align-middle" /><strong>✓ PAGATO</strong> — cliente ha pagato, consegna in corso.</div>
                            <div><span className="inline-block w-3 h-3 rounded-full bg-[#16a34a] mr-2 align-middle" /><strong>📬 CONSEGNATO</strong> — foto HD in mano al cliente.</div>
                            <div><span className="inline-block w-3 h-3 rounded-full bg-[#E2231A] mr-2 align-middle" /><strong>✕ RIFIUTATO</strong> — cliente NON vuole. Non lo scocciamo più.</div>
                        </div>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">🎛️ COSA FA OGNI TASTO (allineato ai nomi in cella)</div>
                        <ul className="space-y-1.5 text-[11px] leading-relaxed">
                            <li><span className="inline-block px-2 py-0.5 border border-[#f59e0b]/50 text-[#f59e0b] mr-2">🎤 Intervista in Pista</span> Popup SÌ/NO — cliente in pista dice se vuole o no. Se NO → stato RIFIUTATO, non lo scocciamo più.</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#E2231A]/50 text-[#E2231A] mr-2">📷 Carica</span> Carica foto/video Cloudinary. HEIC auto-convertito · Foto >3MB compresse a 2560px. Vedi <em>miniature</em> sotto la cella per riguardare/eliminare.</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#9AA2AD]/50 text-[#9AA2AD] mr-2">💧 Applica filigrana</span> Apre in nuova tab la pagina cliente: foto con filigrana grande diagonale, video senza filigrana (non scaricabili).</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#4B7BEA]/50 text-[#4B7BEA] mr-2">📧 Invia foto + Stripe</span> Email al cliente con foto filigranate + link Stripe. Se ci sono video, li annuncia nella prima email e li spedisce dopo Stripe. Se già inviato, tasto diventa <strong>🔄 Reinvia Stripe (N×)</strong>.</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#a855f7]/50 text-[#a855f7] mr-2">📸 Foto senza filigrana</span> <strong>BYPASSA Stripe</strong> — invia SOLO le foto HD. Popup con motivazione (omaggio/cash/pos/bonifico/test/altro).</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#a855f7]/50 text-[#a855f7] mr-2">🎬 Video senza filigrana</span> <strong>BYPASSA Stripe</strong> — invia SOLO video HD (Cloudinary + link WeTransfer). Popup motivazione.</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#a855f7]/70 text-[#a855f7] mr-2 font-semibold">🎁 Tutto insieme</span> <strong>BYPASSA Stripe</strong> — foto+video+WeTransfer in un colpo (visibile solo se hai sia foto che video).</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#16a34a]/50 text-[#16a34a] mr-2">📬 Consegna post-Stripe</span> Compare solo quando cliente ha pagato Stripe (auto-consegna avviene da sola, ma questo tasto forza reinvio manuale).</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#f59e0b]/50 text-[#f59e0b] mr-2">🔀 Riassegna a…</span> Sposta un pacchetto pagato a un ALTRO turno dello stesso cliente (utile: N turni · 1 pack).</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#9AA2AD]/50 text-[#9AA2AD] mr-2">🔗 Link</span> Copia il link Stripe negli appunti (per WhatsApp).</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#E2231A]/50 text-[#E2231A] mr-2">✕ Rifiuta</span> Cliente cambia idea dopo aver detto SÌ: marca rifiutato, stop contatti.</li>
                            <li><span className="inline-block px-2 py-0.5 border border-[#9AA2AD]/50 text-[#9AA2AD] mr-2">↺ Reset</span> Ritorna lo slot a NON OFFERTO.</li>
                        </ul>
                        <div className="mt-3 border border-[#a855f7]/40 bg-[#a855f7]/5 p-3 text-[11px] leading-relaxed text-[#F4F2ED]">
                            <strong className="text-[#a855f7]">🔄 REINVIO (riarmabile):</strong> quando un turno è già <strong>PAGATO/CONSEGNATO</strong>, i tasti diventano
                            <span className="text-[#a855f7]"> 🔄 Reinvia foto</span> · <span className="text-[#a855f7]">🔄 Reinvia video</span> · <span className="text-[#a855f7]">🔄 Reinvia tutto</span>
                            (e per il link video HD: <span className="text-[#f59e0b]">🔄 Reinvia Video HD</span>). Puoi <strong>rimandare l'email quante volte vuoi</strong>
                            (utile se il cliente ha cambiato email o non l'ha ricevuta). Il reinvio <strong>NON tocca pagamento né contabilità</strong>.
                            <br />
                            <strong className="text-[#f59e0b]">✍️ MESSAGGIO:</strong> a ogni invio/reinvio compare un campo dove puoi scrivere un testo personalizzato che verrà aggiunto <strong>sopra</strong> il testo base dell'email (lascia vuoto per il testo standard).
                        </div>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">💰 BADGE ESITO PAGAMENTI (visibile su ogni turno)</div>
                        <ul className="space-y-1.5 text-[11px] leading-relaxed">
                            <li>🟢 <strong>a) PAGATO IN PREVENDITA</strong> — il pacchetto media era incluso nel booking iniziale (bundle).</li>
                            <li>🟢 <strong>b) PAGATO POST-VENDITA</strong> — cliente ha comprato dopo, tramite email Stripe che gli hai mandato.</li>
                            <li>🟣 <strong>c) PAGATO IN PISTA</strong> — hai forzato la consegna bypassando Stripe (cash/POS/bonifico/omaggio).</li>
                            <li>🔴 <strong>d) RIFIUTATO IN PISTA</strong> — cliente non le vuole. Alert extra se provi il bypass.</li>
                        </ul>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">🤖 AUTOMATICO vs ✋ MANUALE</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] leading-relaxed">
                            <div className="border border-[#16a34a]/40 bg-[#16a34a]/5 p-3">
                                <div className="text-[#16a34a] font-semibold mb-1">🤖 AUTOMATICO (default)</div>
                                Cliente paga con Stripe → email di consegna HD parte da sola. Tu non fai nulla.
                            </div>
                            <div className="border border-[#a855f7]/40 bg-[#a855f7]/5 p-3">
                                <div className="text-[#a855f7] font-semibold mb-1">✋ MANUALE (bypass Stripe)</div>
                                Usa <strong>🎁 Manuale</strong> se cliente paga contanti/POS/bonifico o omaggio. Marca pagato + consegna in un click.
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-[#9AA2AD]/20 pt-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">🎬 VIDEO HD GRANDI (WeTransfer/SwissTransfer)</div>
                        <div className="text-[11px] text-[#9AA2AD] leading-relaxed">
                            Se il video HD supera i 100MB per Cloudinary, caricalo su <strong>WeTransfer/SwissTransfer</strong> e incolla il link nella barra <span className="text-[#E2231A]">🔗 Link Video HD</span> sotto la riga del cliente. Verrà incluso <strong>automaticamente</strong> nell'email di consegna finale (dopo Stripe o consegna Manuale).
                        </div>
                    </div>

                    <div className="border-t border-[#E2231A]/40 pt-4 text-center">
                        <div className="text-[10px] uppercase tracking-widest text-[#E2231A] font-bold">⚡ CAUSE & EFFECT</div>
                        <div className="text-[11px] text-[#9AA2AD] mt-1">Ogni tasto comanda davvero il sistema. Nessun pulsante finto.</div>
                    </div>
                </div>
            )}
        </div>
    );
}

// V56.3 · Badge esito pagamenti (colonna 7 tabella PDF) — mostra a/b/c/d chiari
function PaymentOutcomeBadge({ slot, rowContext }) {
    const m = slot.media || {};
    const st = m.status || "not_offered";
    // Euristica: auto_assigned + paid = prevendita (bundle acquistato all'origine)
    //            payment_source=stripe + !auto_assigned = post-vendita
    //            manual_bypass = pagato in pista fuori Stripe
    const isPrevendita = (st === "paid" || st === "delivered") && m.auto_assigned === true && !m.manual_bypass;
    const isManualBypass = m.manual_bypass === true;
    const isPostSale = (st === "paid" || st === "delivered") && !isPrevendita && !isManualBypass;
    let outcome;
    if (st === "delivered" || st === "paid") {
        if (isPrevendita) outcome = { label: "a) PAGATO IN PREVENDITA", color: "#16a34a", icon: "🟢" };
        else if (isManualBypass) outcome = { label: `c) PAGATO IN PISTA · ${(m.payment_source || "manuale").toUpperCase()}`, color: "#a855f7", icon: "🟣" };
        else outcome = { label: "b) PAGATO POST-VENDITA", color: "#16a34a", icon: "🟢" };
    } else if (st === "rejected") {
        outcome = { label: "d) RIFIUTATO IN PISTA", color: "#E2231A", icon: "🔴" };
    } else if (st === "link_sent") {
        outcome = { label: "IN ATTESA PAGAMENTO STRIPE", color: "#4B7BEA", icon: "🔵" };
    } else if (st === "verbal") {
        outcome = { label: "VERBALE OK · DA MANDARE STRIPE", color: "#f59e0b", icon: "🟠" };
    } else {
        outcome = { label: "NON OFFERTO", color: "#9AA2AD", icon: "⚪" };
    }
    return (
        <div className="border border-dashed px-2 py-1 text-[9px] uppercase tracking-widest font-semibold flex items-center gap-1"
            data-testid={`slot-outcome-${slot.slot_id}`}
            style={{ borderColor: outcome.color + "50", background: outcome.color + "15", color: outcome.color }}>
            <span>{outcome.icon}</span>
            <span>{outcome.label}</span>
        </div>
    );
}

function MediaSlotCell({ slot, role, bookingId, onAction, busy, rowContext, uploadingKey, onUpload }) {
    const m = slot.media || {};
    const st = m.status || "not_offered";
    const isAdmin = role === "admin";
    const hasMedia = (m.photos_count || 0) + (m.videos_count || 0) > 0;
    const hasPhotos = (m.photos_count || 0) > 0;
    const hasVideos = (m.videos_count || 0) > 0 || (rowContext?.big_transfer_links || []).length > 0 || !!rowContext?.big_transfer_link;
    const uploading = uploadingKey === `${bookingId}-${slot.slot_id}`;
    // V56.6 · Lightbox in-app: apre le foto grandi (nitide o filigranate)
    const [lightbox, setLightbox] = useState(null); // null | { watermarked: bool, idx: number }
    // V53.3 · trova lo slot pagato (per reassign) e capisci se questa cella è la fonte o la destinazione
    const paidAutoSlot = rowContext?.slots?.find(s => s.media?.status === "paid" && s.media?.auto_assigned && s.slot_id !== slot.slot_id);
    const isAmbiguous = rowContext?.needs_assignment_choice && st === "paid" && m.auto_assigned;
    const canReassignHere = rowContext?.needs_assignment_choice && !!paidAutoSlot && st === "not_offered";

    const statusMeta = {
        not_offered: { color: "#9AA2AD", bg: "rgba(154,162,173,0.1)", icon: "○", label: "NON OFFERTO" },
        verbal:      { color: "#f59e0b", bg: "rgba(245,158,11,0.15)", icon: "🎤", label: "INTERVISTA · pagherà" },
        link_sent:   { color: "#4B7BEA", bg: "rgba(75,123,234,0.15)", icon: "📧", label: `LINK INVIATO${m.link_sent_count > 1 ? ` ×${m.link_sent_count}` : ""}` },
        paid:        { color: "#16a34a", bg: "rgba(22,163,74,0.15)", icon: "✓", label: `PAGATO${m.auto_assigned ? " · da confermare" : ""}` },
        rejected:    { color: "#E2231A", bg: "rgba(226,35,26,0.15)", icon: "✕", label: "RIFIUTATO" },
        delivered:   { color: "#16a34a", bg: "rgba(22,163,74,0.25)", icon: "📬", label: "CONSEGNATO" },
    }[st] || { color: "#9AA2AD", bg: "rgba(154,162,173,0.1)", icon: "?", label: st };

    const isPaidLike = st === "paid" || st === "delivered";

    // V55.8 · Semaforo temporale: mostra "quando" è successa l'azione
    const timeAgo = (iso) => {
        if (!iso) return "";
        const ms = Date.now() - new Date(iso).getTime();
        const m = Math.floor(ms / 60000);
        if (m < 1) return "adesso";
        if (m < 60) return `${m}m fa`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h fa`;
        const d = Math.floor(h / 24);
        return `${d}g fa`;
    };
    const timeHints = {
        verbal: m.verbal_at ? `da ${timeAgo(m.verbal_at)}` : "",
        link_sent: m.link_sent_at ? `inviato ${timeAgo(m.link_sent_at)}` : "",
        paid: m.paid_at ? `pagato ${timeAgo(m.paid_at)}${m.payment_source ? ` · ${m.payment_source.toUpperCase()}` : ""}` : "",
        delivered: m.delivered_at ? `consegnato ${timeAgo(m.delivered_at)}` : "",
        rejected: m.rejected_at ? `rifiutato ${timeAgo(m.rejected_at)}` : "",
    };
    const timeHint = timeHints[st] || "";

    return (
        <div className="space-y-2" data-testid={`slot-cell-${slot.slot_id}`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[#F4F2ED]">
                    {slot.car || "?"} {slot.combo_leg ? `· ${slot.combo_leg}` : ""}
                </div>
                <div className="text-[9px] text-[#9AA2AD]">{slot.start_time || "—"}</div>
            </div>

            <div className="border px-2 py-1 flex items-center gap-1"
                 style={{ borderColor: statusMeta.color + "60", background: statusMeta.bg, color: statusMeta.color }}>
                <span>{statusMeta.icon}</span>
                <span className="text-[9px] uppercase tracking-widest font-semibold">{statusMeta.label}</span>
            </div>
            {timeHint && (
                <div className="text-[9px] text-[#9AA2AD] italic" data-testid={`slot-time-hint-${slot.slot_id}`}>
                    {timeHint}
                </div>
            )}

            {/* V59 · Riepilogo ULTIMO INVIO — cosa è stato mandato, a chi, esito (chiarezza per il fotografo) */}
            {m.last_delivery && (
                <div className={`text-[9px] p-1.5 border ${m.last_delivery.email_sent ? "border-[#16a34a]/40 bg-[#16a34a]/5 text-[#16a34a]" : "border-[#E2231A]/50 bg-[#E2231A]/10 text-[#E2231A]"}`}
                    data-testid={`slot-last-delivery-${slot.slot_id}`}>
                    {m.last_delivery.email_sent ? "✅ Inviato" : "⛔ NON partita"}: {[
                        m.last_delivery.photos ? `${m.last_delivery.photos} foto` : null,
                        m.last_delivery.videos ? `${m.last_delivery.videos} video` : null,
                        m.last_delivery.big_link ? "link HD" : null,
                    ].filter(Boolean).join(" + ") || "nessun file"}
                    <br />→ {m.last_delivery.to} {timeAgo(m.last_delivery.at) ? `· ${timeAgo(m.last_delivery.at)}` : ""}
                    {m.last_delivery.bcc && <span> · CCN {m.last_delivery.bcc}</span>}
                    {!m.last_delivery.email_sent && m.last_delivery.email_error && <span><br />⚠️ {m.last_delivery.email_error}</span>}
                </div>
            )}

            {/* V53.3 · Callout ambiguità: cliente ha 1 pacchetto per N turni */}
            {isAmbiguous && (
                <div className="border border-[#f59e0b]/70 bg-[#f59e0b]/15 text-[#f59e0b] text-[9px] p-1.5" data-testid={`slot-ambiguous-${slot.slot_id}`}>
                    ⚠️ <strong>Da confermare col cliente:</strong> ha pagato <strong>1 pacchetto</strong> ma ha <strong>{rowContext?.n_slots} turni</strong>.
                </div>
            )}
            {/* V53.3 · Riassegna a questo turno */}
            {canReassignHere && (
                <button onClick={() => onAction("reassign_here", { from_slot_id: paidAutoSlot.slot_id })} disabled={busy}
                    data-testid={`slot-reassign-${slot.slot_id}`}
                    className="w-full text-[9px] uppercase tracking-widest border border-[#f59e0b]/70 bg-[#f59e0b]/10 hover:bg-[#f59e0b]/20 text-[#f59e0b] px-2 py-1.5 font-semibold disabled:opacity-40">
                    🔄 Sposta pacchetto qui
                </button>
            )}

            {/* V56.2 · Miniature Cloudinary — NITIDE (le foto vere) · click apre lightbox */}
            {hasMedia && (
                <SlotThumbs
                    photos={m.photos || []}
                    videos={m.videos || []}
                    onDelete={(public_id, kind) => onAction("delete_media", { public_id, kind })}
                    onOpen={(i) => setLightbox({ watermarked: false, idx: i })}
                />
            )}

            {/* V56.6 · Lightbox in-app: mostra le foto grandi con toggle NITIDA↔FILIGRANATA */}
            {lightbox && (
                <MediaLightbox
                    photos={m.photos || []}
                    videos={m.videos || []}
                    initialIndex={lightbox.idx || 0}
                    initialWatermarked={lightbox.watermarked}
                    onClose={() => setLightbox(null)}
                />
            )}

            {(m.photos_count > 0 || m.videos_count > 0) && (
                <div className="text-[9px] text-[#9AA2AD]">
                    {m.photos_count > 0 && <span>📷 {m.photos_count}</span>}
                    {m.videos_count > 0 && <span className="ml-2">🎬 {m.videos_count}</span>}
                </div>
            )}

            {/* V56.3 · BADGE ESITO PAGAMENTI — riga 7 tabella PDF (a/b/c) */}
            <PaymentOutcomeBadge slot={slot} rowContext={rowContext} />

            <div className="flex flex-wrap gap-2 items-start">
                {/* Tasto 1 · Carica foto (SEMPRE VISIBILE) */}
                {onUpload && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <label
                            title={
                                uploading ? "Upload in corso…" :
                                "📷 CARICA · Aggiungi foto/video al turno. HEIC iPhone auto-convertito · foto >3MB compresse. Cloudinary max 20MB foto / 100MB video (usa WeTransfer per file più grandi)."
                            }
                            data-testid={`slot-upload-${slot.slot_id}`}
                            className={`w-full text-[9px] uppercase tracking-widest border px-2 py-1 cursor-pointer flex items-center justify-center gap-1 ${
                                uploading ? "border-[#f59e0b] bg-[#f59e0b]/10 text-[#f59e0b] opacity-70" :
                                "border-[#E2231A]/50 hover:bg-[#E2231A]/10 text-[#E2231A]"
                            }`}>
                            {uploading ? "⏳ Upload…" : "📷 Carica"}
                            <input type="file" multiple accept="image/*,video/*" disabled={uploading} className="hidden"
                                onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} />
                        </label>
                        <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Aggiungi foto/video</span>
                    </div>
                )}

                {/* Tasto · Vedi (nitida) — apre lightbox foto originali */}
                <div className="flex flex-col gap-0.5 w-[116px]">
                    <button onClick={() => setLightbox({ watermarked: false, idx: 0 })}
                        disabled={busy || !hasMedia}
                        title={
                            !hasMedia ? "⚠️ Prima carica almeno 1 foto (tasto 📷 Carica)" :
                            "👁️ VEDI · Apre le foto grandi NITIDE (originali senza filigrana). Puoi navigare, zoomare, scaricare."
                        }
                        data-testid={`slot-view-${slot.slot_id}`}
                        className="w-full text-[9px] uppercase tracking-widest border border-[#9AA2AD]/60 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed">
                        👁️ Vedi
                    </button>
                    <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Guarda gli originali HD</span>
                </div>

                {/* Tasto 2 · Applica filigrana — apre lightbox in modalità FILIGRANATA */}
                <div className="flex flex-col gap-0.5 w-[116px]">
                    <button onClick={() => setLightbox({ watermarked: true, idx: 0 })}
                        disabled={busy || !hasMedia || isPaidLike}
                        title={
                            !hasMedia ? "⚠️ Prima carica almeno 1 foto (tasto 📷 Carica)" :
                            isPaidLike ? "Cliente già pagato — non serve più filigrana" :
                            "💧 APPLICA FILIGRANA · Mostra le foto grandi CON filigrana esattamente come le vede il cliente prima di pagare"
                        }
                        data-testid={`slot-watermark-${slot.slot_id}`}
                        className="w-full text-[9px] uppercase tracking-widest border border-[#E2231A]/60 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed">
                        💧 Anteprima
                    </button>
                    <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Come le vede il cliente</span>
                </div>

                {/* Tasto 3 · Invita a PAGARE con Stripe → dopo il pagamento consegna AUTOMATICA (foto e/o video) */}
                <div className="flex flex-col gap-0.5 w-[116px]">
                    <button onClick={() => onAction("invite")}
                        disabled={busy || isPaidLike || st === "rejected" || !(hasPhotos || hasVideos)}
                        title={
                            !(hasPhotos || hasVideos) ? "⚠️ Prima carica almeno una foto o un video (o incolla un link WeTransfer)" :
                            isPaidLike ? "Cliente ha già pagato · niente Stripe" :
                            st === "rejected" ? "❌ Cliente ha rifiutato — non contattarlo" :
                            st === "link_sent" ? `📧 Link Stripe già inviato ${m.link_sent_count > 1 ? `${m.link_sent_count}×` : "1×"} · click per REINVIARE con nuovo tentativo` :
                            "💳 FAI PAGARE (STRIPE) · QUESTO è il tasto che fa PAGARE il cliente: manda l'email col link Stripe (25€). Appena paga, riceve AUTOMATICAMENTE foto e video HD senza filigrana. Tu ricevi copia (CCN)."
                        }
                        data-testid={`slot-invite-${slot.slot_id}`}
                        className={`w-full text-[9px] uppercase tracking-widest border px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed ${
                            st === "link_sent"
                                ? "border-[#4B7BEA]/70 bg-[#4B7BEA]/10 text-[#4B7BEA] hover:bg-[#4B7BEA]/20"
                                : "border-[#4B7BEA]/50 hover:bg-[#4B7BEA]/10 text-[#4B7BEA]"
                        }`}>
                        {st === "link_sent"
                            ? `🔄 Rimanda pagamento (${m.link_sent_count || 1}×)`
                            : (hasPhotos && hasVideos ? "💳 Fai pagare · foto+video" : hasVideos ? "💳 Fai pagare · video" : "💳 Fai pagare · foto")}
                    </button>
                    <span className="text-[8px] text-[#4B7BEA] leading-tight text-center font-semibold">
                        {st === "link_sent" ? "Rimanda il link di pagamento" : "Paga con Stripe → riceve tutto da solo"}
                    </span>
                </div>

                {/* Tasto 5 · Foto SENZA filigrana — bypass Stripe (prima consegna) o REINVIO (riarmabile se già pagato/consegnato) */}
                <div className="flex flex-col gap-0.5 w-[116px]">
                    <button onClick={() => onAction(isPaidLike ? "redeliver_photos" : "manual_deliver_photos", { _slotObj: slot })}
                        disabled={busy || !hasPhotos}
                        title={
                            !hasPhotos ? "⚠️ Prima carica almeno 1 foto (tasto 📷 Carica)" :
                            isPaidLike ? "🔄 REINVIA SOLO FOTO · Rimanda l'email con le foto HD (utile se il cliente ha cambiato email o non l'ha ricevuta). Non tocca pagamento/contabilità. Puoi aggiungere un messaggio." :
                            "📸 FOTO SENZA FAR PAGARE (BYPASS STRIPE · GRATIS) · Consegna SOLO foto HD senza incassare. Popup motivazione (omaggio/cash/pos/bonifico/test/altro)."
                        }
                        data-testid={`slot-manual-photos-${slot.slot_id}`}
                        className="w-full text-[9px] uppercase tracking-widest border border-[#a855f7]/50 hover:bg-[#a855f7]/10 text-[#a855f7] px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed">
                        {isPaidLike ? "🔄 Reinvia foto" : "📸 Foto gratis"}
                    </button>
                    <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">
                        {isPaidLike ? "Rimanda solo le foto HD" : "Consegna HD senza pagamento"}
                    </span>
                </div>

                {/* Tasto 6 · Video SENZA filigrana — bypass Stripe o REINVIO (riarmabile) */}
                <div className="flex flex-col gap-0.5 w-[116px]">
                    <button onClick={() => onAction(isPaidLike ? "redeliver_videos" : "manual_deliver_videos", { _slotObj: slot })}
                        disabled={busy || !hasVideos}
                        title={
                            !hasVideos ? "⚠️ Prima carica un video (Cloudinary) o incolla un link WeTransfer sotto la riga" :
                            isPaidLike ? "🔄 REINVIA SOLO VIDEO · Rimanda l'email con i video (Cloudinary + link WeTransfer). Non tocca pagamento/contabilità. Puoi aggiungere un messaggio." :
                            "🎬 VIDEO SENZA FAR PAGARE (BYPASS STRIPE · GRATIS) · Consegna SOLO video (Cloudinary + link WeTransfer) senza incassare. Popup motivazione."
                        }
                        data-testid={`slot-manual-videos-${slot.slot_id}`}
                        className="w-full text-[9px] uppercase tracking-widest border border-[#a855f7]/50 hover:bg-[#a855f7]/10 text-[#a855f7] px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed">
                        {isPaidLike ? "🔄 Reinvia video" : "🎬 Video gratis"}
                    </button>
                    <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">
                        {isPaidLike ? "Rimanda solo i video HD" : "Consegna HD senza pagamento"}
                    </span>
                </div>

                {/* Tasto unico · FOTO+VIDEO senza filigrana — appare SOLO se ci sono ENTRAMBI (altrimenti duplica i tasti singoli) */}
                {(hasPhotos && hasVideos) && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <button onClick={() => onAction(isPaidLike ? "redeliver_all" : "manual_deliver", { _slotObj: slot })}
                            disabled={busy}
                            title={isPaidLike
                                ? "🔄 REINVIA FOTO+VIDEO · Rimanda tutto il disponibile (foto + video + link WeTransfer). Non tocca pagamento/contabilità."
                                : "📤 FOTO+VIDEO SENZA FAR PAGARE (BYPASS STRIPE · GRATIS) · Invia foto E video in un'unica email, SENZA incassare. Usalo per omaggi/cash, NON per vendere."}
                            data-testid={`slot-manual-all-${slot.slot_id}`}
                            className="w-full text-[9px] uppercase tracking-widest border border-[#a855f7]/70 bg-[#a855f7]/10 hover:bg-[#a855f7]/20 text-[#a855f7] px-2 py-1 disabled:opacity-30 font-semibold">
                            {isPaidLike ? "🔄 Reinvia tutto" : "📤 Tutto gratis"}
                        </button>
                        <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">
                            {isPaidLike ? "Rimanda foto + video" : "Foto+video HD senza pagamento"}
                        </span>
                    </div>
                )}

                {/* Post-evento · l'intervista in pista è GIÀ avvenuta: registra l'esito già noto con un tap */}
                {st === "not_offered" && (
                    <>
                        <div className="flex flex-col gap-0.5 w-[116px]">
                            <button onClick={() => onAction("verbal")} disabled={busy}
                                title="✅ ERA INTERESSATO · Il cliente è stato fermato in pista durante l'evento e voleva le foto/video. Lo marca come interessato (verbale): poi gli mandi il pagamento Stripe."
                                data-testid={`slot-verbal-${slot.slot_id}`}
                                className="w-full text-[9px] uppercase tracking-widest border border-[#f59e0b]/60 bg-[#f59e0b]/10 hover:bg-[#f59e0b]/20 text-[#f59e0b] px-2 py-1 disabled:opacity-40 font-semibold">
                                ✅ Era interessato
                            </button>
                            <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Fermato in pista, vuole le foto</span>
                        </div>
                        <div className="flex flex-col gap-0.5 w-[116px]">
                            <button onClick={() => onAction("reject")} disabled={busy}
                                title="✕ NON INTERESSATO · Il cliente non voleva foto/video. Non lo contattiamo più."
                                data-testid={`slot-reject-notoffered-${slot.slot_id}`}
                                className="w-full text-[9px] uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1 disabled:opacity-40">
                                ✕ Non interessato
                            </button>
                            <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Non voleva le foto</span>
                        </div>
                    </>
                )}

                {/* Consegna post-Stripe · BACKUP MANUALE (di norma parte da sola dopo il pagamento) */}
                {isAdmin && st === "paid" && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <button onClick={() => onAction("deliver")} disabled={busy}
                            title="📬 CONSEGNA (BACKUP MANUALE) · Di norma la consegna dei file HD parte DA SOLA appena il cliente paga con Stripe. Usa questo tasto solo se l'automatico non è partito (es. avevi caricato le foto DOPO il pagamento) o per rimandarla."
                            data-testid={`slot-deliver-${slot.slot_id}`}
                            className="w-full text-[9px] uppercase tracking-widest border border-[#16a34a]/60 hover:bg-[#16a34a]/15 bg-[#16a34a]/5 text-[#16a34a] px-2 py-1 disabled:opacity-40 font-semibold">
                            📬 Consegna ora
                        </button>
                        <span className="text-[8px] text-[#16a34a] leading-tight text-center font-semibold">Backup · di norma parte da sola</span>
                    </div>
                )}

                {/* Utility */}
                {isAdmin && !isPaidLike && st !== "rejected" && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <button onClick={() => onAction("copy_link")} disabled={busy}
                            title="🔗 Copia il link Stripe negli appunti (per WhatsApp)"
                            data-testid={`slot-link-${slot.slot_id}`}
                            className="w-full text-[9px] uppercase tracking-widest border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-2 py-1 disabled:opacity-40">
                            🔗 Copia link
                        </button>
                        <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Link Stripe per WhatsApp</span>
                    </div>
                )}
                {isPaidLike && (rowContext?.n_slots || 0) > 1 && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <button onClick={() => onAction("reassign_pick", {
                            cliente: rowContext.cliente,
                            other_slots: (rowContext.slots || []).filter(s => s.slot_id !== slot.slot_id),
                        })} disabled={busy}
                            title="🔀 Sposta questo pacchetto pagato ad un altro turno dello stesso cliente"
                            data-testid={`slot-reassign-${slot.slot_id}`}
                            className="w-full text-[9px] uppercase tracking-widest border border-[#f59e0b]/50 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-2 py-1 disabled:opacity-40">
                            🔀 Riassegna
                        </button>
                        <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Sposta a un altro turno</span>
                    </div>
                )}
                {st === "verbal" && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <button onClick={() => onAction("reject")} disabled={busy}
                            title="✕ Cliente cambia idea: marca rifiutato · stop contatti"
                            data-testid={`slot-reject-${slot.slot_id}`}
                            className="w-full text-[9px] uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1 disabled:opacity-40">
                            ✕ Rifiuta
                        </button>
                        <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Non le vuole: stop contatti</span>
                    </div>
                )}
                {st === "rejected" && (
                    <div className="text-[9px] uppercase tracking-widest text-[#E2231A] border border-[#E2231A]/40 bg-[#E2231A]/10 px-2 py-1 w-full text-center">
                        ❌ Cliente ha rifiutato · NON CONTATTARE
                    </div>
                )}
                {st !== "not_offered" && !isPaidLike && (
                    <div className="flex flex-col gap-0.5 w-[116px]">
                        <button onClick={() => onAction("reset")} disabled={busy}
                            title="↺ Ritorna allo stato NON OFFERTO"
                            data-testid={`slot-reset-${slot.slot_id}`}
                            className="w-full text-[9px] uppercase tracking-widest text-[#9AA2AD]/70 hover:text-[#F4F2ED] border border-transparent px-1 py-1 disabled:opacity-40">
                            ↺ Reset
                        </button>
                        <span className="text-[8px] text-[#9AA2AD] leading-tight text-center">Torna a inizio</span>
                    </div>
                )}
            </div>

            {m.notes && <div className="text-[9px] text-[#9AA2AD] italic">"{m.notes}"</div>}
        </div>
    );
}


// V53.2 · Bottone PWA push notifications (attiva/disattiva subscription)
function PushNotificationButton({ token }) {
    const [state, setState] = useState("checking"); // checking | unsupported | denied | subscribed | not_subscribed
    const [busy, setBusy] = useState(false);

    const urlB64ToUint8 = (base64String) => {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const raw = window.atob(b64);
        const out = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
        return out;
    };

    const check = async () => {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setState("unsupported"); return; }
        if (Notification.permission === "denied") { setState("denied"); return; }
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setState(sub ? "subscribed" : "not_subscribed");
        } catch (e) { setState("not_subscribed"); }
    };
    useEffect(() => { check(); }, []); // eslint-disable-line

    const enable = async () => {
        setBusy(true);
        try {
            if (Notification.permission !== "granted") {
                const perm = await Notification.requestPermission();
                if (perm !== "granted") { toast.error("Notifiche negate dal browser"); setState("denied"); return; }
            }
            const { public_key } = await pushGetPublicKey();
            if (!public_key) { toast.error("VAPID public key non configurata"); return; }
            const reg = await navigator.serviceWorker.ready;
            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlB64ToUint8(public_key),
                });
            }
            const json = sub.toJSON();
            await pushSubscribe(token, {
                endpoint: json.endpoint,
                keys: json.keys,
                user_agent: navigator.userAgent,
                label: `${/(iPhone|iPad|Android)/.test(navigator.userAgent) ? "Mobile" : "Desktop"} · ${new Date().toLocaleDateString("it")}`,
            });
            toast.success("🔔 Notifiche attivate su questo device");
            setState("subscribed");
        } catch (e) {
            console.error(e);
            toast.error("Errore attivazione notifiche: " + (e.message || e));
        } finally { setBusy(false); }
    };

    const disable = async () => {
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await pushUnsubscribe(token, sub.endpoint);
                await sub.unsubscribe();
            }
            toast.info("🔕 Notifiche disattivate");
            setState("not_subscribed");
        } catch (e) { toast.error("Errore"); }
        finally { setBusy(false); }
    };

    const sendTest = async () => {
        setBusy(true);
        try {
            const r = await pushSendTest(token);
            toast.success(`Notifica di test inviata (${r.sent} device)`);
        } catch (e) { toast.error("Errore test"); }
        finally { setBusy(false); }
    };

    if (state === "checking") return null;
    if (state === "unsupported") return (
        <button disabled data-testid="btn-push-unsupported" className="border border-[#9AA2AD]/30 text-[#9AA2AD]/50 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 cursor-not-allowed">
            🔔 Push non supportate
        </button>
    );
    if (state === "denied") return (
        <button onClick={() => toast.info("Vai nelle impostazioni del browser e riabilita le notifiche per questo sito")}
            data-testid="btn-push-denied"
            className="border border-[#E2231A]/40 text-[#E2231A] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
            🔕 Notifiche bloccate
        </button>
    );
    if (state === "subscribed") return (
        <div className="flex gap-1">
            <button onClick={sendTest} disabled={busy} data-testid="btn-push-test"
                className="border border-[#4B7BEA]/50 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-40"
                title="Invia notifica di test">
                📨 Test
            </button>
            <button onClick={disable} disabled={busy} data-testid="btn-push-disable"
                className="border border-[#16a34a]/60 bg-[#16a34a]/10 hover:bg-[#16a34a]/20 text-[#16a34a] px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-40">
                🔔 Notifiche ON
            </button>
        </div>
    );
    return (
        <button onClick={enable} disabled={busy} data-testid="btn-push-enable"
            className="border border-[#f59e0b]/60 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-40">
            🔔 Attiva Notifiche
        </button>
    );
}


// V53.4 · Modal Dettaglio Cassa Foto (audit trasparente movimenti media)
function CassaCashModal({ token, onClose }) {
    const { eventId: activeEventId } = useEventScope();
    const [items, setItems] = useState([]);
    const [eventi, setEventi] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cliente, setCliente] = useState("");
    const [importo, setImporto] = useState("");
    const [eventoId, setEventoId] = useState("");
    const [tipo, setTipo] = useState("in-evento");
    const [busy, setBusy] = useState(false);
    const [filterEv, setFilterEv] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [r, e] = await Promise.all([cbCassaCash(token), cbEventi(token)]);
            setItems(r.items || []);
            setEventi((e.eventi || []).filter(x => activeEventId === "tutti" || x._id === activeEventId));
            if (!eventoId && (e.eventi || []).length) {
                const svolto = (e.eventi || []).find(x => x.kind === "svolto");
                setEventoId(activeEventId !== "tutti" ? activeEventId : svolto ? svolto._id : e.eventi[0]._id);
            }
        } catch (err) { toast.error("Errore caricamento cassa cash"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const shown = filterEv ? items.filter(x => x.evento_id === filterEv) : items;
    const total = shown.reduce((s, x) => s + (x.importo_cents || 0), 0);
    const eventoNome = (id) => (eventi.find(e => e._id === id)?.nome) || (id === "generale" ? "Generale" : "—");

    const add = async () => {
        if (!cliente.trim()) { toast.error("Nome pagante obbligatorio"); return; }
        if (!(parseFloat(importo) > 0)) { toast.error("Importo non valido"); return; }
        setBusy(true);
        try {
            await cbCassaCashCreate(token, { cliente: cliente.trim(), importo: parseFloat(importo), evento_id: eventoId || "generale", tipo });
            setCliente(""); setImporto("");
            toast.success("Pagante cash aggiunto");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };
    const remove = async (id) => {
        if (!window.confirm("Eliminare questo pagante cash?")) return;
        try { await cbCassaCashDelete(token, id); toast.success("Eliminato"); await load(); }
        catch (err) { toast.error("Errore eliminazione"); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="cassa-cash-modal">
            <div className="bg-[#0F1114] border border-[#f59e0b]/40 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#f59e0b]/30 p-4 flex items-center justify-between z-10">
                    <div>
                        <h3 className="uppercase tracking-widest text-[#f59e0b] text-lg">💵 Cassa Cash · Paganti in contanti</h3>
                        <div className="text-[10px] text-[#9AA2AD] mt-1 flex items-center gap-2 flex-wrap">
                            <span>Totale: <strong className="text-[#f59e0b]">€{(total / 100).toFixed(2)}</strong> · {shown.length} paganti</span>
                            <select value={filterEv} onChange={e => setFilterEv(e.target.value)} data-testid="cash-filter-evento"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 px-2 py-1 text-[10px] outline-none">
                                <option value="">Tutti gli eventi</option>
                                {eventi.map(e => <option key={e._id} value={e._id}>{e.nome}</option>)}
                            </select>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white bg-[#E2231A] px-3 py-1 text-xs uppercase tracking-widest" data-testid="cassa-cash-close">✕ Chiudi</button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Form aggiunta */}
                    <div className="border border-[#f59e0b]/25 bg-[#f59e0b]/5 p-3 space-y-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold">+ Aggiungi pagante cash</div>
                        <div className="flex flex-wrap gap-2">
                            <input type="text" value={cliente} onChange={e => setCliente(e.target.value)} placeholder="Nome pagante *" data-testid="cash-cliente"
                                className="flex-1 min-w-[160px] bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#f59e0b] px-3 py-2 text-sm outline-none" />
                            <input type="number" step="0.01" value={importo} onChange={e => setImporto(e.target.value)} placeholder="€ importo *" data-testid="cash-importo"
                                className="w-28 bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#f59e0b] px-3 py-2 text-sm outline-none" />
                            <select value={eventoId} onChange={e => setEventoId(e.target.value)} data-testid="cash-evento"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 px-2 py-2 text-sm outline-none">
                                {eventi.map(e => <option key={e._id} value={e._id}>{e.nome}</option>)}
                            </select>
                            <select value={tipo} onChange={e => setTipo(e.target.value)} data-testid="cash-tipo"
                                className="bg-[#0B0D10] border border-[#9AA2AD]/30 px-2 py-2 text-sm outline-none">
                                <option value="in-evento">In-evento</option>
                                <option value="prevendita">Prevendita</option>
                            </select>
                            <button onClick={add} disabled={busy} data-testid="cash-add"
                                className="bg-[#f59e0b] hover:bg-[#d98806] text-black font-semibold px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40">
                                {busy ? "…" : "+ Aggiungi"}
                            </button>
                        </div>
                    </div>

                    <div className="border border-[#9AA2AD]/20 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-[#1C2026] text-[#9AA2AD] uppercase tracking-widest text-[10px]">
                                    <th className="p-2 text-left">Data</th>
                                    <th className="p-2 text-left">Pagante</th>
                                    <th className="p-2 text-left">Evento</th>
                                    <th className="p-2 text-left">Tipo</th>
                                    <th className="p-2 text-right">Importo</th>
                                    <th className="p-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="p-6 text-center text-[#9AA2AD]">Carico…</td></tr>
                                ) : shown.length === 0 ? (
                                    <tr><td colSpan={6} className="p-6 text-center text-[#9AA2AD]">Nessun pagante cash. Aggiungine uno col form sopra.</td></tr>
                                ) : shown.map((r) => (
                                    <tr key={r.id} className="border-t border-[#9AA2AD]/10 hover:bg-[#15181C]" data-testid={`cash-row-${r.id}`}>
                                        <td className="p-2 text-[#9AA2AD] font-mono text-[10px]">{(r.date || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="p-2 text-[#F4F2ED]">{r.cliente}</td>
                                        <td className="p-2 text-[#9AA2AD]">{eventoNome(r.evento_id)}</td>
                                        <td className="p-2 text-[#9AA2AD]">{r.tipo}</td>
                                        <td className="p-2 text-right text-[#f59e0b] font-bold">€{((r.importo_cents || 0) / 100).toFixed(2)}</td>
                                        <td className="p-2 text-right">
                                            <button onClick={() => remove(r.id)} data-testid={`cash-del-${r.id}`}
                                                className="text-[9px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A]">🗑</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[10px] text-[#9AA2AD] p-3 border border-[#f59e0b]/25 bg-[#f59e0b]/5">
                        💡 Paganti in <strong className="text-[#f59e0b]">contanti fuori Stripe</strong>. Questa Cassa Cash è un dato unico: compare qui in Dashboard e nella Contabilità (stesso totale, stesso dettaglio).
                    </div>
                </div>
            </div>
        </div>
    );
}

function MediaLedgerModal({ rows, total, onClose }) {
    const [showTest, setShowTest] = useState(false);
    const filtered = rows.filter(r => showTest || !r.is_test);
    const totalFiltered = filtered.reduce((s, r) => s + (r.amount_cents || 0), 0);
    const bySource = filtered.reduce((acc, r) => {
        const k = r.source || "standalone";
        acc[k] = (acc[k] || 0) + r.amount_cents;
        return acc;
    }, {});

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="media-ledger-modal">
            <div className="bg-[#0F1114] border border-[#16a34a]/40 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#16a34a]/30 p-4 flex items-center justify-between z-10">
                    <div>
                        <h3 className="uppercase tracking-widest text-[#16a34a] text-lg">📸 Cassa Foto · Dettaglio Movimenti</h3>
                        <div className="text-[10px] text-[#9AA2AD] mt-1">Totale: <strong className="text-[#16a34a]">€{(totalFiltered / 100).toFixed(2)}</strong> · {filtered.length} movimenti</div>
                    </div>
                    <button onClick={onClose} className="text-white bg-[#E2231A] px-3 py-1 text-xs uppercase tracking-widest">✕ Chiudi</button>
                </div>

                <div className="p-4 space-y-3">
                    <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#9AA2AD] cursor-pointer">
                        <input type="checkbox" checked={showTest} onChange={e => setShowTest(e.target.checked)} data-testid="ledger-show-test" />
                        Mostra transazioni di test ({rows.filter(r => r.is_test).length})
                    </label>

                    <div className="border border-[#9AA2AD]/20 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-[#1C2026] text-[#9AA2AD] uppercase tracking-widest text-[10px]">
                                    <th className="p-2 text-left">Data</th>
                                    <th className="p-2 text-left">Cliente</th>
                                    <th className="p-2 text-left">Voucher</th>
                                    <th className="p-2 text-right">Importo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={4} className="p-6 text-center text-[#9AA2AD]">Nessun movimento</td></tr>
                                ) : filtered.map((r, i) => (
                                    <tr key={i} className="border-t border-[#9AA2AD]/10 hover:bg-[#15181C]" data-testid={`ledger-row-${i}`}>
                                        <td className="p-2 text-[#9AA2AD] font-mono text-[10px]">{(r.date || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="p-2">
                                            <div className="text-[#F4F2ED]">{r.customer_name}{r.is_test && <span className="ml-1 text-[9px] text-[#f59e0b]">(TEST)</span>}</div>
                                            <div className="text-[9px] text-[#9AA2AD] font-mono">{r.customer_email}</div>
                                        </td>
                                        <td className="p-2 text-[#9AA2AD] font-mono text-[10px]">{r.voucher_code}</td>
                                        <td className="p-2 text-right text-[#16a34a] font-bold">€{(r.amount_cents / 100).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[10px] text-[#9AA2AD] p-3 border border-[#16a34a]/25 bg-[#16a34a]/5">
                        💡 Tutti i movimenti sono <strong className="text-[#16a34a]">pagati e incassati</strong> su Stripe. Questa è la Cassa Foto separata dalla Cassa Turni (pagamento fotografo).
                    </div>
                </div>
            </div>
        </div>
    );
}



// ═══════════════════════════════════════════════════════════════════════════
// V54 · CONTABILITÀ GIORNATA — chiusura fiscale/finanziaria per una data
// Cards: Lordo Turni · Lordo Media · Lordo Totale · Fee Stripe · Netto Stripe
//        · F24 · Partner costs (editabile) · Netto Reale
// Azioni: salva costi, riconcilia Stripe, esporta CSV, apri PDF
// ═══════════════════════════════════════════════════════════════════════════
const eur = (cents) => {
    const n = Number(cents || 0) / 100;
    return "€ " + n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// V55.1 · MissingDocsListModal — elenco di tutti i clienti con moduli mancanti
// Per ogni cliente: nome, voucher, turno, elenco moduli mancanti, pulsante stampa PDF.
function MissingDocsListModal({ token, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const r = await adminMissingDocsSummary(token);
                setData(r);
            } catch (e) {
                toast.error("Errore caricamento elenco: " + (e?.response?.data?.detail || e.message));
            } finally { setLoading(false); }
        })();
    }, [token]);
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="missing-docs-modal">
            <div className="bg-[#1C2026] border border-[#9AA2AD]/30 max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#9AA2AD]/20 p-4 flex items-center justify-between z-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-semibold">📋 V55.1</div>
                        <h2 className="font-display text-2xl text-[#F4F2ED]">Elenco Documenti Mancanti</h2>
                        {data && <div className="text-xs text-[#9AA2AD] mt-1">{data.with_missing} clienti su {data.total_bookings} hanno moduli da firmare</div>}
                    </div>
                    <div className="flex items-center gap-2">
                        {data && data.with_missing > 0 && (
                            <a href={adminPrintMissingAllUrl(token)} target="_blank" rel="noreferrer" className="bg-[#4B7BEA] hover:bg-[#3D6BC7] text-[#F4F2ED] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="btn-print-all-from-modal">
                                <FileText size={14} /> Stampa Tutti
                            </a>
                        )}
                        <button onClick={onClose} className="text-[#9AA2AD] hover:text-[#F4F2ED] p-2" data-testid="btn-close-missing-modal"><X size={20} /></button>
                    </div>
                </div>
                {loading && <div className="p-10 text-center text-[#9AA2AD]">Caricamento…</div>}
                {!loading && data && data.with_missing === 0 && (
                    <div className="p-16 text-center">
                        <div className="text-6xl mb-4">🎉</div>
                        <div className="font-display text-3xl text-[#16a34a] mb-2">Tutto firmato!</div>
                        <div className="text-sm text-[#9AA2AD]">Nessun cliente ha moduli mancanti. Puoi partire tranquillo.</div>
                    </div>
                )}
                {!loading && data && data.with_missing > 0 && (
                    <div className="divide-y divide-[#9AA2AD]/10" data-testid="missing-clients-list">
                        {data.clients.map((cli) => (
                            <div key={cli.booking_id} className="p-4 hover:bg-[#15181C]" data-testid={`missing-client-${cli.booking_id}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 flex-wrap mb-1">
                                            {cli.turno_progressivo && (
                                                <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD] bg-[#0f1116] px-2 py-0.5">Turno #{cli.turno_progressivo}</span>
                                            )}
                                            {cli.planned_time && (
                                                <span className="text-[10px] uppercase tracking-widest text-[#4B7BEA]">🕐 {cli.planned_time}</span>
                                            )}
                                            {cli.assigned_car && (
                                                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${cli.assigned_car === "EVO" ? "bg-[#E2231A]/20 text-[#E2231A]" : cli.assigned_car === "WRX" ? "bg-[#4B7BEA]/20 text-[#4B7BEA]" : "bg-[#9AA2AD]/20 text-[#9AA2AD]"}`}>{cli.assigned_car}</span>
                                            )}
                                            <span className="text-xs text-[#9AA2AD] font-mono">{cli.voucher_code}</span>
                                        </div>
                                        <div className="font-display text-lg text-[#F4F2ED]">{cli.customer_name}</div>
                                        <div className="text-xs text-[#9AA2AD] mb-2">{cli.customer_email}</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {cli.missing.map(m => (
                                                <span key={m.key} className="text-[10px] uppercase tracking-widest bg-[#E2231A]/10 border border-[#E2231A]/30 text-[#E2231A] px-2 py-0.5">
                                                    ✗ {m.title}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                        <div className="font-display text-3xl text-[#f59e0b]">{cli.missing_count}</div>
                                        <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Moduli</div>
                                        <a href={adminPrintMissingUrl(cli.booking_id, token)} target="_blank" rel="noreferrer" className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-1" data-testid={`btn-print-single-${cli.booking_id}`}>
                                            🖨 Stampa
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function OfficinaView({ token }) {
    const [text, setText] = useState("");
    const [savedText, setSavedText] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await adminOfficinaNotesGet(token);
                setText(r.text || "");
                setSavedText(r.text || "");
                setUpdatedAt(r.updated_at || null);
            } catch (e) {
                toast.error("Errore caricamento note officina: " + (e?.response?.data?.detail || e.message));
            } finally { setLoading(false); }
        })();
    }, [token]);

    const dirty = text !== savedText;

    const save = async () => {
        setSaving(true);
        try {
            await adminOfficinaNotesSave(token, text);
            setSavedText(text);
            setUpdatedAt(new Date().toISOString());
            toast.success("Note officina salvate");
        } catch (e) {
            toast.error("Errore salvataggio: " + (e?.response?.data?.detail || e.message));
        } finally { setSaving(false); }
    };

    if (loading) return <div className="text-center py-16 text-[#9AA2AD]">Caricamento…</div>;

    return (
        <div className="space-y-4" data-testid="officina-view">
            <div className="flex flex-wrap items-end gap-3 justify-between border-b border-[#9AA2AD]/20 pb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#E2231A] font-semibold">🔧 V59</div>
                    <h2 className="font-display text-3xl text-[#F4F2ED] mt-1">Officina & Ricambi</h2>
                    <div className="text-xs text-[#9AA2AD] mt-1">Appunti liberi · pezzi ordinati · interventi da fare · scadenze tagliandi. Da rifinire.</div>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                    {updatedAt && (
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                            Ultimo salvataggio: {new Date(updatedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                    )}
                    <button
                        data-testid="btn-save-officina"
                        onClick={save}
                        disabled={saving || !dirty}
                        className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 text-[#F4F2ED] px-5 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                    >
                        <Save size={14} /> {saving ? "Salvo…" : dirty ? "Salva note" : "Nessuna modifica"}
                    </button>
                </div>
            </div>

            <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-5">
                <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD] block mb-2">📝 Note libere</label>
                <textarea
                    data-testid="officina-textarea"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={"Es.\n- Cambio olio EVO ogni 3000 km · prossimo 12/09\n- Pneumatici WRX da sostituire entro 3 eventi\n- Ordinare pastiglie freni Brembo cod. XYZ\n- Contattare gommista Rossi per sconto flotta"}
                    rows={20}
                    className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm font-mono text-[#F4F2ED] resize-y min-h-[400px]"
                />
                <div className="mt-2 flex items-center justify-between text-[10px] text-[#9AA2AD]">
                    <span>{text.length} caratteri · {text.split("\n").length} righe</span>
                    {dirty && <span className="text-[#f59e0b]">● modifiche non salvate</span>}
                </div>
            </div>

            <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">💡 Idee per la prossima versione</div>
                <ul className="text-xs text-[#9AA2AD] space-y-1">
                    <li>· Lista strutturata ricambi con quantità, prezzo, fornitore</li>
                    <li>· Scadenze tagliandi per auto (EVO, WRX) con reminder</li>
                    <li>· Storico interventi datato con costo</li>
                    <li>· Foto/scontrini attachment</li>
                </ul>
            </div>
        </div>
    );
}

